function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

// Scroll lock manager with reference counting
const scrollLockManager = {
  lockedBy: new Set(),
  scrollY: 0,

  lock(element) {
    // Store scroll position on first lock
    if (this.lockedBy.size === 0) {
      this.scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${this.scrollY}px`;
      document.body.style.width = "100%";
    }

    // Add element to locked set
    this.lockedBy.add(element);
    element.dataset.scrollY = this.scrollY;
  },

  unlock(element) {
    // Remove element from locked set
    this.lockedBy.delete(element);

    // Only unlock body if no more elements need lock
    if (this.lockedBy.size === 0) {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo({
        behavior: "instant",
        left: 0,
        top: this.scrollY,
      });
      console.log('Body scroll unlocked');
    }
  },

  isLocked() {
    return this.lockedBy.size > 0;
  },

  clear() {
    this.lockedBy.clear();
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
  }
};

// Maintain backward compatibility
function lockScrollForThis(element) {
  scrollLockManager.lock(element);
}
function unlockScrollForThis(element) {
  scrollLockManager.unlock(element);
}

/**
 * iOS Safari pinch-zoom guard.
 *
 * Pinch-zoom does not call theme `location.reload`. When Safari's WebContent
 * process OOMs under compositor/GPU pressure, the tab silently reloads.
 * Permanent `will-change`, infinite transform marquees, sticky+transform
 * headers, and backdrop-filter are the usual pressure sources.
 *
 * This module always (not only when sticky header mounts) mirrors
 * `visualViewport.scale` onto `<html data-viewport-zoomed>` so CSS can pause
 * animations / drop will-change, and so resize handlers can no-op.
 */
const VIEWPORT_ZOOM_EPSILON = 0.001;
const VIEWPORT_ZOOM_RESET_DELAY_MS = 250;

let viewportZoomed = false;
let viewportScale = 1;
let viewportZoomResetTimeout = null;

/**
 * @returns {boolean} Whether the visual viewport is currently pinch-zoomed
 */
function isViewportZoomed() {
  return (
    viewportZoomed ||
    document.documentElement.hasAttribute("data-viewport-zoomed")
  );
}

/**
 * @returns {number} Current visualViewport.scale (1 when unavailable)
 */
function getViewportScale() {
  return viewportScale;
}

/**
 * @param {boolean} isZoomed
 * @private
 */
function setViewportZoomed(isZoomed) {
  if (viewportZoomed === isZoomed) return;
  viewportZoomed = isZoomed;

  const html = document.documentElement;
  if (isZoomed) {
    html.setAttribute("data-viewport-zoomed", "true");
    // Pause decoding/compositing of playing videos for the zoom gesture.
    document.querySelectorAll("video").forEach((video) => {
      if (!video.paused && !video.ended) {
        video.dataset.wasPlayingBeforeZoom = "true";
        try {
          video.pause();
        } catch (_) {
          /* ignore autoplay/policy errors */
        }
      }
    });
  } else {
    html.removeAttribute("data-viewport-zoomed");
    document.querySelectorAll("video[data-was-playing-before-zoom]").forEach((video) => {
      video.removeAttribute("data-was-playing-before-zoom");
      try {
        const playPromise = video.play();
        if (playPromise?.catch) playPromise.catch(() => {});
      } catch (_) {
        /* ignore */
      }
    });
  }

  window.dispatchEvent(
    new CustomEvent("voldt:viewport-zoom", {
      detail: { zoomed: isZoomed, scale: viewportScale },
    }),
  );
}

function initViewportZoomGuard() {
  if (window.__voldtViewportZoomGuardBound) return;
  window.__voldtViewportZoomGuardBound = true;

  const viewport = window.visualViewport;
  if (!viewport || typeof viewport.scale !== "number") return;

  const scheduleReset = () => {
    clearTimeout(viewportZoomResetTimeout);
    viewportZoomResetTimeout = setTimeout(() => {
      if (Math.abs((viewportScale || 1) - 1) < VIEWPORT_ZOOM_EPSILON) {
        setViewportZoomed(false);
      }
    }, VIEWPORT_ZOOM_RESET_DELAY_MS);
  };

  const handleViewportChange = () => {
    const scale = viewport.scale || 1;
    viewportScale = scale;
    setViewportZoomed(Math.abs(scale - 1) > VIEWPORT_ZOOM_EPSILON);
    scheduleReset();
  };

  viewport.addEventListener("resize", handleViewportChange, { passive: true });
  viewport.addEventListener("scroll", handleViewportChange, { passive: true });
  handleViewportChange();
}

initViewportZoomGuard();

// fix childMenu positions
const fixChildMenuPositions = () => {
  if (isViewportZoomed()) return;
  const childMenus = document.querySelectorAll("._headerChildMenu");
  const isRTL = document.body.style.direction === "rtl";
  const windowWidth = window.innerWidth;

  childMenus.forEach((childMenu) => {
    let isOffScreen = false;
    const childMenuRect = childMenu.getBoundingClientRect();

    // Check if the child menu itself is off-screen
    if (isRTL) {
      isOffScreen = childMenuRect.left < 0;
    } else {
      isOffScreen = childMenuRect.right > windowWidth;
    }

    // If child menu is not off-screen, check its grandchild menus
    if (!isOffScreen) {
      const grandChildMenus = childMenu.querySelectorAll(
        "._headerGrandChildMenu",
      );
      grandChildMenus.forEach((grandChildMenu) => {
        // Temporarily make the grandchild menu visible for accurate measurements
        const originalDisplay = grandChildMenu.style.display;
        grandChildMenu.style.display = "block";
        grandChildMenu.style.visibility = "hidden";

        const grandChildMenuRect = grandChildMenu.getBoundingClientRect();

        // Restore original display
        grandChildMenu.style.display = originalDisplay;
        grandChildMenu.style.visibility = "";

        if (isRTL) {
          if (grandChildMenuRect.left < 0) isOffScreen = true;
        } else {
          if (grandChildMenuRect.right > windowWidth) isOffScreen = true;
        }
      });
    }

    if (isOffScreen) {
      childMenu.setAttribute("data-offscreen", "true");
    } else {
      childMenu.removeAttribute("data-offscreen");
    }
  });
};

window.addEventListener("resize", debounce(fixChildMenuPositions, 150));
window.addEventListener("DOMContentLoaded", fixChildMenuPositions);


function initImageTextSliders() {
  document.querySelectorAll('.js-image-text-toggle').forEach((el) => {
    el.addEventListener('click', () => {
      el.classList.toggle('active');
    });
  });
}

['DOMContentLoaded', 'shopify:section:load'].forEach((event) => {
  document.addEventListener(event, initImageTextSliders);
});

const normalize = (str) => str?.toString().trim().toLowerCase() || '';
(function () {
/**
 * @fileoverview Core Web Components for Electric Maybe Shopify Theme
 * @description This file contains all the foundational web components used throughout the theme.
 * Each component follows modern web standards with proper lifecycle management,
 * accessibility features, and performance optimizations.
 *
 * @author Electric Maybe
 * @version 3.0.0
 * @license MIT
 */

/**
 * LayerManager - Manages z-index stacking context for overlays
 * @class
 * @description Provides dynamic z-index management for modals, drawers, and other overlay elements
 */
class LayerManager {
  constructor() {
    this.layers = [];
    this.baseZIndex = {
      dropdown: 100,     // --layer-dropdown
      sticky: 200,         // --layer-sticky
      drawer: 300,         // --layer-drawer
      modal: 400,          // --layer-modal
      notification: 500,   // --layer-notification
      debug: 9999
    };
    this.animatingLayers = new Set();
  }

  /**
   * Push a new layer onto the stack with parent tracking
   * @param {string} id - Unique identifier for the layer
   * @param {string} type - Type of layer (modal, drawer, etc.)
   * @param {HTMLElement} element - The element to manage
   * @param {string} parentId - Optional parent layer ID
   * @returns {number} The calculated z-index for this layer
   */
  push(id, type, element, parentId = null) {
    // Remove if already exists (to move to top)
    this.remove(id);

    // Find parent layer if specified
    let parentLayer = null;
    let baseZ = this.baseZIndex[type] || this.baseZIndex.drawer;

    if (parentId) {
      parentLayer = this.layers.find(l => l.id === parentId);
      if (parentLayer) {
        // Child should be above parent by at least 100
        baseZ = Math.max(baseZ, parentLayer.zIndex + 100);
      }
    }

    // Find the highest z-index in use
    const highestZ = this.layers.reduce((max, layer) => {
      return Math.max(max, layer.zIndex);
    }, baseZ - 1);

    // New layer goes above everything else
    const zIndex = Math.max(baseZ, highestZ + 10);

    // Add to stack with parent reference
    this.layers.push({
      id,
      type,
      element,
      zIndex,
      parentId,
      children: []
    });

    // Update parent's children list
    if (parentLayer) {
      parentLayer.children.push(id);
    }

    // Apply z-index
    if (element) {
      element.style.zIndex = zIndex;
      element.dataset.layerZIndex = zIndex;
    }

    return zIndex;
  }

  /**
   * Mark a layer as animating (delays z-index removal)
   */
  startAnimating(id) {
    this.animatingLayers.add(id);
  }

  /**
   * Mark animation as complete and remove if pending
   */
  stopAnimating(id) {
    this.animatingLayers.delete(id);

    // Check if this layer was marked for removal
    const layer = this.layers.find(l => l.id === id);
    if (layer && layer.pendingRemoval) {
      this.remove(id, true);
    }
  }

  /**
   * Remove a layer from the stack
   * @param {string} id - Unique identifier for the layer
   * @param {boolean} immediate - Force immediate removal even if animating
   */
  remove(id, immediate = false) {
    const index = this.layers.findIndex(l => l.id === id);
    if (index === -1) return;

    const layer = this.layers[index];

    // If layer is animating and not forced, mark for removal later
    if (this.animatingLayers.has(id) && !immediate) {
      layer.pendingRemoval = true;
      return;
    }

    // Remove children first
    if (layer.children && layer.children.length > 0) {
      layer.children.forEach(childId => this.remove(childId, immediate));
    }

    // Remove from parent's children list
    if (layer.parentId) {
      const parent = this.layers.find(l => l.id === layer.parentId);
      if (parent && parent.children) {
        parent.children = parent.children.filter(childId => childId !== id);
      }
    }

    // Clear z-index
    if (layer.element) {
      layer.element.style.zIndex = '';
      delete layer.element.dataset.layerZIndex;
    }

    // Remove from stack
    this.layers.splice(index, 1);
  }

  /**
   * Get the current parent context (topmost modal or drawer)
   */
  getCurrentContext() {
    // Find the topmost modal or drawer that could be a parent
    for (let i = this.layers.length - 1; i >= 0; i--) {
      const layer = this.layers[i];
      if (layer.type === 'modal' || layer.type === 'drawer') {
        return layer.id;
      }
    }
    return null;
  }

  /**
   * Get the topmost layer
   * @returns {Object|null} The topmost layer or null
   */
  getTop() {
    return this.layers[this.layers.length - 1] || null;
  }

  /**
   * Check if a specific layer is on top
   * @param {string} id - Unique identifier for the layer
   * @returns {boolean} True if the layer is on top
   */
  isOnTop(id) {
    const top = this.getTop();
    return top && top.id === id;
  }

  /**
   * Debug: Log current layer stack
   */
  debug() {
    // Debug method - uncomment for debugging
    // console.log('LayerManager Stack:');
    // this.layers.forEach(layer => {
    //   console.log(`  ${layer.id} (${layer.type}): z-${layer.zIndex}${layer.parentId ? ` [parent: ${layer.parentId}]` : ''}${layer.pendingRemoval ? ' [pending removal]' : ''}`);
    // });
  }

  /**
   * Clear all layers
   */
  clear() {
    this.layers.forEach(layer => {
      if (layer.element) {
        layer.element.style.zIndex = '';
        delete layer.element.dataset.layerZIndex;
      }
    });
    this.layers = [];
    this.animatingLayers.clear();
  }
}

// Global instance
window.layerManager = window.layerManager || new LayerManager();

/**
 * @typedef {Object} ComponentInfo
 * @property {string} name - Component name
 * @property {string} tag - HTML tag name
 * @property {string} purpose - What the component does
 * @property {string[]} features - Key features
 * @property {string} usage - Basic usage example
 */

/**
 * @const {ComponentInfo[]} COMPONENTS
 * @description List of all web components defined in this file
 */
const COMPONENTS = [
  {
    name: 'DisclosureElement',
    tag: '<disclosure-element>',
    purpose: 'Dropdown/select functionality with keyboard navigation',
    features: ['ESC key support', 'Outside click detection', 'ARIA attributes'],
    usage: '<disclosure-element><button>...</button><ul>...</ul></disclosure-element>'
  },
  {
    name: 'ElectricModal',
    tag: '<electric-modal>',
    purpose: 'Modern modal system with single container approach',
    features: ['ESC key support', 'Focus trapping', 'Dynamic content loading', 'Single container for proper z-index'],
    usage: '<button data-modal-trigger="modal-id">...</button><template data-modal-content="modal-id">...</template>'
  },
  {
    name: 'SectionFetcher',
    tag: '<section-fetcher>',
    purpose: 'Dynamic content loading for sections',
    features: ['URL parameter tracking', 'Intersection observer', 'Debounced fetching'],
    usage: '<section-fetcher data-section-id="..." data-url="...">...</section-fetcher>'
  },
  {
    name: 'DrawerTrigger',
    tag: '<drawer-trigger>',
    purpose: 'Button that triggers a drawer to open/close',
    features: ['Automatic drawer targeting via data-target attribute'],
    usage: '<drawer-trigger data-target="drawer-id">...</drawer-trigger>'
  },
  {
    name: 'DrawerElement',
    tag: '<drawer-element>',
    purpose: 'Slide-out drawer container with animations',
    features: ['Smooth animations', 'Scroll locking', 'Focus management'],
    usage: '<drawer-element><drawer>...</drawer></drawer-element>'
  },

  {
    name: 'ElectricTab',
    tag: '<electric-tab>',
    purpose: 'Accessible tab switching functionality with WCAG 2.1 AA compliance',
    features: ['Keyboard navigation (Arrow keys, Home, End)', 'Screen reader announcements', 'Focus management', 'Custom events', 'CSS class management', 'No auto-scroll on load'],
    usage: '<electric-tab><button data-tab-button data-tab-index="0">...</button><div data-tab-content data-tab-index="0">...</div></electric-tab>'
  }
];

/**
 * @const {Object} FEATURES
 * @description Key features implemented across all components
 */
const FEATURES = {
  performance: [
    'Passive event listeners for scroll/touch events',
    'Debounced network requests',
    'Intersection observer for lazy loading',
    'Proper cleanup in disconnectedCallback',
    'Memory leak prevention'
  ],
  accessibility: [
    'ARIA attributes and roles',
    'Keyboard navigation support',
    'Focus trapping and management',
    'Screen reader compatibility',
    'Reduced motion support'
  ]
};

/**
 * DisclosureElement - A web component for collapsible content with sync group support
 * 
 * This component provides a disclosure widget that can be synchronized with other
 * disclosure elements (including accordion-disclosure) using the data-sync-group attribute.
 * When one element in a sync group is opened, all others in the same group are closed.
 * 
 * @example
 * <disclosure-element data-sync-group="my-group">
 *   <button aria-expanded="false">Toggle</button>
 *   <ul>...</ul>
 * </disclosure-element>
 */
class DisclosureElement extends HTMLElement {
  constructor() {
    super();
    this.elements = {
      button: this.querySelector("button"),
    };
    this.elements.button.addEventListener("click", this.toggle.bind(this));
    this.addEventListener("keyup", this.onContainerKeyUp.bind(this));

    this.listItems = this.querySelectorAll("li[data-value]");
    this.listItems.forEach((item) =>
      item.addEventListener("click", this.onListItemClick.bind(this)),
    );
  }

  /**
   * Initialize the component and set up sync group functionality
   * @private
   */
  connectedCallback() {
    // Sync group functionality is now handled dynamically in the open() method
  }

  // Cleanup function to remove the event listener when the element is disconnected
  disconnectedCallback() {
    document.removeEventListener("click", this.handleDocumentClick);
  }

  onListItemClick(event) {
    const listItem = event.currentTarget;
    const value = listItem.dataset.value;
    this.querySelector(".selected-value").innerText = value;
    this.close();
    this.listItems.forEach((item) => item.classList.remove("font-bold"));
    listItem.classList.add("font-bold");
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    this.close();
    this.elements.button.focus();
  }

  toggle() {
    const isExpanded =
      this.elements.button.getAttribute("aria-expanded") === "true";
    if (isExpanded) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Get current sync group members (always returns fresh list)
   * @returns {NodeList | null} Current sync group members or null if no sync group
   * @private
   */
  #getSyncGroup() {
    const syncGroupName = this.dataset?.syncGroup;
    if (!syncGroupName) return null;
    
    return document.querySelectorAll(`[data-sync-group="${syncGroupName}"]`);
  }

  /**
   * Close other elements in the same sync group
   * @private
   */
  #closeSyncGroup() {
    const syncGroupMembers = this.#getSyncGroup();
    if (!syncGroupMembers) return;
    
    syncGroupMembers.forEach((el) => {
      if (el !== this && el !== this.elements.button) {
        if (el.classList.contains('disclosure-element') || el.hasAttribute('is')) {
          // Handle different types of disclosure elements
          if (el.hasAttribute('is')) {
            const elType = el.getAttribute('is');
            if (elType === 'accordion-disclosure' || elType === 'dropdown-disclosure') {
              if (el.open) {
                el.close();
              }
            }
          } else if (el.elements?.button?.getAttribute('aria-expanded') === 'true') {
            el.close();
          }
        }
      }
    });
  }

  /**
   * Open the disclosure and close other elements in the same sync group
   * @private
   */
  open() {
    // Close other elements in the same sync group
    this.#closeSyncGroup();

    this.elements.button.setAttribute("aria-expanded", "true");
    document.addEventListener(
      "click",
      this.handleDocumentClick.bind(this),
      true,
    );
  }

  close() {
    this.elements.button.setAttribute("aria-expanded", "false");
    document.removeEventListener("click", this.handleDocumentClick);
  }

  handleDocumentClick(event) {
    const isOutsideClick = !this.contains(event.target);
    if (isOutsideClick) {
      this.close();
    }
  }

  /**
   * Static utility method to get sync group members by group name
   * Always returns fresh list of current DOM elements
   * @param {string} syncGroupName - Name of the sync group
   * @returns {NodeList} Current sync group members
   * @static
   */
  static getSyncGroup(syncGroupName) {
    if (!syncGroupName) {
      console.warn('DisclosureElement.getSyncGroup: syncGroupName is required');
      return document.querySelectorAll('[data-sync-group="__non_existent__"]'); // Empty NodeList
    }
    
    return document.querySelectorAll(`[data-sync-group="${syncGroupName}"]`);
  }
}

customElements.define("disclosure-element", DisclosureElement);

/**
 * ElectricModal - Modern modal system with single container approach
 * @class
 * @extends HTMLElement
 * @description Creates a single modal container that handles all modal interactions
 */
class ElectricModal extends HTMLElement {
  constructor() {
    super();
    this.modal = null;
    this.backdrop = null;
    this.content = null;
    this.previousFocus = null;
    this.isOpen = false;
    this.scrollPosition = 0;
    this.boundEventHandler = null;
  }

  connectedCallback() {
    // Only create one instance globally
    if (!window.electricModal) {
      window.electricModal = this;
      console.log('ElectricModal: Initializing');
      this.init();
    } else {
      console.log('ElectricModal: Already initialized');
    }
  }

  disconnectedCallback() {
    if (window.electricModal === this) {
      window.electricModal = null;
      this.cleanup();
    }
  }

  init() {
    this.createModalContainer();
    this.setupTriggers();
    this.setupKeyboardListeners();
  }

  cleanup() {
    // Remove event listeners
    if (this.boundEventHandler) {
      document.removeEventListener('click', this.boundEventHandler);
    }
    document.removeEventListener('keydown', this.handleKeydown);

    // Remove modal container
    if (this.modal) {
      this.modal.remove();
    }
  }

  createModalContainer() {
    // Create modal HTML structure
    const modalHTML = `
      <div
        id="electric-modal-container"
        class="fixed inset-0 hidden"
        role="dialog"
        aria-modal="true"
      >
        <!-- Backdrop -->
        <div
          class="electric-modal-backdrop fade-in fixed inset-0 bg-black/50"
          data-modal-close
        ></div>

        <!-- Modal container -->
        <div class="fixed inset-0 overflow-y-auto p-4 sm:p-6 pointer-events-none">
          <div class="flex min-h-full items-stretch lg:items-center justify-center">
            <!-- Modal content -->
            <div
              class="electric-modal-content slide-up-sm relative bg-white shadow-xl w-fit pointer-events-auto"
              role="document"
            >
              <!-- Content will be inserted here -->
            </div>
          </div>
        </div>
      </div>
    `;

    // Add to body
    document.body.insertAdjacentHTML('beforeend', modalHTML);

    // Cache elements
    this.modal = document.getElementById('electric-modal-container');
    this.backdrop = this.modal.querySelector('.electric-modal-backdrop');
    this.content = this.modal.querySelector('.electric-modal-content');

    // Setup backdrop click handler
    this.backdrop.addEventListener('click', () => this.close('backdrop'));
  }

  setupTriggers() {
    // Use event delegation for modal triggers
    this.boundEventHandler = (e) => {
      const trigger = e.target.closest('[data-modal-trigger]');
      if (!trigger) return;

      e.preventDefault();
      e.stopPropagation();

      const modalId = trigger.dataset.modalTrigger;
      const data = this.collectTriggerData(trigger);

      console.log('ElectricModal: Opening modal', modalId, data);
      this.open(modalId, data);
    };

    document.addEventListener('click', this.boundEventHandler);
    console.log('ElectricModal: Event listener attached');
  }

  setupKeyboardListeners() {
    this.handleKeydown = (e) => {
      if (!this.isOpen) return;

      if (e.key === 'Escape') {
        this.close('escape');
      }

      // Focus trap
      if (e.key === 'Tab') {
        this.handleTabKey(e);
      }
    };

    document.addEventListener('keydown', this.handleKeydown);
  }

  collectTriggerData(trigger) {
    const data = {};

    // Collect all data-modal-* attributes
    Array.from(trigger.attributes).forEach(attr => {
      if (attr.name.startsWith('data-modal-') && attr.name !== 'data-modal-trigger') {
        const key = attr.name.replace('data-modal-', '');
        data[key] = attr.value;
      }
    });

    return data;
  }

  async open(modalId, data = {}) {
    console.log('ElectricModal.open called with:', modalId, data);
    if (this.isOpen) {
      console.log('ElectricModal: Already open, returning');
      return;
    }

    try {
      // Store current focus
      this.previousFocus = document.activeElement;

      // Use LayerManager to handle z-index with parent tracking
      const layerId = `modal-${modalId}`;
      if (window.layerManager) {
        const parentContext = window.layerManager.getCurrentContext();
        window.layerManager.push(layerId, 'modal', this.modal, parentContext);
        this.modal.dataset.layerId = layerId;
        this.currentModalId = layerId;

        // Mark as animating (for fade in)
        window.layerManager.startAnimating(layerId);
      }

      // Load content
      await this.loadContent(modalId, data);

      // Lock body scroll
      this.lockBodyScroll();

      // Show modal with animation
      this.modal.classList.remove('hidden');

      // Wait a frame for the display change to take effect
      await new Promise(resolve => requestAnimationFrame(resolve));

      // Add animation classes if they exist
      this.modal.classList.add('modal-open');

      // Set state
      this.isOpen = true;

      // Focus management
      this.trapFocus();

      // Animation complete
      if (window.layerManager && layerId) {
        // Small delay to ensure animation has started
        setTimeout(() => {
          window.layerManager.stopAnimating(layerId);
        }, 300);
      }

      // Dispatch event
      this.dispatchEvent('modal:open', { modalId, data });

    } catch (error) {
      console.error('Error opening modal:', error);
      this.close();
    }
  }

  async loadContent(modalId, data) {
    // Show loading state
    this.content.innerHTML = `
      <div class="p-8 text-center">
        <div class="inline-block w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin"></div>
        <p class="mt-2 text-sm opacity-50">Loading...</p>
      </div>
    `;

    // Check if there's a template element for this modal
    const template = document.querySelector(`template[data-modal-content="${modalId}"]`);

    if (template) {
      // Use template content
      this.content.innerHTML = template.innerHTML;

      // Replace any template variables with data
      this.replaceTemplateVariables(data);

      // Check for section-fetcher and load content if present
      const sectionFetcher = this.content.querySelector('section-fetcher');
      if (sectionFetcher) {
        await sectionFetcher.checkAndFetch?.();
      }

    } else {
      // Try to find existing modal content
      const existingModal = document.querySelector(`[data-modal-id="${modalId}"]`);

      if (existingModal) {
        this.content.innerHTML = existingModal.innerHTML;
      } else {
        // Fallback content
        this.content.innerHTML = `
          <div class="p-6">
            <h3 class="text-lg font-medium mb-4">Modal</h3>
            <p class="opacity-50 mb-6">Modal content for "${modalId}"</p>
            <div class="flex justify-end">
              <button
                type="button"
                class="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                data-modal-close
              >
                Close
              </button>
            </div>
          </div>
        `;
      }
    }

    // Setup close buttons
    this.setupCloseButtons();
  }

  replaceTemplateVariables(data) {
    Object.keys(data).forEach(key => {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
      this.content.innerHTML = this.content.innerHTML.replace(regex, data[key] || '');
    });
  }

  setupCloseButtons() {
    const closeButtons = this.content.querySelectorAll('[data-modal-close]');
    closeButtons.forEach(button => {
      button.addEventListener('click', () => this.close('button'));
    });
  }

  close(reason = 'programmatic') {
    if (!this.isOpen) return;

    try {
      // Dispatch closing event (cancelable)
      const closingEvent = this.dispatchEvent('modal:closing', { reason });
      if (closingEvent.defaultPrevented) return;

      const layerId = this.modal.dataset.layerId;

      // Mark as animating for close animation
      if (window.layerManager && layerId) {
        window.layerManager.startAnimating(layerId);
      }

      // Remove animation classes
      this.modal.classList.remove('modal-open');

      // Wait for fade out animation (if any)
      const animationDuration = 300; // milliseconds
      setTimeout(() => {
        // Hide modal
        this.modal.classList.add('hidden');

        // Animation complete - now remove from LayerManager
        if (window.layerManager && layerId) {
          window.layerManager.stopAnimating(layerId);
          window.layerManager.remove(layerId);
          delete this.modal.dataset.layerId;
        }

        // Restore body scroll
        this.unlockBodyScroll();

        // Restore focus
        if (this.previousFocus && this.previousFocus.focus) {
          // Use setTimeout to avoid scroll issues
          setTimeout(() => {
            if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
              this.previousFocus.focus({ preventScroll: true });
            }
          }, 0);
        }

        // Clear content
        this.content.innerHTML = '';

        // Set state
        this.isOpen = false;
        this.currentModalId = null;

        // Dispatch closed event
        this.dispatchEvent('modal:closed', { reason });
      }, animationDuration);

    } catch (error) {
      console.error('Error closing modal:', error);
      // Force cleanup on error
      this.modal.classList.add('hidden');
      this.isOpen = false;
    }
  }

  lockBodyScroll() {
    if (typeof lockScrollForThis === 'function') {
      lockScrollForThis(this.modal);
    } else {
      // Fallback if helpers not available
      this.scrollPosition = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = '100%';
    }
  }

  unlockBodyScroll() {
    if (typeof unlockScrollForThis === 'function') {
      unlockScrollForThis(this.modal);
    } else {
      // Fallback if helpers not available
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (this.scrollPosition) {
        window.scrollTo(0, this.scrollPosition);
      }
    }
  }

  trapFocus() {
    const focusableElements = this.content.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  handleTabKey(e) {
    const focusableElements = this.content.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }

  dispatchEvent(name, detail = {}) {
    const event = new CustomEvent(name, {
      detail,
      bubbles: true,
      cancelable: name === 'modal:closing'
    });

    document.dispatchEvent(event);
    return event;
  }

  // Public API methods
  static open(modalId, data = {}) {
    if (window.electricModal) {
      return window.electricModal.open(modalId, data);
    }
  }

  static close(reason = 'programmatic') {
    if (window.electricModal) {
      return window.electricModal.close(reason);
    }
  }

  static isOpen() {
    return window.electricModal ? window.electricModal.isOpen : false;
  }
}
customElements.define("electric-modal", ElectricModal);

// Initialize one instance globally
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!document.querySelector('electric-modal')) {
      const modal = document.createElement('electric-modal');
      document.body.appendChild(modal);
    }
  });
} else {
  // DOM is already loaded
  if (!document.querySelector('electric-modal')) {
    const modal = document.createElement('electric-modal');
    document.body.appendChild(modal);
  }
}

/**
 * DrawerTrigger Web Component
 * 
 * A trigger element that opens/closes a target drawer. Automatically connects
 * to drawer elements and provides proper accessibility attributes and state management.
 * 
 * @example
 * <button is="drawer-trigger" data-target="my-drawer" aria-expanded="false">
 *   Open Menu
 * </button>
 * <drawer-element id="my-drawer">...</drawer-element>
 */
customElements.define(
  "drawer-trigger",
  class DrawerTrigger extends HTMLElement {
    constructor() {
      super();
      
      // Initialize properties
      this.targetDrawer = null;
      this.clickHandler = this.#handleClick.bind(this);
      this.keyHandler = this.#handleKeyDown.bind(this);
      
      // Set default ARIA attributes
      this.setAttribute('role', 'button');
      this.setAttribute('tabindex', '0');
      this.setAttribute('aria-expanded', 'false');
      this.setAttribute('aria-haspopup', 'dialog');
    }

    /**
     * Called when the element is connected to the DOM
     * @private
     */
    connectedCallback() {
      this.#connectToTarget();
      this.#bindEvents();
    }

    /**
     * Disconnect the component and clean up resources
     * @private
     */
    disconnectedCallback() {
      this.#disconnectFromTarget();
      this.#unbindEvents();
    }

    /**
     * Connect this trigger to its target drawer
     * @private
     */
    #connectToTarget() {
      const targetId = this.dataset.target;
      
      if (!targetId) {
        return;
      }

      // Use a small delay to ensure drawer is available
      setTimeout(() => {
        this.targetDrawer = document.getElementById(targetId);
        
        if (!this.targetDrawer) {
          return;
        }

        // Verify it's a drawer element
        if (!this.targetDrawer.matches('drawer-element')) {
          return;
        }

        // Add this trigger to drawer's triggers
        if (!this.targetDrawer.triggers) {
          this.targetDrawer.triggers = new Set();
        }
        this.targetDrawer.triggers.add(this);

        // Update ARIA attributes based on drawer state
        this.#updateAriaAttributes();
        
        // Listen for drawer state changes
        const ariaUpdateHandler = this.#updateAriaAttributes.bind(this);
        this.targetDrawer.addEventListener('drawer:opening', ariaUpdateHandler);
        this.targetDrawer.addEventListener('drawer:closing', ariaUpdateHandler);
        
      }, 100);
    }

    /**
     * Disconnect this trigger from its target drawer
     * @private
     */
    #disconnectFromTarget() {
      if (this.targetDrawer?.triggers) {
        this.targetDrawer.triggers.delete(this);
        
        // Remove event listeners
        this.targetDrawer.removeEventListener('drawer:opening', this.#updateAriaAttributes.bind(this));
        this.targetDrawer.removeEventListener('drawer:closing', this.#updateAriaAttributes.bind(this));
      }
      
      this.targetDrawer = null;
    }

    /**
     * Bind event listeners
     * @private
     */
    #bindEvents() {
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keydown', this.keyHandler);
    }

    /**
     * Unbind event listeners
     * @private
     */
    #unbindEvents() {
      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keydown', this.keyHandler);
    }

    /**
     * Handle click events to toggle drawer
     * @param {Event} event - The click event
     * @private
     */
    #handleClick(event) {
      event.preventDefault();
      
      if (!this.targetDrawer) {
        return;
      }

      if (typeof this.targetDrawer.toggleDrawer === 'function') {
        try {
          this.targetDrawer.toggleDrawer(this);
        } catch (error) {
          // Silently handle errors
        }
      }
    }

    /**
     * Handle keyboard events for accessibility
     * @param {KeyboardEvent} event - The keydown event
     * @private
     */
    #handleKeyDown(event) {
      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.#handleClick(event);
          break;
        case 'Escape':
          // Close drawer if it's open
          if (this.targetDrawer?.isOpen) {
            this.targetDrawer.closeDrawer();
          }
          break;
      }
    }

    /**
     * Update ARIA attributes based on drawer state
     * @private
     */
    #updateAriaAttributes() {
      if (!this.targetDrawer) {
        return;
      }
      
      const isOpen = this.targetDrawer.isOpen || false;
      this.setAttribute('aria-expanded', isOpen.toString());
      
      // Update aria-controls to reference the drawer
      if (this.targetDrawer.id) {
        this.setAttribute('aria-controls', this.targetDrawer.id);
      }
    }

    /**
     * Get the current target drawer element
     * @returns {HTMLElement|null} The target drawer element or null if not connected
     * @public
     */
    getTargetDrawer() {
      return this.targetDrawer;
    }

    /**
     * Check if the trigger is connected to a valid drawer
     * @returns {boolean} True if connected to a valid drawer
     * @public
     */
    isConnected() {
      return this.targetDrawer !== null;
    }

    /**
     * Static method to find all triggers for a specific drawer
     * @param {string} drawerId - The ID of the drawer to find triggers for
     * @returns {DrawerTrigger[]} Array of trigger elements
     * @public
     */
    static getTriggersForDrawer(drawerId) {
      return Array.from(document.querySelectorAll(`drawer-trigger[data-target="${drawerId}"]`));
    }

    /**
     * Static method to validate if an element is a valid drawer trigger
     * @param {HTMLElement} element - The element to validate
     * @returns {boolean} True if the element is a valid drawer trigger
     * @public
     */
    static isValidTrigger(element) {
      return element && 
             element.matches('drawer-trigger') && 
             element.dataset.target &&
             document.getElementById(element.dataset.target);
    }

    /**
     * Debug method to get full diagnostic information about this trigger
     * Call from console: document.querySelector('drawer-trigger').debug()
     * @returns {object} Diagnostic information
     * @public
     */
    debug() {
      const targetId = this.dataset?.target || 'NOT SET';
      const targetDrawer = this.targetDrawer || (targetId !== 'NOT SET' ? document.getElementById(targetId) : null);
      
      const diagnostics = {
        trigger: {
          element: this,
          tagName: this.tagName,
          id: this.id || 'no-id',
          className: this.className || 'no-class',
          dataTarget: targetId,
          isConnected: this.isConnected,
          ariaExpanded: this.getAttribute('aria-expanded'),
          ariaControls: this.getAttribute('aria-controls'),
          ariaHaspopup: this.getAttribute('aria-haspopup'),
          tabindex: this.getAttribute('tabindex'),
          hasClickHandler: !!this.clickHandler,
          hasKeyHandler: !!this.keyHandler
        },
        targetDrawer: targetDrawer ? {
          element: targetDrawer,
          id: targetDrawer.id || 'no-id',
          tagName: targetDrawer.tagName,
          isDrawerElement: targetDrawer.matches('drawer-element'),
          isOpen: targetDrawer.isOpen || false,
          hasToggleMethod: typeof targetDrawer.toggleDrawer === 'function',
          hasCloseMethod: typeof targetDrawer.closeDrawer === 'function',
          hasOpenMethod: typeof targetDrawer.openDrawer === 'function',
          availableMethods: Object.getOwnPropertyNames(Object.getPrototypeOf(targetDrawer)).filter(name => typeof targetDrawer[name] === 'function'),
          triggersCount: targetDrawer.triggers?.size || 0,
          isInTriggers: targetDrawer.triggers?.has(this) || false
        } : {
          found: false,
          reason: targetId === 'NOT SET' ? 'No data-target attribute' : `Element with id "${targetId}" not found`,
          allDrawerElements: Array.from(document.querySelectorAll('drawer-element')).map(el => ({
            id: el.id || 'no-id',
            tagName: el.tagName
          }))
        },
        domState: {
          documentReady: document.readyState,
          triggerInDOM: document.contains(this),
          targetInDOM: targetDrawer ? document.contains(targetDrawer) : false
        }
      };
      
      console.group('[DrawerTrigger] 🔍 Debug Information');
      console.log('Trigger Element:', diagnostics.trigger);
      console.log('Target Drawer:', diagnostics.targetDrawer);
      console.log('DOM State:', diagnostics.domState);
      
      if (!targetDrawer) {
        console.warn('❌ Target drawer not found or not connected');
      } else if (!targetDrawer.matches('drawer-element')) {
        console.warn('❌ Target element is not a drawer-element');
      } else if (typeof targetDrawer.toggleDrawer !== 'function') {
        console.warn('❌ Target drawer does not have toggleDrawer method');
      } else {
        console.log('✅ Trigger appears to be properly configured');
      }
      
      console.groupEnd();
      
      return diagnostics;
    }

    /**
     * Static method to debug all drawer triggers on the page
     * Call from console: customElements.get('drawer-trigger').debugAll()
     * @public
     */
    static debugAll() {
      const triggers = Array.from(document.querySelectorAll('drawer-trigger'));
      
      console.group('[DrawerTrigger] 🔍 Debug All Triggers');
      console.log(`Found ${triggers.length} drawer trigger(s) on the page`);
      
      if (triggers.length === 0) {
        console.warn('No drawer triggers found on the page');
        console.groupEnd();
        return;
      }
      
      triggers.forEach((trigger, index) => {
        console.group(`Trigger ${index + 1}`);
        trigger.debug();
        console.groupEnd();
      });
      
      console.groupEnd();
      
      return triggers.map(trigger => trigger.debug());
    }
  }
);

// Define drawer-element custom element with position-aware animations
customElements.define(
  "drawer-element",
  class DrawerElement extends HTMLElement {
    constructor() {
      super();
      this.drawer = this.querySelector("drawer");
      this.overlay = this.querySelector(".js-overlay");
      this.keepHeader = false;
      this.isOpen = false;
      this.animationDuration =
        parseInt(this.dataset.animationDuration) || window.animation.duration;
      
      // Get position information from data attributes
      this.positionMobile = this.dataset.positionMobile || 'right';
      this.positionDesktop = this.dataset.positionDesktop || 'right';
      
      // Initialize resize handler
      this.resizeHandler = null;
      this.resizeTimeout = null;

      this.bindEvents();
      
      // Listen for search events to close drawer
      this.boundSearchOpenedHandler = this.#handleSearchOpened.bind(this);
      document.addEventListener('search:opened', this.boundSearchOpenedHandler);
    }

    /**
     * Called when the element is connected to the DOM
     * @private
     */
    connectedCallback() {
      // Ensure drawer is properly initialized
      if (!this.drawer) {
        console.error('DrawerElement: No drawer element found');
        return;
      }

      // Set initial state
      this.isOpen = false;
      this.keepHeader = false;

      // Remove the CSS transform classes that might conflict with JS animations
      const removeToOpenClasses = this.drawer.getAttribute('data-remove-to-open');
      if (removeToOpenClasses) {
        const classesToRemove = removeToOpenClasses.trim().split(/\s+/);
        classesToRemove.forEach(cls => {
          this.drawer.classList.remove(cls);
        });
      }

      // Set initial transform based on position using JS instead of CSS
      const currentPosition = this.#getCurrentPosition();
      this.drawer.style.transform = this.#getInitialTransform(currentPosition);
      this.drawer.style.visibility = "hidden";

      // Ensure the drawer element is properly positioned
      this.style.display = 'none';

      // Check for data-load-on-event attribute and set up event listener
      if (this.dataset.loadOnEvent) {
        const eventName = this.dataset.loadOnEvent;

        // Create bound event handler to maintain reference for cleanup
        this.boundEventHandler = async (event) => {
          // Only open if not already open
          if (!this.isOpen) {
            // For cart:added event, refetch the cart section to ensure fresh content
            if (eventName === 'cart:added' && this.id === 'cart-drawer') {
              await this.#refetchCartSection();
            }
            this.openDrawer();
          }
        };

        // Add event listener to document
        document.addEventListener(eventName, this.boundEventHandler);
      }

      // For cart drawer, also listen to cart:update events to handle cart becoming empty
      if (this.id === 'cart-drawer') {
        this.boundCartUpdateHandler = async (event) => {
          if (!this.isOpen) return;

          const itemCount = Number(event.detail?.cart?.item_count);
          if (!Number.isFinite(itemCount) || itemCount > 0) return;

          await this.#refetchCartSection();
        };
        
        document.addEventListener('cart:update', this.boundCartUpdateHandler);
      }

      // Listen for search events to close drawer
      if (!this.boundSearchOpenedHandler) {
        this.boundSearchOpenedHandler = this.#handleSearchOpened.bind(this);
        document.addEventListener('search:opened', this.boundSearchOpenedHandler);
      }
    }

    /**
     * Disconnect the component and clean up resources
     * @private
     */
    disconnectedCallback() {
      // Remove resize listener
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
      }

      // Clear any timeouts
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      // Remove the data-load-on-event listener if it was added
      if (this.dataset.loadOnEvent && this.boundEventHandler) {
        document.removeEventListener(this.dataset.loadOnEvent, this.boundEventHandler);
      }

      // Remove cart:update listener for cart drawer
      if (this.id === 'cart-drawer' && this.boundCartUpdateHandler) {
        document.removeEventListener('cart:update', this.boundCartUpdateHandler);
      }

      // Remove search event listener
      if (this.boundSearchOpenedHandler) {
        document.removeEventListener('search:opened', this.boundSearchOpenedHandler);
      }

      // Clean up
      this.cleanup();
    }

    /**
     * Bind all event listeners for the drawer component
     * @private
     */
    bindEvents() {
      // Add overlay click listener
      if (this.overlay) {
        this.overlay.addEventListener("click", () => this.closeDrawer());
      }

      // Add event listeners for keyup and focusout
      document.addEventListener("keyup", this.onKeyUp.bind(this));
      
      // Add resize listener to handle position changes
      this.resizeHandler = this.#handleResize.bind(this);
      window.addEventListener('resize', this.resizeHandler);
    }

    /**
     * Handle resize events to update position-based animations
     * @private
     */
    #handleResize() {
      // Debounce resize events
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      
      this.resizeTimeout = setTimeout(() => {
        // Skip layout work while pinch-zoomed (iOS Safari WebContent OOM risk).
        if (isViewportZoomed()) return;
        if (this.isOpen) {
          // Update transform if drawer is open
          const currentPosition = this.#getCurrentPosition();
          this.drawer.style.transform = this.#getOpenTransform(currentPosition);
        }
      }, 100);
    }

    /**
     * Refetch the cart section to update drawer content
     * @returns {Promise<void>}
     * @private
     */
    async #refetchCartSection() {
      try {
        const sectionEl = this.closest(".shopify-section");
        const sectionId =
          sectionEl?.id?.replace(/^shopify-section-/, "") || "cart-drawer";
        const url = new URL(window.location.href);
        url.searchParams.set("section_id", sectionId);

        const response = await fetch(url.toString());
        if (!response.ok) {
          throw new Error(`Failed to fetch cart section: ${response.status}`);
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const newDrawerElement = doc.querySelector(
          'drawer-element[id="cart-drawer"] drawer',
        );
        const currentDrawerElement = this.querySelector("drawer");

        if (newDrawerElement && currentDrawerElement) {
          currentDrawerElement.innerHTML = newDrawerElement.innerHTML;
        }
      } catch (error) {
        console.error("[Cart Drawer] Error refetching cart section:", error);
      }
    }

    /**
     * Get the current position based on viewport size
     * @returns {string} Current position for the current viewport
     * @private
     */
    #getCurrentPosition() {
      return window.innerWidth >= 768 ? this.positionDesktop : this.positionMobile;
    }

    /**
     * Get initial transform based on position
     * @param {string} position - The position of the drawer
     * @returns {string} CSS transform value
     * @private
     */
    #getInitialTransform(position) {
      const isMobile = window.innerWidth < 768;
      const actualPosition = isMobile ? this.positionMobile : this.positionDesktop;
      
      switch (actualPosition) {
        case 'left':
          return 'translateX(calc(-100% - 1rem))';
        case 'right':
          return 'translateX(calc(100% + 1rem))';
        case 'bottom-auto':
        case 'bottom-to-top':
        case 'bottom-to-almost-top':
          return 'translateY(3rem) opacity(0)';
        case 'top-to-bottom':
          return 'translateY(-3rem)';
        default:
          return 'translateX(calc(100% + 1rem))';
      }
    }

    /**
     * Get open transform based on position
     * @param {string} position - The position of the drawer
     * @returns {string} CSS transform value
     * @private
     */
    #getOpenTransform(position) {
      const isMobile = window.innerWidth < 768;
      const actualPosition = isMobile ? this.positionMobile : this.positionDesktop;
      
      switch (actualPosition) {
        case 'left':
        case 'right':
        case 'top-to-bottom':
          return 'translate(0, 0)';
        case 'bottom-auto':
        case 'bottom-to-top':
        case 'bottom-to-almost-top':
          return 'translateY(0) opacity(1)';
        default:
          return 'translate(0, 0)';
      }
    }

    /**
     * Get animation keyframes based on position and state
     * @param {string} position - The position of the drawer
     * @param {boolean} isOpening - Whether the drawer is opening
     * @returns {Array} Array of keyframe objects
     * @private
     */
    #getAnimationKeyframes(position, isOpening) {
      const isMobile = window.innerWidth < 768;
      const actualPosition = isMobile ? this.positionMobile : this.positionDesktop;
      
      if (isOpening) {
        // Opening animation
        switch (actualPosition) {
          case 'left':
            return [
              { transform: 'translateX(calc(-100% - 1rem))', opacity: 1 },
              { transform: 'translateX(0)', opacity: 1 }
            ];
          case 'right':
            return [
              { transform: 'translateX(calc(100% + 1rem))', opacity: isMobile ? 1 : 0 },
              { transform: 'translateX(0)', opacity: 1 }
            ];
          case 'bottom-auto':
          case 'bottom-to-top':
          case 'bottom-to-almost-top':
            return [
              { transform: 'translateY(3rem)', opacity: 0 },
              { transform: 'translateY(0)', opacity: 1 }
            ];
          case 'top-to-bottom':
            return [
              { transform: 'translateY(-3rem)', opacity: 1 },
              { transform: 'translateY(0)', opacity: 1 }
            ];
          default:
            return [
              { transform: 'translateX(calc(100% + 1rem))', opacity: isMobile ? 1 : 0 },
              { transform: 'translateX(0)', opacity: 1 }
            ];
        }
      } else {
        // Closing animation
        switch (actualPosition) {
          case 'left':
            return [
              { transform: 'translateX(0)', opacity: 1 },
              { transform: 'translateX(calc(-100% - 1rem))', opacity: 1 }
            ];
          case 'right':
            return [
              { transform: 'translateX(0)', opacity: 1 },
              { transform: 'translateX(calc(100% + 1rem))', opacity: isMobile ? 1 : 0 }
            ];
          case 'bottom-auto':
          case 'bottom-to-top':
          case 'bottom-to-almost-top':
            return [
              { transform: 'translateY(0)', opacity: 1 },
              { transform: 'translateY(3rem)', opacity: 0 }
            ];
          case 'top-to-bottom':
            return [
              { transform: 'translateY(0)', opacity: 1 },
              { transform: 'translateY(-3rem)', opacity: 1 }
            ];
          default:
            return [
              { transform: 'translateX(0)', opacity: 1 },
              { transform: 'translateX(calc(100% + 1rem))', opacity: isMobile ? 1 : 0 }
            ];
        }
      }
    }

    /**
     * Prepare drawer for animation by setting initial state
     * @param {HTMLElement} drawer - The drawer element
     * @param {string} position - The position of the drawer
     * @private
     */
    prepareDrawerForAnimation(drawer, position) {
      drawer.style.visibility = "visible";
      drawer.style.transform = this.#getInitialTransform(position);
    }

    /**
     * Clean up drawer after animation
     * @param {HTMLElement} drawer - The drawer element
     * @param {string} position - The position of the drawer
     * @private
     */
    cleanupDrawerAfterAnimation(drawer, position) {
      if (!this.isOpen) {
        drawer.style.visibility = "hidden";
        drawer.style.transform = this.#getInitialTransform(position);
        // unlockScrollForThis(this)
      }
    }

    /**
     * Get animation options with proper duration and easing
     * @returns {Object} Animation options
     * @private
     */
    getAnimationOptions() {
      return {
        duration: this.animationDuration,
        easing: window.animation.cubicBezier,
        fill: "forwards",
      };
    }

    /**
     * Animation for opening the drawer
     * @returns {Promise} Promise that resolves when animation completes
     * @private
     */
    async openDrawerAnimation() {
      // lockScrollForThis(this)
      const { drawer, overlay } = this;
      const options = this.getAnimationOptions();
      const currentPosition = this.#getCurrentPosition();

      this.prepareDrawerForAnimation(drawer, currentPosition);

      const drawerAnimation = drawer.animate(
        this.#getAnimationKeyframes(currentPosition, true),
        options,
      );

      const overlayAnimation = overlay?.animate(
        [{ opacity: 0 }, { opacity: 0.7 }],
        options,
      );

      await Promise.all([
        drawerAnimation.finished,
        overlayAnimation?.finished,
      ]);

      drawer.style.transform = this.#getOpenTransform(currentPosition);

    }

    /**
     * Animation for closing the drawer
     * @returns {Promise} Promise that resolves when animation completes
     * @private
     */
    async closeDrawerAnimation() {
      const { drawer, overlay } = this;
      const options = this.getAnimationOptions();
      const currentPosition = this.#getCurrentPosition();

      const drawerAnimation = drawer.animate(
        this.#getAnimationKeyframes(currentPosition, false),
        options,
      );

      const overlayAnimation = overlay?.animate(
        [{ opacity: 0.7 }, { opacity: 0 }],
        options,
      );

      await Promise.all([
        drawerAnimation.finished,
        overlayAnimation?.finished,
      ]);

      this.cleanupDrawerAfterAnimation(drawer, currentPosition);
    }

    /**
     * Open the drawer with proper animation
     * @returns {Promise} Promise that resolves when drawer is fully open
     * @private
     */
    async openDrawer() {
      try {
        this.isOpen = true;
        this.keepHeader = true;

        // Use LayerManager to handle z-index with parent tracking
        if (window.layerManager) {
          const drawerId = this.id || `drawer-${Date.now()}`;
          const parentContext = window.layerManager.getCurrentContext();
          window.layerManager.push(drawerId, 'drawer', this, parentContext);
          this.dataset.layerId = drawerId;

          // Mark as animating
          window.layerManager.startAnimating(drawerId);
        }

        // Update header sticky offset before opening drawer
        const headerElement = document.querySelector('header-element');
        if (headerElement && typeof headerElement.updateHeaderStickyOffset === 'function') {
          headerElement.updateHeaderStickyOffset();
        }

        // Show the drawer container
        this.style.display = 'block';
        this.classList.remove('hidden');

        // Handle nested drawer height constraints
        console.log('DrawerElement: Opening drawer', this.id);
        this.#constrainNestedDrawerHeight();

        // Lock scroll for this drawer
        if (typeof lockScrollForThis === 'function') {
          lockScrollForThis(this);
        }

        this.storeHeaderState();
        this.addBodyClass();
        this.dispatchEvent(new CustomEvent('drawer:opening', {
          bubbles: true,
          detail: { drawer: this }
        }));
        await this.openDrawerAnimation();

        // Animation complete
        if (window.layerManager && this.dataset.layerId) {
          window.layerManager.stopAnimating(this.dataset.layerId);
        }

        this.focusFirstFocusableElement();

        // Dispatch opened event
        this.dispatchEvent(new CustomEvent('drawer:opened', {
          bubbles: true,
          detail: { drawer: this }
        }));
      } catch (error) {
        console.error('DrawerElement: Error opening drawer:', error);
        this.isOpen = false;
        this.keepHeader = false;
        this.style.display = 'none';
      }
    }

    /**
     * Close the drawer with proper animation
     * @returns {Promise} Promise that resolves when drawer is fully closed
     * @private
     */
    async closeDrawer() {
      try {
        this.isOpen = false;
        this.keepHeader = false;

        // Mark as animating before removal
        if (window.layerManager && this.dataset.layerId) {
          window.layerManager.startAnimating(this.dataset.layerId);
        }

        this.removeBodyClass();
        this.dispatchEvent(new CustomEvent('drawer:closing', {
          bubbles: true,
          detail: { drawer: this }
        }));
        await this.closeDrawerAnimation();
        
        // Hide the drawer container first to prevent flash
        this.style.display = 'none';
        
        // Unlock scroll for this drawer
        if (typeof unlockScrollForThis === 'function') {
          unlockScrollForThis(this);
        }
        
        // Restore header state after drawer is hidden and scroll is unlocked
        this.restoreHeaderState();
        this.focusLastFocusedElement();

        // Reset height constraints when closing
        this.#resetHeightConstraints();

        // Animation complete - now remove from LayerManager
        if (window.layerManager && this.dataset.layerId) {
          window.layerManager.stopAnimating(this.dataset.layerId);
          window.layerManager.remove(this.dataset.layerId);
          delete this.dataset.layerId;
        }

        // Dispatch closed event
        this.dispatchEvent(new CustomEvent('drawer:closed', {
          bubbles: true,
          detail: { drawer: this }
        }));
      } catch (error) {
        console.error('DrawerElement: Error closing drawer:', error);
        this.isOpen = true;
        this.keepHeader = true;
      }
    }

    /**
     * Toggle the drawer state
     * @param {HTMLElement} trigger - The element that triggered the toggle
     * @private
     */
    toggleDrawer(trigger) {
      if (this.isOpen) {
        this.closeDrawer();
      } else {
        this.openDrawer();
      }
    }

    /**
     * Store the current header state
     * @private
     */
    storeHeaderState() {
      const header = document.querySelector("header");
      if (header) {
        this.dataset.originalStickyState = header.dataset.sticky;
        // header.dataset.sticky = "false";
      }
    }

    /**
     * Restore the original header state
     * @private
     */
    restoreHeaderState() {
      const header = document.querySelector("header");
      if (header && this.dataset.originalStickyState !== undefined) {
        header.dataset.sticky = this.dataset.originalStickyState;
      }
    }

    /**
     * Add body class for drawer open state
     * @private
     */
    addBodyClass() {
      document.body.classList.add("drawer-open", "overflow-hidden");
    }

    /**
     * Remove body class for drawer open state
     * @private
     */
    removeBodyClass() {
      document.body.classList.remove("drawer-open", "overflow-hidden");
    }

    /**
     * Focus the first focusable element in the drawer
     * @private
     */
    focusFirstFocusableElement() {
      const focusableElements = this.drawer.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }

    /**
     * Focus the last focused element before drawer opened
     * @private
     */
    focusLastFocusedElement() {
      // This would need to be implemented based on your focus management strategy
      // For now, we'll just focus the body
      document.body.focus();
    }

    /**
     * Handle key events for accessibility
     * @param {KeyboardEvent} event - The key event
     * @private
     */
    onKeyUp(event) {
      if (event.key === "Escape" && this.isOpen) {
        this.closeDrawer();
      }
    }

    /**
     * Cleanup method to reset stored states and prevent memory leaks
     * @private
     */
    cleanup() {
      delete this.dataset.originalStickyState;
      
      // Clear resize timeout
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = null;
      }
    }

    // Static utility methods for component querying and validation
    static isValidDrawer(element) {
      return element && element.tagName === 'DRAWER-ELEMENT';
    }

    static getDrawerPosition(element) {
      if (!this.isValidDrawer(element)) {
        return null;
      }
      
      return {
        mobile: element.dataset.positionMobile || 'right',
        desktop: element.dataset.positionDesktop || 'right'
      };
    }

    /**
     * Handle search opened event by closing the drawer
     * @param {CustomEvent} event - The search opened event
     * @private
     */
    #handleSearchOpened(event) {
      // Close drawer if it's currently open
      if (this.isOpen) {
        this.closeDrawer();
      }
    }

    /**
     * Constrain nested drawer height to prevent parent drawer bleed-through
     * @private
     */
    #constrainNestedDrawerHeight() {
      // Check if this drawer is nested within another drawer
      const parentDrawer = this.closest('drawer-element');
      
      if (parentDrawer && parentDrawer !== this) {
        // This is a nested drawer - constrain the parent drawer's height
        const parentDrawerElement = parentDrawer.querySelector('drawer');
        if (parentDrawerElement) {
          // Get the current viewport height
          const viewportHeight = window.innerHeight;
          
          // Calculate available height (viewport minus any header offset)
          const headerElement = document.querySelector('header');
          const headerHeight = headerElement ? headerElement.offsetHeight : 0;
          const availableHeight = viewportHeight - headerHeight;
          
          // Apply height constraint to the parent drawer element
          parentDrawerElement.style.maxHeight = `${availableHeight}px`;
          parentDrawerElement.style.height = `${availableHeight}px`;
          
          // Also constrain the parent drawer container
          parentDrawer.style.maxHeight = `${availableHeight}px`;
          parentDrawer.style.height = `${availableHeight}px`;
          
          // Add a class for CSS targeting
          parentDrawer.classList.add('parent-drawer-constrained');
          this.classList.add('nested-drawer-active');
          
          console.log('DrawerElement: Constrained parent drawer height to', availableHeight, 'px');
        }
      } else {
        // This is a top-level drawer - ensure no height constraints
        this.drawer.style.maxHeight = '';
        this.drawer.style.height = '';
        this.style.height = '';
        this.style.maxHeight = '';
        this.classList.remove('nested-drawer-constrained');
      }
    }

    /**
     * Reset height constraints for this drawer
     * @private
     */
    #resetHeightConstraints() {
      // Check if this drawer is nested within another drawer
      const parentDrawer = this.closest('drawer-element');
      
      if (parentDrawer && parentDrawer !== this) {
        // Reset constraints on the parent drawer
        const parentDrawerElement = parentDrawer.querySelector('drawer');
        if (parentDrawerElement) {
          parentDrawerElement.style.maxHeight = '';
          parentDrawerElement.style.height = '';
          parentDrawer.style.maxHeight = '';
          parentDrawer.style.height = '';
          parentDrawer.classList.remove('parent-drawer-constrained');
          
          console.log('DrawerElement: Reset parent drawer height constraints');
        }
      }
      
      // Reset constraints on this drawer
      this.drawer.style.maxHeight = '';
      this.drawer.style.height = '';
      this.style.height = '';
      this.style.maxHeight = '';
      this.classList.remove('nested-drawer-constrained', 'nested-drawer-active');
    }

    /**
     * Cleanup event listeners when element is removed
     */
    disconnectedCallback() {
      if (this.boundSearchOpenedHandler) {
        document.removeEventListener('search:opened', this.boundSearchOpenedHandler);
      }
      
      // Clean up resize handler
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
      }
      
      // Clean up timeout
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
    }
  }
);

/**
 * AccordionDisclosure - A web component for collapsible accordion sections with sync group support
 * 
 * This component extends the native HTML details element to provide enhanced accordion functionality
 * with smooth animations, keyboard navigation, and sync group support. Can be synchronized with
 * other disclosure elements using the data-sync-group attribute.
 * 
 * @example
 * <details is="accordion-disclosure" data-sync-group="my-group">
 *   <summary>Accordion Title</summary>
 *   <div>Accordion content...</div>
 * </details>
 */


// ElectricTab component - handles tab switching with proper ARIA support
class ElectricTab extends HTMLElement {
  #abortController; // Declare private field
  #isInitialized = false;
  
  constructor() {
    super();
    this.activeTabClass = 'text-body';
    this.inactiveTabClass = 'text-body/50';
    this.hiddenContentClass = 'hidden';
    this.#abortController = new AbortController();
    this.collectionId = null;
    this.collectionHandle = null;
  }

  connectedCallback() {
    this.tabButtons = this.querySelectorAll('[data-tab-button]');
    this.tabContents = this.querySelectorAll('[data-tab-content]');
    
    this.tabButtons.forEach(button => {
      button.addEventListener('click', this.#handleTabClick.bind(this), {
        signal: this.#abortController.signal
      });
      button.addEventListener('keydown', this.#handleKeyDown.bind(this), {
        signal: this.#abortController.signal
      });
    });

    // Activate first tab by default without focusing to prevent auto-scroll
    this.#activateTab(0, false);
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #handleTabClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const tabIndex = parseInt(button.dataset.tabIndex, 10);
    this.#activateTab(tabIndex);
  }

  #handleKeyDown(event) {
    const currentIndex = parseInt(event.currentTarget.dataset.tabIndex, 10);
    
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.#activateTab(Math.max(0, currentIndex - 1), true);
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.#activateTab(Math.min(this.tabButtons.length - 1, currentIndex + 1), true);
        break;
      case 'Home':
        event.preventDefault();
        this.#activateTab(0, true);
        break;
      case 'End':
        event.preventDefault();
        this.#activateTab(this.tabButtons.length - 1, true);
        break;
    }
  }

  #activateTab(tabIndex, shouldFocus = false) {
    // Update tab buttons
    this.tabButtons.forEach((btn, index) => {
      const isActive = index === tabIndex;
      btn.classList.toggle(this.activeTabClass, isActive);
      btn.classList.toggle(this.inactiveTabClass, !isActive);
      btn.setAttribute('aria-selected', isActive.toString());
      // Keep all tabs focusable for keyboard navigation - WCAG 2.1 compliance
      btn.setAttribute('tabindex', '0');
    });

    // Update tab contents
    this.tabContents.forEach((content, index) => {
      const isActive = index === tabIndex;
      content.classList.toggle(this.hiddenContentClass, !isActive);
      content.setAttribute('aria-hidden', (!isActive).toString());
    });

    // Only focus if explicitly requested (e.g., from keyboard navigation)
    if (shouldFocus) {
      this.tabButtons[tabIndex]?.focus();
    }

    // Announce tab change to screen readers
    this.#announceTabChange(tabIndex);

    // Dispatch custom event for the parent component to handle
    this.dispatchEvent(new CustomEvent('tabChanged', {
      bubbles: true,
      detail: { tabIndex }
    }));
  }

  /**
   * Announce tab change to screen readers
   * @param {number} tabIndex - Index of the activated tab
   * @private
   */
  #announceTabChange(tabIndex) {
    const activeTab = this.tabButtons[tabIndex];
    if (!activeTab) return;

    // Create or get the announcer element
    let announcer = this.querySelector('[aria-live]');
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.className = 'sr-only';
      this.appendChild(announcer);
    }

    // Announce the tab change
    const tabText = activeTab.textContent?.trim() || `Tab ${tabIndex + 1}`;
    announcer.textContent = `Switched to ${tabText} tab`;
  }
}

// Register the custom elements
if (!customElements.get('electric-tab')) {
  customElements.define('electric-tab', ElectricTab);
}
})();
(function () {
/**
 * @fileoverview Electric Disclosure System for Electric Maybe Shopify Theme
 * @description Shared disclosure system providing both accordion and dropdown functionality
 * with comprehensive accessibility, keyboard navigation, and sync group support.
 * 
 * @author Electric Maybe
 * @version 3.0.0
 * @license MIT
 */

/**
 * Base disclosure class that provides shared functionality for both accordion and dropdown variants
 * 
 * This class handles common disclosure patterns including:
 * - Sync group management (closing other disclosures when one opens)
 * - Keyboard navigation (Enter, Space, Arrow keys, Escape)
 * - Accessibility attributes (ARIA expanded, controls, labelledby)
 * - Event handling and cleanup
 * - Error handling and logging
 * 
 * @example
 * // For accordion disclosure
 * <accordion-disclosure data-sync-group="faq">
 *   <details>
 *     <summary>FAQ Item</summary>
 *     <div>Answer content</div>
 *   </details>
 * </accordion-disclosure>
 * 
 * @example
 * // For dropdown disclosure  
 * <dropdown-disclosure data-sync-group="filters">
 *   <details>
 *     <summary>Filter Options</summary>
 *     <div class="dropdown-content">Filter content</div>
 *   </details>
 * </dropdown-disclosure>
 */
class ElectricDisclosure extends HTMLElement {
  /** @type {boolean} Component connection state */
  #isConnected = false;
  
  /** @type {HTMLDetailsElement | null} Details element */
  #details = null;
  
  /** @type {HTMLElement | null} Summary element */
  #summary = null;
  
  /** @type {HTMLElement | null} Content element */
  #content = null;

  /**
   * Get the details element (protected for child classes)
   * @protected
   */
  get details() {
    return this.#details;
  }

  /**
   * Get the summary element (protected for child classes)
   * @protected
   */
  get summary() {
    return this.#summary;
  }

  /**
   * Get the content element (protected for child classes)
   * @protected
   */
  get content() {
    return this.#content;
  }
  
  /** @type {NodeList | null} Other elements in sync group */
  #syncGroup = null;
  
  /** @type {string} Disclosure type (accordion or dropdown) */
  #disclosureType = '';

  constructor() {
    super();
  }

  /**
   * Initialize the disclosure component
   * @private
   */
  connectedCallback() {
    if (this.#isConnected) return;
    
    try {
      this.#isConnected = true;
      this.#disclosureType = this.tagName.toLowerCase();
      this.#initializeElements();
      this.#setupAccessibility();
      this.#setupEventListeners();
      this.#setupSyncGroup();
      this.#setupInitialState();
    } catch (error) {
      this.#handleError('Failed to initialize disclosure', error);
    }
  }

  /**
   * Clean up resources when component is disconnected
   * @private
   */
  disconnectedCallback() {
    this.#isConnected = false;
    this.#cleanup();
  }

  /**
   * Initialize DOM elements
   * @private
   */
  #initializeElements() {
    this.#details = this.querySelector('details');
    this.#summary = this.#details?.querySelector('summary');
    // Find the first content element that is not a descendant of <summary>
    this.#content = null;
    if (this.#details) {
      const candidates = Array.from(this.#details.querySelectorAll('div, ul, ol, section, article'));
      for (const el of candidates) {
        // Exclude elements that are inside a <summary>
        if (!el.closest('summary')) {
          this.#content = el;
          break;
        }
      }
    }
    
    if (!this.#details) {
      throw new Error('ElectricDisclosure: Details element is required');
    }
    
    if (!this.#summary) {
      throw new Error('ElectricDisclosure: Summary element is required');
    }
    
    if (!this.#content) {
      throw new Error('ElectricDisclosure: Content element is required');
    }
  }

  /**
   * Setup accessibility attributes and roles
   * @private
   */
  #setupAccessibility() {
    // Set up summary accessibility
    this.#summary.setAttribute('tabindex', '0');
    this.#summary.setAttribute('role', 'button');
    this.#summary.setAttribute('aria-expanded', 'false');
    
    // Generate unique IDs for ARIA relationships
    const contentId = `${this.id || 'disclosure'}-content`;
    const summaryId = `${this.id || 'disclosure'}-summary`;
    
    this.#content.id = contentId;
    this.#summary.id = summaryId;
    
    // Set up ARIA relationships
    this.#summary.setAttribute('aria-controls', contentId);
    this.#content.setAttribute('aria-labelledby', summaryId);
    
    // Set appropriate role for content
    if (this.#disclosureType === 'dropdown-disclosure') {
      this.#content.setAttribute('role', 'menu');
    } else {
      this.#content.setAttribute('role', 'region');
    }
  }

  /**
   * Setup event listeners for keyboard and mouse interactions
   * @private
   */
  #setupEventListeners() {
    // Click handler
    this.#summary.addEventListener('click', this.#handleClick.bind(this));
    
    // Keyboard navigation
    this.#summary.addEventListener('keydown', this.#handleKeydown.bind(this));
    
    // Outside click detection for dropdowns
    if (this.#disclosureType === 'dropdown-disclosure') {
      document.addEventListener('click', this.#handleDocumentClick.bind(this), true);
    }
  }

  /**
   * Get current sync group members (always returns fresh list)
   * @returns {NodeList | null} Current sync group members or null if no sync group
   * @private
   */
  #getSyncGroup() {
    const syncGroupName = this.dataset.syncGroup;
    if (!syncGroupName) return null;
    
    return document.querySelectorAll(`[data-sync-group="${syncGroupName}"]`);
  }

  /**
   * Setup sync group functionality
   * @private
   */
  #setupSyncGroup() {
    // Store initial sync group reference for validation
    this.#syncGroup = this.#getSyncGroup();
  }

  /**
   * Setup initial state based on attributes
   * @private
   */
  #setupInitialState() {
    // Check if should be open by default
    if (this.#details.hasAttribute('open')) {
      this.setAttribute('aria-expanded', 'true');
      
      // For accordions, trigger the open animation
      if (this.#disclosureType === 'accordion-disclosure') {
        this.open();
      }
    }
  }

  /**
   * Handle click events on the summary
   * @param {Event} event - Click event
   * @private
   */
  #handleClick(event) {
    event.preventDefault();
    
    if (this.#details.open) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} event - Keydown event
   * @private
   */
  #handleKeydown(event) {
    // Prevent default behavior for these keys
    if (["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "Enter", " "].includes(event.key)) {
      event.preventDefault();
    }

    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        if (!this.#details.open) {
          this.open();
        } else if (this.#disclosureType === 'accordion-disclosure') {
          // Move focus to first focusable element in content
          const firstFocusable = this.#content.querySelector('[tabindex="0"], button, a, input, select, textarea');
          if (firstFocusable) {
            firstFocusable.focus();
          }
        }
        break;
        
      case "ArrowUp":
      case "ArrowLeft":
        if (this.#details.open && this.#disclosureType === 'accordion-disclosure') {
          this.close();
        }
        break;
        
      case "Enter":
      case " ": // Space key
        this.#handleClick(event);
        break;
        
      case "Escape":
        if (this.#details.open) {
          this.close();
          this.#summary.focus();
        }
        break;
    }
  }

  /**
   * Handle clicks outside the disclosure (for dropdowns)
   * @param {Event} event - Click event
   * @private
   */
  #handleDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  /**
   * Open the disclosure and close others in sync group
   * @private
   */
  open() {
    // Close other elements in the same sync group
    this.#closeSyncGroup();
    
    // Set open state
    this.#details.open = true;
    this.setAttribute('aria-expanded', 'true');
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('disclosure:opened', {
      detail: { type: this.#disclosureType },
      bubbles: true
    }));
  }

  /**
   * Close the disclosure
   * @private
   */
  close() {
    this.#details.open = false;
    this.setAttribute('aria-expanded', 'false');
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('disclosure:closed', {
      detail: { type: this.#disclosureType },
      bubbles: true
    }));
  }

  /**
   * Close other elements in the same sync group
   * @private
   */
  #closeSyncGroup() {
    const syncGroupMembers = this.#getSyncGroup();
    if (!syncGroupMembers) return;
    
    syncGroupMembers.forEach((el) => {
      // Skip if element is being replaced or not connected
      if (el === this || !el.isConnected) return;
      
      // Handle different types of disclosure elements
      const tagName = el.tagName.toLowerCase();
      
      if (tagName === 'accordion-disclosure' || tagName === 'dropdown-disclosure') {
        // Check if element has the close method (properly initialized)
        if (typeof el.close === 'function' && el.details?.open) {
          try {
            el.close();
          } catch (error) {
            console.warn('Failed to close sync group member:', error);
          }
        }
      } else if (el.classList.contains('disclosure-element')) {
        // Handle legacy disclosure-element components
        if (el.elements?.button?.getAttribute('aria-expanded') === 'true') {
          try {
            el.close();
          } catch (error) {
            console.warn('Failed to close sync group member:', error);
          }
        }
      }
    });
  }

  /**
   * Handle errors gracefully with logging and events
   * @param {string} message - Error message
   * @param {Error} error - Original error object
   * @private
   */
  #handleError(message, error) {
    console.error(`ElectricDisclosure: ${message}`, error);
    
    this.dispatchEvent(new CustomEvent('disclosure:error', {
      detail: { message, error: error.message, type: this.#disclosureType },
      bubbles: true
    }));
  }

  /**
   * Clean up resources and event listeners
   * @private
   */
  #cleanup() {
    if (this.#disclosureType === 'dropdown-disclosure') {
      document.removeEventListener('click', this.#handleDocumentClick, true);
    }
  }

  /**
   * Static utility method to find disclosure elements
   * @param {string} type - Disclosure type to find
   * @returns {NodeList} Matching disclosure elements
   * @static
   */
  static findDisclosures(type = '') {
    const selector = type ? `${type}` : 'accordion-disclosure, dropdown-disclosure';
    return document.querySelectorAll(selector);
  }

  /**
   * Static utility method to close all disclosures of a specific type
   * @param {string} type - Disclosure type to close
   * @static
   */
  static closeAll(type = '') {
    const disclosures = this.findDisclosures(type);
    disclosures.forEach(disclosure => {
      if (disclosure.details?.open) {
        disclosure.close();
      }
    });
  }

  /**
   * Static utility method to get sync group members by group name
   * Always returns fresh list of current DOM elements
   * @param {string} syncGroupName - Name of the sync group
   * @returns {NodeList} Current sync group members
   * @static
   */
  static getSyncGroup(syncGroupName) {
    if (!syncGroupName) {
      console.warn('ElectricDisclosure.getSyncGroup: syncGroupName is required');
      return document.querySelectorAll('[data-sync-group="__non_existent__"]'); // Empty NodeList
    }
    
    return document.querySelectorAll(`[data-sync-group="${syncGroupName}"]`);
  }
}

/**
 * Accordion Disclosure - Animated collapsible content sections
 * 
 * Extends ElectricDisclosure to provide smooth height animations
 * and accordion-specific behavior for content sections.
 * 
 * @example
 * <accordion-disclosure data-sync-group="faq">
 *   <details>
 *     <summary>FAQ Item</summary>
 *     <div>Answer content with smooth animations</div>
 *   </details>
 * </accordion-disclosure>
 */
class AccordionDisclosure extends ElectricDisclosure {
  /** @type {Animation | null} Current animation instance */
  #animation = null;
  
  /** @type {boolean} Whether accordion is currently closing */
  #isClosing = false;
  
  /** @type {boolean} Whether accordion is currently expanding */
  #isExpanding = false;

  constructor() {
    super();
  }

  /**
   * Override open method to add accordion-specific behavior
   * @private
   */
  open() {
    // Prevent interaction while animations are in progress
    if (this.#isClosing || this.#isExpanding) {
      return;
    }

    // Ensure any ongoing animation is canceled before starting a new one
    if (this.#animation) {
      this.#animation.cancel();
      this.#animation = null;
    }

    // Preset the host element height to the closed (summary-only) height so the
    // expand animation has a correct starting value even after base open logic
    const summaryHeight = `${this.summary?.offsetHeight || 0}px`;
    this.style.display = "block";
    this.style.height = summaryHeight;
    this.style.overflow = "hidden";

    // Call base open to handle sync-group closing, aria, and events
    super.open();

    // Animate to full height on next frame when layout has updated
    window.requestAnimationFrame(() => this.#expand());
  }

  /**
   * Override close method to add accordion-specific behavior
   * @private
   */
  close() {
    // Prevent interaction while animations are in progress
    if (this.#isClosing || this.#isExpanding) {
      return;
    }
    
    this.#shrink();
  }

  /**
   * Animate the accordion shrinking
   * @param {number} duration - Animation duration in milliseconds
   * @private
   */
  async #shrink(duration = window.animation.duration) {
    this.#isClosing = true;
    this.setAttribute('aria-expanded', 'false');

    const startHeight = `${this.offsetHeight + 1}px`;
    const endHeight = `${this.summary.offsetHeight + 2}px`;

    try {
        if (this.#animation) {
          this.#animation.cancel();
        }

        this.#animation = this.animate(
          {
            height: [startHeight, endHeight],
          },
          {
            duration,
            easing: window.animation.cubicBezier
          }
        );

        // Animate content opacity
        this.content.animate(
          {
            opacity: [1, 0],
            transform: ["translateY(0px)", "translateY(4px)"]
          },
          {
            duration: window.animation.durationShort,
            easing: window.animation.cubicBezier,
            fill: "forwards",
          }
        );

        this.#animation.onfinish = () => this.#onAnimationFinish(false);
        this.#animation.oncancel = () => (this.#isClosing = false);
    } catch (error) {
      console.error('AccordionDisclosure: Shrink animation failed', error);
      // Graceful degradation - just close without animation
      this.style.height = endHeight;
      this.#onAnimationFinish(false);
    }
  }

  /**
   * Animate the accordion expanding
   * @private
   */
  async #expand() {
    this.#isExpanding = true;
    const startHeight = `${this.offsetHeight + 2}px`;
    const endHeight = `${this.summary.offsetHeight + this.content?.offsetHeight + 1}px`;
    
    try {
        // Fallback to Web Animations API
        if (this.#animation) {
          this.#animation.cancel();
        }

        this.#animation = this.animate(
          {
            height: [startHeight, endHeight],
          },
          {
            duration: window.animation.duration,
            easing: window.animation.cubicBezier
          }
        );

        // Animate content opacity
        this.content.animate(
          {
            opacity: [0, 1],
            transform: ["translateY(4px)", "translateY(0)"]
          },
          {
            duration: window.animation.durationShort,
            easing: "ease-out",
            fill: "forwards",
          }
        );

        this.#animation.onfinish = () => this.#onAnimationFinish(true);
        this.#animation.oncancel = () => (this.#isExpanding = false);
    } catch (error) {
      console.error('AccordionDisclosure: Expand animation failed', error);
      // Graceful degradation - just open without animation
      this.style.height = 'auto';
      this.#onAnimationFinish(true);
    }
  }

  /**
   * Handle animation completion
   * @param {boolean} open - Whether the accordion is now open
   * @private
   */
  #onAnimationFinish(open) {
    this.details.open = open;
    this.#animation = null;
    this.#isClosing = false;
    this.#isExpanding = false;
    this.style.height = this.style.overflow = "";

    // When an accordion finishes closing, force a reflow on children
    // with slide-up-sm to reset their CSS animations/transitions
    if (!open) {
      this.#forceReflowOnSlideUpElements();
    }
  }

  /**
   * Public method to shrink accordion (for external control)
   * @param {number} duration - Animation duration
   */
  shrink(duration) {
    this.#shrink(duration);
  }

  /**
   * Force a synchronous reflow on elements that should re-run their CSS animations
   * Targets children with class `slide-up-sm` inside the accordion content
   * @private
   */
  #forceReflowOnSlideUpElements() {
    try {
      const targets = this.content?.querySelectorAll('.slide-up-sm');
      if (!targets || targets.length === 0) return;

      targets.forEach((node) => {
        // Force a reflow to restart CSS animations/transitions reliably
        // eslint-disable-next-line no-unused-expressions
        node.classList.remove('slide-up-sm');
        setTimeout(() => {
          void document.body.offsetHeight;
        }, 100);
        setTimeout(() => {
          node.classList.add('slide-up-sm');
        }, 200);
      });
    } catch (error) {
      console.error('AccordionDisclosure: failed to force reflow', error);
      this.dispatchEvent(new CustomEvent('disclosure:error', {
        detail: { message: 'Failed to force reflow', error: error?.message, type: 'accordion-disclosure' },
        bubbles: true
      }));
    }
  }
  
  /**
   * Clean up resources on disconnect
   * Per javascript-standards.mdc Line 156-173
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    
    // Cancel any ongoing animations
    if (this.#animation) {
      this.#animation.cancel();
      this.#animation = null;
    }
  }
}

/**
 * Dropdown Disclosure - Dropdown menu functionality
 * 
 * Extends ElectricDisclosure to provide dropdown-specific behavior
 * with absolute positioning and outside click detection.
 * 
 * @example
 * <dropdown-disclosure data-sync-group="filters">
 *   <details>
 *     <summary>Filter Options</summary>
 *     <div class="absolute top-full left-0 z-50">Filter content</div>
 *   </details>
 * </dropdown-disclosure>
 */
class DropdownDisclosure extends ElectricDisclosure {
  constructor() {
    super();
  }

  /**
   * Override open method to add dropdown-specific behavior
   * @private
   */
  open() {
    super.open();
    
    // Ensure dropdown content is properly positioned
    if (this.content) {
      this.content.style.display = 'block';
    }
  }

  /**
   * Override close method to add dropdown-specific behavior
   * @private
   */
  close() {
    super.close();
    
    // Hide dropdown content
    if (this.content) {
      this.content.style.display = 'none';
    }
  }
}

/**
 * Register all disclosure components when DOM is ready
 * @private
 */
function registerDisclosureComponents() {
  // Register accordion disclosure
  if (!customElements.get('accordion-disclosure')) {
    customElements.define('accordion-disclosure', AccordionDisclosure);
  }
  
  // Register dropdown disclosure
  if (!customElements.get('dropdown-disclosure')) {
    customElements.define('dropdown-disclosure', DropdownDisclosure);
  }
}

// Register components when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', registerDisclosureComponents);
} else {
  registerDisclosureComponents();
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    ElectricDisclosure,
    AccordionDisclosure,
    DropdownDisclosure
  };
}
})();
(function () {
/**
 * ImageHotspots - Interactive hotspot group for product feature callouts
 *
 * Wraps a positioned image with native `<details>` hotspots. Progressive
 * enhancement closes other open hotspots in the group, dismisses on outside
 * click, and closes on Escape. Works without JS via native details/summary.
 *
 * @example
 * <image-hotspots>
 *   <div class="relative w-full">
 *     <img src="..." alt="">
 *     <details data-hotspot style="left: 50%; top: 50%;">
 *       <summary aria-label="Feature">...</summary>
 *       <div>...</div>
 *     </details>
 *   </div>
 * </image-hotspots>
 */
class ImageHotspots extends HTMLElement {
  /** @type {boolean} */
  #isConnected = false;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    if (this.#isConnected) return;

    try {
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.addEventListener(
        'toggle',
        (event) => {
          const target = event.target;
          if (!(target instanceof HTMLDetailsElement)) return;
          if (!target.hasAttribute('data-hotspot')) return;

          this.#syncCardEnterAnimation(target);

          if (!target.open) return;

          this.querySelectorAll('details[data-hotspot][open]').forEach((details) => {
            if (details !== target) details.removeAttribute('open');
          });
        },
        { capture: true, signal },
      );

      document.addEventListener(
        'click',
        (event) => {
          if (!(event.target instanceof Node) || this.contains(event.target)) return;
          this.#closeAll();
        },
        { signal },
      );

      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape') this.#closeAll();
        },
        { signal },
      );

      this.#isConnected = true;
    } catch (error) {
      console.error('ImageHotspots: Error in connectedCallback', error);
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#isConnected = false;
  }

  /** @private */
  #closeAll() {
    this.querySelectorAll('details[data-hotspot][open]').forEach((details) => {
      details.removeAttribute('open');
    });
  }

  /**
   * Fade/slide the callout card in when a hotspot opens.
   * @param {HTMLDetailsElement} details
   * @private
   */
  #syncCardEnterAnimation(details) {
    const card = details.querySelector('[data-hotspot-card]');
    if (!(card instanceof HTMLElement)) return;

    card.classList.remove('is-entering');

    if (!details.open) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      card.classList.add('is-entering');
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        card.classList.add('is-entering');
      });
    });
  }
}

if (!customElements.get('image-hotspots')) {
  customElements.define('image-hotspots', ImageHotspots);
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = ImageHotspots;
}
})();
(function () {
/**
 * Dynamic section loader for Shopify themes
 * 
 * Handles lazy loading, content replacement, and URL-based updates
 * for Shopify theme sections with performance optimizations.
 * 
 * @example
 * <electric-section 
 *   data-section-id="collection-main"
 *   data-url="/collections/all"
 *   data-animate="true"
 *   data-onload="true"
 *   data-fire-event="section-updated">
 * </electric-section>
 * 
 * @example
 * // With custom event trigger
 * <electric-section 
 *   data-section-id="product-grid"
 *   data-load-on-event="filter-changed"
 *   data-animate="false">
 * </electric-section>
 * 
 * @example
 * // Prefetch and fetch events
 * const section = document.querySelector('electric-section');
 * 
 * // Prefetch content without replacing
 * section.dispatchEvent(new CustomEvent('electric-section:prefetch', {
 *   detail: { url: '/collections/all?sort_by=price-ascending' }
 * }));
 * 
 * // Fetch and replace content immediately
 * section.dispatchEvent(new CustomEvent('electric-section:fetch', {
 *   detail: { url: '/collections/all?sort_by=price-descending' }
 * }));
 */
class ElectricSection extends HTMLElement {
  // Private fields
  #intersectionObserver = null;
  #isInitializing = true;
  #lastFetchedUrl = null;
  #debouncedCheckAndFetch = null;
  #temporaryData = new WeakMap();
  #connectionState = 'disconnected';
  #selectorCache = new Map();
  #fragmentCache = new Map();
  #cacheSize = 0;
  #maxCacheSize = 10; // Maximum number of cached fragments
  #performanceObserver = null;

  // Observed attributes for reactive updates
  static observedAttributes = [
    "data-url",
    "data-section-id",
    "data-selector",
    "data-animate",
    "data-fire-event",
    "data-onload",
    "data-load-on-event",
    "data-to-keep",
    "data-disable-pop-events"
  ];

  // Relevant URL parameters for change detection
  static #RELEVANT_PARAMS = new Set([
    "sort_by",
    "page",
    "q",
    "view",
    "limit"
  ]);

  constructor() {
    super();
    
    // Create debounced fetch function once
    this.#debouncedCheckAndFetch = this.#debounce(
      this.#checkAndFetch.bind(this),
      250
    );

    // Setup performance monitoring
    this.#setupPerformanceMonitoring();
  }

  /**
   * Called when element is connected to DOM
   * @private
   */
  connectedCallback() {
    if (this.#connectionState === 'connected') return;
    
    try {
      this.#setupInitialState();
      this.#addEventListeners();
      this.#restoreTemporaryData();
      
      // Setup lazy loading if enabled
      if (this.dataset.onload === "true") {
        this.#setupIntersectionObserver();
      }
      
      this.#isInitializing = false;
      this.#connectionState = 'connected';
      
      // Dispatch ready event
      this.#dispatchEvent('electric-section-ready', { 
        sectionId: this.#getSectionId() 
      });
      
    } catch (error) {
      console.error('[ElectricSection] Initialization error:', error);
      this.#dispatchEvent('electric-section-error', { error });
    }
  }

  /**
   * Called when element is disconnected from DOM
   * @private
   */
  disconnectedCallback() {
    this.#connectionState = 'disconnected';
    this.#removeEventListeners();
    this.#removeIntersectionObserver();
    this.#removePerformanceObserver();
  }

  /**
   * Called when observed attributes change
   * @param {string} name - Attribute name
   * @param {string} oldValue - Previous value
   * @param {string} newValue - New value
   * @private
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || this.#isInitializing) return;

    switch (name) {
      case 'data-url':
        // Pass the new URL value to ensure we use the updated value
        this.#debouncedCheckAndFetch(false, true, newValue);
        break;
      case 'data-onload':
        if (newValue === "true") {
          this.#setupIntersectionObserver();
        } else {
          this.#removeIntersectionObserver();
        }
        break;
    }
  }

  /**
   * Setup intersection observer for lazy loading
   * @private
   */
  #setupIntersectionObserver() {
    if (this.#intersectionObserver) return;

    const options = {
      rootMargin: "0px 0px 800px 0px", // 800px below viewport
      threshold: 0
    };

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => this.#handleIntersection(entries),
      options
    );

    this.#intersectionObserver.observe(this);
  }

  /**
   * Handle intersection observer callback
   * @param {IntersectionObserverEntry[]} entries - Intersection entries
   * @private
   */
  #handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting && this.dataset.onload === "true") {
        queueMicrotask(() => this.#checkAndFetch(true));
        this.#removeIntersectionObserver();
      }
    });
  }

  /**
   * Remove intersection observer
   * @private
   */
  #removeIntersectionObserver() {
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /**
   * Setup initial state and URL
   * @private
   */
  #setupInitialState() {
    const defaultUrl = new URL(window.location.href);
    defaultUrl.searchParams.delete("section_id");
    
    this.#lastFetchedUrl = this.dataset.url || window.location.href;
    
    // Set ARIA attributes for accessibility
    this.setAttribute('aria-live', 'polite');
    this.setAttribute('role', 'region');
  }

  /**
   * Add event listeners for navigation and custom events
   * @private
   */
  #addEventListeners() {
    if (this.dataset.disablePopEvents !== "true") {
      window.addEventListener("popstate", () => this.#handlePopState(), { passive: true });
    }
    
    if (this.dataset.loadOnEvent) {
      window.addEventListener(this.dataset.loadOnEvent, () => this.#handlePopState(), { passive: true });
    }

    // Listen for prefetch and fetch events
    this.addEventListener('electric-section:prefetch', (e) => this.#handlePrefetchEvent(e));
    this.addEventListener('electric-section:fetch', (e) => this.#handleFetchEvent(e));
  }

  /**
   * Remove event listeners
   * @private
   */
  #removeEventListeners() {
    // Note: We can't remove arrow function listeners, but this is acceptable
    // since the element will be garbage collected when disconnected
  }

  /**
   * Handle browser navigation events
   * @private
   */
  #handlePopState() {
    this.dataset.url = this.#buildFetchUrl(window.location.href);
    this.#debouncedCheckAndFetch(false);
  }

  /**
   * Handle prefetch event - fetch and cache content without replacing
   * @param {CustomEvent} event - Prefetch event
   * @private
   */
  #handlePrefetchEvent(event) {
    const url = this.#buildFetchUrl(event.detail?.url);
    if (!url) {
      console.warn('[ElectricSection] Prefetch event missing URL in detail');
      return;
    }

    this.#prefetchContent(url);
  }

  /**
   * Handle fetch event - fetch and replace content immediately
   * @param {CustomEvent} event - Fetch event
   * @private
   */
  #handleFetchEvent(event) {
    const url = this.#buildFetchUrl(event.detail?.url);
    if (!url) {
      console.warn('[ElectricSection] Fetch event missing URL in detail');
      return;
    }

    // Update the data-url attribute to trigger normal fetch flow
    this.dataset.url = url;
  }

  /**
   * Check if content should be fetched and fetch if needed
   * @param {boolean} isInitialLoad - Whether this is the initial load
   * @param {boolean} isAttributeChange - Whether triggered by attribute change
   * @param {string} newUrl - New URL value (for attribute changes)
   * @returns {Promise<boolean>} Whether content was fetched
   * @private
   */
  async #checkAndFetch(isInitialLoad = false, isAttributeChange = false, newUrl = null) {
    const startTime = performance.now();
    
    try {
      // Use the new URL if provided (for attribute changes), otherwise use dataset.url
      const urlToUse = newUrl || this.dataset.url;
      const currentUrl = new URL(urlToUse, window.location.origin);
      const dataUrl = new URL(this.#lastFetchedUrl, window.location.origin);

      // Get all relevant parameters including dynamic filters
      const relevantParams = this.#getRelevantParameters(currentUrl, dataUrl);
      
      // Always fetch on attribute change, otherwise check if it's initial load
      let shouldFetch = isAttributeChange || isInitialLoad;

      if (shouldFetch) {
        const fetchUrl = this.#buildFetchUrl(urlToUse);

        if (fetchUrl !== this.#lastFetchedUrl) {
          this.#lastFetchedUrl = fetchUrl;

          const success = await this.#fetchAndReplaceContent(fetchUrl, isAttributeChange);
          
          // Log performance if operation took longer than 16ms
          const duration = performance.now() - startTime;
          if (duration > 16) {
            console.warn(`[ElectricSection] Slow operation: ${duration.toFixed(2)}ms`);
          }
          
          return success;
        }
      }
      
      return false;
    } catch (error) {
      console.error('[ElectricSection] Check and fetch error:', error);
      this.#dispatchEvent('electric-section-error', { error });
      return false;
    }
  }

  /**
   * Get all relevant URL parameters including dynamic filters
   * @param {URL} currentUrl - Current URL
   * @param {URL} dataUrl - Data URL
   * @returns {Set<string>} Set of relevant parameter names
   * @private
   */
  #getRelevantParameters(currentUrl, dataUrl) {
    const params = new Set(ElectricSection.#RELEVANT_PARAMS);
    
    // Add dynamic filter parameters
    [currentUrl, dataUrl].forEach(url => {
      Array.from(url.searchParams.keys())
        .filter(key => key.startsWith('filter.'))
        .forEach(key => params.add(key));
    });
    
    return params;
  }

  /**
   * Update URL parameters with new values
   * @param {URL} url - URL to update
   * @param {string} param - Parameter name
   * @param {string[]} values - New values
   * @private
   */
  #updateUrlParameters(url, param, values) {
    url.searchParams.delete(param);
    values.forEach(value => url.searchParams.append(param, value));
  }

  /**
   * Build fetch URL with section ID
   * @param {string} url - Base URL
   * @returns {string} Fetch URL with section ID
   * @private
   */
  #buildFetchUrl(url) {
    const fetchUrl = new URL(url, window.location.origin);
    const sectionId = this.#getSectionId();
    if (sectionId) {
      fetchUrl.searchParams.set("section_id", sectionId);
    }
    return fetchUrl.toString();
  }

  /**
   * Get the section ID from the closest section element or data attribute
   * @returns {string|null} Section ID or null if not found
   * @private
   */
  #getSectionId() {
    // First check if we have a data-section-id attribute
    if (this.dataset.sectionId) {
      return this.dataset.sectionId;
    }
    
    // Find the closest section element
    const sectionElement = this.closest('section');
    if (sectionElement) {
      // Try to get the section ID from the section element's ID
      const sectionId = sectionElement.id;
      if (sectionId && sectionId.startsWith('shopify-section-')) {
        return sectionId.replace('shopify-section-', '');
      }
      
      // Try to get from data-section-id attribute on the section
      if (sectionElement.dataset.sectionId) {
        return sectionElement.dataset.sectionId;
      }
    }
    
    return null;
  }

  /**
   * Get optimized selector for section ID
   * @param {string} sectionId - Section ID
   * @returns {string} Optimized selector
   * @private
   */
  #getOptimizedSelector(sectionId) {
    if (!this.#selectorCache.has(sectionId)) {
      // Try selectors in order of specificity and performance
      const selectors = [
        `#shopify-section-${sectionId}`, // Most specific, fastest
        `[data-section-id="${sectionId}"]`,
        `[data-section="${sectionId}"]`
      ];
      
      // Cache the first working selector
      for (const selector of selectors) {
        if (document.querySelector(selector)) {
          this.#selectorCache.set(sectionId, selector);
          break;
        }
      }
      
      // Fallback to first selector if none found
      if (!this.#selectorCache.has(sectionId)) {
        this.#selectorCache.set(sectionId, selectors[0]);
      }
    }
    
    return this.#selectorCache.get(sectionId);
  }

  /**
   * Store temporary data before content replacement
   * @private
   */
  #storeTemporaryData() {
    if (!this.dataset.toKeep) return;
    
    const dataToKeep = {};
    this.dataset.toKeep.split(",").forEach(key => {
      dataToKeep[key.trim()] = this.dataset[key.trim()];
    });
    
    this.#temporaryData.set(this, dataToKeep);
    
    // Auto-cleanup after 3 seconds
    setTimeout(() => {
      this.#temporaryData.delete(this);
    }, 3000);
  }

  /**
   * Restore temporary data after content replacement
   * @private
   */
  #restoreTemporaryData() {
    const savedData = this.#temporaryData.get(this);
    if (!savedData) return;
    
    Object.entries(savedData).forEach(([key, value]) => {
      this.dataset[key] = value;
    });
  }

  /**
   * Fetch and replace content
   * @param {string} url - URL to fetch from
   * @param {boolean} isAttributeChange - Whether triggered by attribute change
   * @returns {Promise<boolean>} Whether operation was successful
   * @private
   */
  async #fetchAndReplaceContent(url, isAttributeChange = false) {
    const startTime = performance.now();
    
    try {
      this.setAttribute('aria-busy', 'true');
      this.#storeTemporaryData();
      
      let newContent = this.#getCachedFragment(url);
      
      if (!newContent) {
        const response = await this.#fetchWithRetry(url);
        const html = await response.text();
        newContent = this.#parseHTML(html);
        
        if (newContent === null) {
          this.remove();
          return false;
        }
        
        // Cache the parsed fragment
        this.#cacheFragment(url, newContent);
      }

      // Replace content with or without animation
      if (this.dataset.animate !== "false") {
        await this.#animateTransition(newContent);
      } else {
        this.#replaceContent(newContent);
      }

      this.#fireCustomEvent(newContent);
      
      const duration = performance.now() - startTime;
      this.#dispatchEvent('electric-section-updated', { 
        url, 
        duration,
        isAttributeChange 
      });
      
      return true;
    } catch (error) {
      console.error('[ElectricSection] Fetch and replace error:', error);
      this.#dispatchEvent('electric-section-error', { error });
      return false;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Get cached fragment if available
   * @param {string} url - URL to check cache for
   * @returns {Element|null} Cached fragment or null
   * @private
   */
  #getCachedFragment(url) {
    const cached = this.#fragmentCache.get(url);
    if (cached) {
      // Move to end for LRU behavior
      this.#fragmentCache.delete(url);
      this.#fragmentCache.set(url, cached);
      return cached.cloneNode(true);
    }
    return null;
  }

  /**
   * Cache fragment with LRU behavior
   * @param {string} url - URL to cache
   * @param {Element} fragment - Fragment to cache
   * @private
   */
  #cacheFragment(url, fragment) {
    // Remove oldest entry if cache is full
    if (this.#cacheSize >= this.#maxCacheSize) {
      const firstKey = this.#fragmentCache.keys().next().value;
      this.#fragmentCache.delete(firstKey);
      this.#cacheSize--;
    }
    
    this.#fragmentCache.set(url, fragment.cloneNode(true));
    this.#cacheSize++;
    
    // Auto-cleanup after 5 minutes
    setTimeout(() => {
      if (this.#fragmentCache.has(url)) {
        this.#fragmentCache.delete(url);
        this.#cacheSize--;
      }
    }, 300000);
  }

  /**
   * Prefetch content without replacing current content
   * @param {string} url - URL to prefetch
   * @private
   */
  async #prefetchContent(url) {
    try {
      // Check if already cached
      if (this.#getCachedFragment(url)) {
        return;
      }

      // Fetch and cache the content
      const response = await this.#fetchWithRetry(url);
      const html = await response.text();

      // Validate the content can be parsed
      const parsed = this.#parseHTML(html);
      if (parsed) {
        this.#cacheFragment(url, parsed);
        
        // Dispatch prefetch success event
        this.#dispatchEvent('electric-section:prefetch-success', { 
          url,
          cached: true 
        });
      } else {
        throw new Error('Failed to parse prefetched content');
      }
    } catch (error) {
      console.error('[ElectricSection] Prefetch error:', error);
      this.#dispatchEvent('electric-section:prefetch-error', { 
        url, 
        error: error.message 
      });
    }
  }

  /**
   * Fetch with retry logic and exponential backoff
   * @param {string} url - URL to fetch
   * @param {number} retries - Number of retry attempts
   * @returns {Promise<Response>} Fetch response
   * @private
   */
  async #fetchWithRetry(url, retries = 3) {
    for (let i = 0; i < retries; i++) {
      try {
        const response = await fetch(url, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        return response;
      } catch (error) {
        if (i === retries - 1) throw error;
        
        const delay = 1000 * Math.pow(2, i);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  /**
   * Parse HTML and extract section content with optimized selector
   * @param {string} html - HTML string to parse
   * @returns {Element|null} Parsed section element or null
   * @private
   */
  #parseHTML(html) {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      
      // Check for parsing errors
      const parseError = doc.querySelector('parsererror');
      if (parseError) {
        throw new Error('HTML parsing failed');
      }
      
      const sectionId = this.#getSectionId();
      if (!sectionId) {
        console.warn('[ElectricSection] No section ID found for parsing');
        return null;
      }
      
      // Use optimized selector
      const selector = this.#getOptimizedSelector(sectionId);
      const element = doc.querySelector(selector);
      
      return element || null;
    } catch (error) {
      console.error('[ElectricSection] HTML parsing error:', error);
      return null;
    }
  }

  /**
   * Animate content transition using CSS transitions
   * @param {Element} newContent - New content to animate in
   * @private
   */
  async #animateTransition(newContent) {
    try {
      // Add transition class
      this.classList.add('electric-section-transitioning');
      
      // Fade out current content
      this.style.opacity = '0';
      this.style.transition = 'opacity 150ms ease-out';
      
      // Wait for fade out
      await new Promise(resolve => setTimeout(resolve, 150));
      
      this.#replaceContent(newContent);
      
      // Fade in new content
      this.style.transition = 'opacity 150ms ease-in';
      this.style.opacity = '1';
      
      // Clean up after animation
      setTimeout(() => {
        this.classList.remove('electric-section-transitioning');
        this.style.transition = '';
        this.style.opacity = '';
      }, 150);
      
    } catch (error) {
      console.error('[ElectricSection] Animation error:', error);
      // Fallback to immediate replacement
      this.#replaceContent(newContent);
    }
  }

  /**
   * Replace current content with new content using DocumentFragment
   * @param {Element} newContent - New content element
   * @private
   */
  #replaceContent(newContent) {
    // Preserve any temporary data
    const tempData = this.#temporaryData.get(this);
    
    // Use DocumentFragment for better performance
    const fragment = document.createDocumentFragment();
    while (newContent.firstChild) {
      fragment.appendChild(newContent.firstChild);
    }
    
    // Clear and replace content
    this.innerHTML = '';
    this.appendChild(fragment);
    
    // Restore temporary data to new element
    if (tempData) {
      this.#temporaryData.set(this, tempData);
    }
  }

  /**
   * Fire custom event after content update
   * @param {Element} newContent - New content element
   * @private
   */
  #fireCustomEvent(newContent) {
    if (this.dataset.fireEvent) {
      const event = new CustomEvent(this.dataset.fireEvent, {
        bubbles: true,
        detail: {
          source: this.#getSectionId(),
          found: newContent !== null,
          url: this.dataset.url
        }
      });
      this.dispatchEvent(event);
    }
  }

  /**
   * Dispatch internal event
   * @param {string} eventName - Event name
   * @param {object} detail - Event detail
   * @private
   */
  #dispatchEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, {
      bubbles: true,
      detail: {
        sectionId: this.#getSectionId(),
        ...detail
      }
    });
    this.dispatchEvent(event);
  }

  /**
   * Setup performance monitoring
   * @private
   */
  #setupPerformanceMonitoring() {
    if ('PerformanceObserver' in window) {
      try {
        this.#performanceObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'measure' && entry.name.includes('electric-section')) {
              if (entry.duration > 100) {
                console.warn(`[ElectricSection] Slow operation detected: ${entry.name} took ${entry.duration.toFixed(2)}ms`);
              }
            }
          }
        });
        
        this.#performanceObserver.observe({ entryTypes: ['measure'] });
      } catch (error) {
        console.warn('[ElectricSection] Performance monitoring not available:', error);
      }
    }
  }

  /**
   * Remove performance observer
   * @private
   */
  #removePerformanceObserver() {
    if (this.#performanceObserver) {
      this.#performanceObserver.disconnect();
      this.#performanceObserver = null;
    }
  }

  /**
   * Debounce function to limit execution frequency
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} Debounced function
   * @private
   */
  #debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Static utility methods for external use

  /**
   * Find all electric-section elements in the document
   * @param {string} sectionId - Optional section ID to filter by
   * @returns {ElectricSection[]} Array of electric-section elements
   * @static
   */
  static findAll(sectionId = null) {
    const selector = sectionId 
      ? `electric-section[data-section-id="${sectionId}"]`
      : 'electric-section';
    return Array.from(document.querySelectorAll(selector));
  }

  /**
   * Find electric-section element by section ID
   * @param {string} sectionId - Section ID to find
   * @returns {ElectricSection|null} Electric section element or null
   * @static
   */
  static findBySectionId(sectionId) {
    return document.querySelector(`electric-section[data-section-id="${sectionId}"]`);
  }

  /**
   * Trigger content update for all electric-section elements
   * @param {string} sectionId - Optional section ID to filter by
   * @static
   */
  static async updateAll(sectionId = null) {
    const elements = this.findAll(sectionId);
    await Promise.all(elements.map(el => el.#checkAndFetch(false, true)));
  }

  /**
   * Clear all cached content
   * @static
   */
  static clearCache() {
    // This would need to be implemented if we want to expose cache clearing
    console.warn('[ElectricSection] Cache clearing not implemented in this version');
  }

  /**
   * Prefetch content for a specific section
   * @param {string} sectionId - Section ID to prefetch for
   * @param {string} url - URL to prefetch
   * @static
   */
  static prefetch(sectionId, url) {
    const section = this.findBySectionId(sectionId);
    if (section) {
      section.dispatchEvent(new CustomEvent('electric-section:prefetch', {
        detail: { url },
        bubbles: false
      }));
    }
  }

  /**
   * Fetch and replace content for a specific section
   * @param {string} sectionId - Section ID to fetch for
   * @param {string} url - URL to fetch
   * @static
   */
  static fetch(sectionId, url) {
    const section = this.findBySectionId(sectionId);
    if (section) {
      section.dispatchEvent(new CustomEvent('electric-section:fetch', {
        detail: { url },
        bubbles: false
      }));
    }
  }

  /**
   * Prefetch content for all sections
   * @param {string} url - URL to prefetch
   * @static
   */
  static prefetchAll(url) {
    const sections = this.findAll();
    sections.forEach(section => {
      section.dispatchEvent(new CustomEvent('electric-section:prefetch', {
        detail: { url },
        bubbles: false
      }));
    });
  }
}

// Register the custom element with safety check
if (!customElements.get('electric-section')) {
  customElements.define('electric-section', ElectricSection);
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ElectricSection;
}
})();
(function () {
/**
 * ElectricPub - Simple Event Publisher
 * Dispatches custom events when buttons or radio buttons inside are interacted with
 * 
 * @example
 * <electric-pub data-channel=".my-section" data-hover-event="prefetch" data-click-event="fetch">
 *   <button data-detail-url="/collections/all" data-detail-foo="bar">All Products</button>
 * </electric-pub>
 * 
 * @example With radio buttons for tabs
 * <electric-pub data-channel=".my-section" data-change-event="fetch">
 *   <input type="radio" name="tabs" id="tab1" data-detail-url="/collections/all" checked>
 *   <label for="tab1">All Products</label>
 * </electric-pub>
 */
class ElectricPub extends HTMLElement {
  #isConnected = false;

  constructor() {
    super();
    this.handleMouseOver = this.handleMouseOver.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  connectedCallback() {
    if (this.#isConnected) return;
    
    this.addEventListener('mouseover', this.handleMouseOver, { passive: true });
    this.addEventListener('click', this.handleClick, { passive: true });
    this.addEventListener('change', this.handleChange, { passive: true });
    this.addEventListener('focus', this.handleFocus, { passive: true });
    this.addEventListener('keydown', this.handleKeyDown);
    this.#isConnected = true;
  }

  disconnectedCallback() {
    if (!this.#isConnected) return;
    
    this.removeEventListener('mouseover', this.handleMouseOver);
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('change', this.handleChange);
    this.removeEventListener('focus', this.handleFocus);
    this.removeEventListener('keydown', this.handleKeyDown);
    this.#isConnected = false;
  }

  handleMouseOver(event) {
    this.#processEvent(event, 'hoverEvent');
  }

  handleClick(event) {
    this.#processEvent(event, 'clickEvent');
  }

  handleChange(event) {
    this.#processEvent(event, 'changeEvent');
  }

  handleFocus(event) {
    this.#processEvent(event, 'focusEvent');
  }

  handleKeyDown(event) {
    const target = event.target;
    if (target.role !== 'tab') return;

    const tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    const currentIndex = tabs.indexOf(target);

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        const prevIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
        this.#activateTab(tabs[prevIndex]);
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        const nextIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex + 1;
        this.#activateTab(tabs[nextIndex]);
        break;
      case 'Home':
        event.preventDefault();
        this.#activateTab(tabs[0]);
        break;
      case 'End':
        event.preventDefault();
        this.#activateTab(tabs[tabs.length - 1]);
        break;
    }
  }

  #processEvent(event, eventTypeKey) {
    // Handle both button clicks and radio button changes
    const interactiveElement = event.target.closest('button') || event.target.closest('input[type="radio"]') || event.target.querySelector('input[type="radio"]');

    if (!interactiveElement) return;

    // For tab buttons, activate the tab on click
    if (eventTypeKey === 'clickEvent' && interactiveElement.role === 'tab') {
      this.#activateTab(interactiveElement);
    }

    const eventName = this.dataset[eventTypeKey];
    if (!eventName) return;

    const channel = this.dataset.channel;
    if (!channel) return;

    const targetElement = document.querySelector(channel);
    if (!targetElement) return;

    const detail = {};
    
    // Add all data-detail-* properties to the detail object
    Array.from(interactiveElement.attributes).forEach(attr => {
      if (attr.name.startsWith('data-detail-')) {
        const key = attr.name.replace('data-detail-', '');
        detail[key] = attr.value;
      }
    });

    // For radio buttons, add the checked state
    if (interactiveElement.type === 'radio') {
      detail.checked = interactiveElement.checked;
      detail.name = interactiveElement.name;
      detail.value = interactiveElement.value;
    }

    // For tab buttons, add active state
    if (interactiveElement.role === 'tab') {
      detail.active = interactiveElement.getAttribute('aria-selected') === 'true';
      detail.tabId = interactiveElement.id;
    }

    targetElement.dispatchEvent(new CustomEvent(eventName, {
      detail,
      bubbles: true,
      cancelable: true
    }));
  }

  #activateTab(tab) {
    // Deactivate all tabs
    const tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    tabs.forEach(t => {
      t.setAttribute('aria-selected', 'false');
      t.setAttribute('tabindex', '-1');
      t.removeAttribute('data-active');
    });

    // Activate selected tab
    tab.setAttribute('aria-selected', 'true');
    tab.setAttribute('tabindex', '0');
    tab.setAttribute('data-active', 'true');
    tab.focus();
  }

  /**
   * Get all radio buttons within this component
   * @returns {NodeListOf<HTMLInputElement>}
   */
  static getRadioButtons(element) {
    return element.querySelectorAll('input[type="radio"]');
  }

  /**
   * Get the currently checked radio button
   * @returns {HTMLInputElement|null}
   */
  static getCheckedRadio(element) {
    return element.querySelector('input[type="radio"]:checked');
  }

  /**
   * Check a specific radio button by value
   * @param {string} value - The value of the radio button to check
   */
  static checkRadioByValue(element, value) {
    const radio = element.querySelector(`input[type="radio"][value="${value}"]`);
    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

if (!customElements.get('electric-pub')) {
  customElements.define('electric-pub', ElectricPub);
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = ElectricPub;
}
})();
(function () {
if (!customElements.get("electric-slider")) {
  class ElectricSlider extends HTMLElement {
    /** @type {{ timeoutId: ReturnType<typeof setTimeout>, scrollEndHandler: () => void, slider: HTMLElement } | null} */
    #pendingSnapRestore = null;

    /**
     * While true, scroll-driven index updates are skipped (programmatic smooth scroll
     * would otherwise apply page-based math that disagrees with centered slides).
     * @private
     */
    #suppressScrollIndexSync = false;

    /** @private Incremented so stale scrollend/timeouts from a prior navigation cannot clear state. */
    #navScrollSyncToken = 0;

    /**
     * When true, skip {@link #scrollThumbnailIntoView} in updateNavs — used when the user
     * clicked a thumbnail so the main feed is animating; aligning the thumb rail mid-flight
     * fights that scroll. Cleared when main programmatic scroll finishes ({@link #restoreSliderSnapStyles}).
     * @private
     */
    #deferThumbnailRailScroll = false;

    constructor() {
      super();
      this.state = {
        isDragging: false,
        wasDragged: false,
        dragThreshold: 10,
        pos: { left: 0, x: 0 },
        isTransitioning: false,
        lastWheelTime: 0
      };
      
      this.observers = [];
      this.eventRegistry = [];
      this.items = [];
      /** @type {HTMLElement | null} Grid row wrapping bar + nav (not getter-cached; `els` returns a new object each time). */
      this._navChromeRow = null;

      // Pre-bind critical methods to avoid repeated binding
      this.dragMove = this.dragMove.bind(this);
      this.dragEnd = this.dragEnd.bind(this);
      this.resetScrollSnap = this.resetScrollSnap.bind(this);
    }

    connectedCallback() {
      try {
        this.cacheElements();
        this.setupObservers();
        this.attachEvents();
        
        // Reset drag state if drag is disabled
        if (this.dataset.drag === "false") {
          this.state.wasDragged = false;
          this.state.isDragging = false;
        }
      } catch (error) {
        console.error('ElectricSlider initialization error:', error);
      }
    }

    disconnectedCallback() {
      this.#clearPendingSnapRestore();
      this.detachEvents();
      this.disconnectObservers();
      this._navChromeRow = null;

      // Clean up content update timeout
      if (this.contentUpdateTimeout) {
        clearTimeout(this.contentUpdateTimeout);
        this.contentUpdateTimeout = null;
      }

      const feed = this.querySelector(".electric-slider__feed");
      if (feed) {
        feed.style.removeProperty("scroll-snap-type");
        feed.style.removeProperty("scroll-behavior");
      }
    }

    /**
     * Re-bind observers/events after the slider's inner DOM is replaced in-place
     * (e.g. PDP combined-listing gallery swap via data-variant-dynamic).
     * connectedCallback does not re-run when only innerHTML changes.
     */
    reinitialize() {
      try {
        this.#clearPendingSnapRestore();
        this.#suppressScrollIndexSync = false;
        this.#deferThumbnailRailScroll = false;
        this.#navScrollSyncToken += 1;

        this.startObserver?.disconnect();
        this.endObserver?.disconnect();
        this.elementObserver?.disconnect();
        this.startObserver = null;
        this.endObserver = null;
        this.elementObserver = null;

        this.detachEvents();
        this.disconnectObservers();
        this._navChromeRow = null;

        if (this.contentUpdateTimeout) {
          clearTimeout(this.contentUpdateTimeout);
          this.contentUpdateTimeout = null;
        }

        this.items = [];
        this.firstEl = null;
        this.lastEl = null;
        this.currentIndex = 0;

        this.cacheElements();
        this.setupObservers();
        this.attachEvents();

        if (this.dataset.drag === "false") {
          this.state.wasDragged = false;
          this.state.isDragging = false;
        }

        this.initSlider();
      } catch (error) {
        console.error("ElectricSlider reinitialize error:", error);
      }
    }

    get els() {
      const target = this.dataset.id;
      return {
        slider: this.querySelector(".electric-slider__feed") || (() => { throw new Error("Slider feed not found"); })(),
        prevBtn: this.querySelector(`button[aria-label="previous"][data-slider-target="${target}"]`),
        nextBtn: this.querySelector(`button[aria-label="next"][data-slider-target="${target}"]`),
        upBtn: this.querySelector(`button[name="up"][data-slider-target="${target}"]`),
        downBtn: this.querySelector(`button[name="down"][data-slider-target="${target}"]`),
        gradient: this.querySelector("gradient-finish"),
        dots: this.querySelector('.electric-slider__dots'),
        thumbnails: this.querySelector('.electric-slider__thumbnails'),
        sliderBar: this.querySelector(`[data-slider-target="${target}"].electric-slider__bar`),
        verticalBarWrapper: this.querySelector(`.electric-slider__vertical-bar-wrapper[data-slider-target="${target}"]`),
      }
    }

    cacheElements() {
      const { prevBtn } = this.els;

      // Bottom layout: bar + buttons live in a shared grid row above the feed.
      // On-sides: buttons are direct children of this — _navChromeRow stays null.
      let navChromeRow = null;
      const btnParent = prevBtn?.parentElement;
      if (btnParent && btnParent !== this) {
        const row = btnParent.parentElement;
        if (row && row !== this && row.contains(btnParent) && row.contains(prevBtn)) {
          navChromeRow = row;
        }
      }
      this._navChromeRow = navChromeRow;
    }

    attachEvents() {
      const { slider, prevBtn, nextBtn, sliderBar } = this.els;
      
      // Common event handler setup
      // Only prevent clicks if drag is enabled AND was dragged
      if (this.dataset.drag !== "false") {
        this.addEvent(slider, 'click', e => this.state.wasDragged && (e.preventDefault(), e.stopPropagation(), this.state.wasDragged = false), true);
      }
      this.addEvent(slider, 'scroll', () => this.throttleUpdate(), { passive: true });
      this.addEvent(slider, 'pointerdown', () => this.#onSliderPointerDownDuringProgrammaticScroll(), {
        passive: true,
      });
      
      // Drag events
      if (this.dataset.drag !== "false") {
        this.addEvent(slider, 'mousedown', e => this.startDrag(e));
        if (this.dataset.carouselMode === "fade") {
          this.addEvent(slider, 'touchstart', e => {
            // Store initial touch position
            this.state.initialTouch = {
              x: e.touches[0].clientX,
              y: e.touches[0].clientY,
              time: Date.now()
            };
            
            // Don't prevent default yet, we'll do it in touchmove if needed
            this.startDrag(e, false);
          }, { passive: true });
        }
      }
      
      // Fade mode specific
      if (this.dataset.carouselMode === "fade") {
        this.addEvent(slider, 'wheel', e => this.handleWheel(e), { passive: false });
      }
      
      // Navigation buttons
      this.addEvent(prevBtn, 'click', e => this.handleDirectionClick(e));
      this.addEvent(nextBtn, 'click', e => this.handleDirectionClick(e));
      
      // Vertical navigation buttons
      const { upBtn, downBtn } = this.els;
      this.addEvent(upBtn, 'click', e => this.handleVerticalDirectionClick(e));
      this.addEvent(downBtn, 'click', e => this.handleVerticalDirectionClick(e));
      
      // Listen for product fetch completion events
      this.addEvent(this, 'rebuy-products-loaded', () => this.handleProductsLoaded());
      this.addEvent(this, 'rebuy-shopify-products-loaded', () => this.handleProductsLoaded());
    }
    
    addEvent(element, eventType, handler, options = {}) {
      if (!element) return;
      element.addEventListener(eventType, handler, options);
      this.eventRegistry.push([element, eventType, handler, options]);
    }

    detachEvents() {
      this.eventRegistry.forEach(([element, eventType, handler, options]) => {
        element?.removeEventListener(eventType, handler, options);
      });
      this.eventRegistry = [];
      this.cleanUpGlobalEvents();
    }

    cleanUpGlobalEvents() {
      document.removeEventListener('mousemove', this.dragMove);
      document.removeEventListener('touchmove', this.dragMove);
      document.removeEventListener('mouseup', this.dragEnd);
      document.removeEventListener('mouseleave', this.dragEnd);
      document.removeEventListener('touchend', this.dragEnd);
      document.removeEventListener('touchcancel', this.dragEnd);
    }

    addGlobalEvents(isTouch) {
      const events = isTouch 
        ? [['touchmove', this.dragMove, {passive: false}], ['touchend', this.dragEnd], ['touchcancel', this.dragEnd]]
        : [['mousemove', this.dragMove], ['mouseup', this.dragEnd], ['mouseleave', this.dragEnd]];
      
      events.forEach(([event, handler, options]) => 
        document.addEventListener(event, handler, options));
    }

    setupObservers() {
      const { slider } = this.els;

      // Dynamic content changes
      if (this.dataset.dummy === "true") {
        this.addObserver(new MutationObserver(() => this.initSlider()), 
          slider, { childList: true });
      }
      
      // Content change observer - detects when items are added/removed
      const contentObserver = new MutationObserver((mutations) => {
        let shouldUpdate = false;
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            // Check if any added nodes are slider items
            const addedItems = Array.from(mutation.addedNodes).some(node => 
              node.nodeType === Node.ELEMENT_NODE && 
              (node.classList?.contains('electric-slider__item') || 
               node.querySelector?.('.electric-slider__item'))
            );
            if (addedItems) {
              shouldUpdate = true;
            }
          }
        });
        
        if (shouldUpdate) {
          // Debounce updates to avoid excessive calls
          clearTimeout(this.contentUpdateTimeout);
          this.contentUpdateTimeout = setTimeout(() => {
            this.handleContentChange();
          }, 100);
        }
      });
      
      this.addObserver(contentObserver, slider, { childList: true, subtree: true });
      
      // Resize observer for content dimension changes
      const resizeObserver = new ResizeObserver(() => {
        // Pinch-zoom does not change layout boxes; skip to avoid reflow storms
        // that contribute to iOS Safari WebContent crashes under zoom.
        if (document.documentElement.hasAttribute('data-viewport-zoomed')) return;
        requestAnimationFrame(() => {
          if (document.documentElement.hasAttribute('data-viewport-zoomed')) return;
          this.handleContentChange();
        });
      });
      
      this.addObserver(resizeObserver, slider);
      
      // Visibility observer
      const visibilityObserver = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          this.initSlider();
          visibilityObserver.unobserve(slider);
        }
      });
      
      this.addObserver(visibilityObserver, slider);
      
      // Attribute observer for dynamic drag toggle
      const attributeObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'data-drag') {
            this.handleDragAttributeChange();
          }
        });
      });
      
      attributeObserver.observe(this, { attributes: true, attributeFilter: ['data-drag'] });
      this.observers.push({ observer: attributeObserver, target: this, options: { attributes: true, attributeFilter: ['data-drag'] } });
    }
    
    addObserver(observer, target, options = {}) {
      observer.observe(target, options);
      this.observers.push({ observer, target, options });
    }
    
    /**
     * Handle dynamic changes to the data-drag attribute
     * @private
     */
    handleDragAttributeChange() {
      const isDragDisabled = this.dataset.drag === "false";
      
      // Reset drag state when drag is disabled
      if (isDragDisabled) {
        this.state.wasDragged = false;
        this.state.isDragging = false;
        
        // Clean up any active drag operations
        this.cleanUpGlobalEvents();
      }
    }
    
    /**
     * Handle product fetch completion events
     * Updates navigation visibility and edge states when products are dynamically loaded
     * @private
     */
    handleProductsLoaded() {
      // Wait a brief moment for DOM to update with new products
      requestAnimationFrame(() => {
        // Re-cache items if they've changed
        const { slider } = this.els;
        if (slider) {
          const newItems = this.#queryFeedSlideElements(slider);

          if (newItems.length !== this.items?.length) {
            this.handleContentChange();
          } else {
            // Just update UI state
            this.updateUI();
            this.updateEdgeStates();
          }
        }
      });
    }
    
    /**
     * Handle content changes (items added/removed)
     * @private
     */
    handleContentChange() {
      const { slider } = this.els;
      
      // Re-cache slider items
      this.items = this.#queryFeedSlideElements(slider);

      if (!this.items.length) return;
      
      // Wait for elements to be properly rendered before finding first/last
      requestAnimationFrame(() => {
        // Find first and last visible elements (don't mutate original array)
        const itemsArray = [...this.items];
        this.firstEl = itemsArray.map(item => this.#getFirstChildWithWidth(item)).find(el => el);
        this.lastEl = [...itemsArray].reverse().map(item => this.#getLastChildWithWidth(item)).find(el => el);
        
        // If elements still don't have width, wait a bit more
        if (!this.firstEl || !this.lastEl || this.firstEl.clientWidth === 0 || this.lastEl.clientWidth === 0) {
          setTimeout(() => {
            this.firstEl = itemsArray.map(item => this.#getFirstChildWithWidth(item)).find(el => el);
            this.lastEl = [...itemsArray].reverse().map(item => this.#getLastChildWithWidth(item)).find(el => el);
            
            if (this.firstEl && this.lastEl) {
              this.setupEdgeDetection();
              this.updateUI();
              this.updateEdgeStates();
            }
          }, 100);
          return;
        }
        this.setupEdgeDetection();
        
        // Update UI state
        this.updateUI();
        
        // Update edge states after content change
        this.updateEdgeStates();
      });
    }
    
    /**
     * Handle content resize events
     * @private
     */
    handleContentResize() {
      // Only update if slider is already initialized
      if (!this.items?.length) return;
      
      // Re-cache first and last elements in case they changed
      const itemsArray = [...this.items];
      this.firstEl = itemsArray.map(item => this.#getFirstChildWithWidth(item)).find(el => el);
      this.lastEl = [...itemsArray].reverse().map(item => this.#getLastChildWithWidth(item)).find(el => el);
      
      // Update UI state
      this.updateUI();
      
      // Update edge states after resize
      setTimeout(() => this.updateEdgeStates(), 50);
    }
    
    createObserver(callback, options = {}) {
      return new IntersectionObserver(callback, options);
    }
    
    disconnectObservers() {
      this.observers.forEach(({ observer }) => observer.disconnect());
      this.observers = [];
    }

    /**
     * Get the first child element with actual width, handling display: contents
     * @param {Element} element - Element to check
     * @returns {Element|null} First child with width > 0, or null
     * @private
     */
    #getFirstChildWithWidth(element) {
      if (!element) return null;
      
      // Check if element itself has width
      if (element.clientWidth > 0) return element;
      
      // Check if element has display: contents
      const computedStyle = window.getComputedStyle(element);
      if (computedStyle.display === 'contents') {
        // Recursively check first child
        const firstChild = element.firstElementChild;
        if (firstChild) {
          return this.#getFirstChildWithWidth(firstChild);
        }
      }
      
      return null;
    }

    /**
     * @returns {boolean}
     * @private
     */
    #prefersReducedMotion() {
      return (
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      );
    }

    /**
     * @param {'smooth'|'instant'|string} behavior
     * @returns {'smooth'|'auto'}
     * @private
     */
    #resolveScrollBehavior(behavior) {
      if (behavior === "instant" || behavior === "auto") return "auto";
      if (this.#prefersReducedMotion()) return "auto";
      return "smooth";
    }

    /**
     * Horizontal scroll position to center a feed child (layout from visible rects —
     * offsetLeft can disagree with flex + gap when offsetParent ≠ scroll container).
     * @param {HTMLElement} slider
     * @param {HTMLElement} slide
     * @returns {number|null}
     * @private
     */
    #computeCenteredScrollLeft(slider, slide) {
      if (!slider || !slide) return null;
      if (slider.clientWidth <= 0) return null;
      const sliderRect = slider.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const viewCenterX = sliderRect.left + sliderRect.width / 2;
      const slideCenterX = slideRect.left + slideRect.width / 2;
      const delta = slideCenterX - viewCenterX;
      const target = slider.scrollLeft + delta;
      const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
      return Math.max(0, Math.min(target, maxScroll));
    }

    /**
     * Which slide’s center is nearest to the viewport center (for center-aligned feeds).
     * @param {HTMLElement} slider
     * @returns {number}
     * @private
     */
    #getClosestSlideIndexToViewportCenter(slider) {
      const sliderRect = slider.getBoundingClientRect();
      const viewCenterX = sliderRect.left + sliderRect.width / 2;
      let closestIndex = 0;
      let closestDist = Infinity;

      this.items.forEach((item, index) => {
        if (this.#isThumbnailRail(item)) return;
        const r = item.getBoundingClientRect();
        if (r.width <= 0 && r.height <= 0) return;
        const itemCenterX = r.left + r.width / 2;
        const dist = Math.abs(itemCenterX - viewCenterX);
        if (dist < closestDist) {
          closestDist = dist;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    /**
     * @param {HTMLElement} el
     * @returns {boolean}
     * @private
     */
    #isThumbnailRail(el) {
      return (
        el?.tagName === "ELECTRIC-THUMBNAILS" ||
        el?.classList?.contains("electric-slider__thumbnails")
      );
    }

    /**
     * Slide nodes inside the horizontal feed (excludes thumbnail rail and PDP overlay siblings).
     * Product gallery injects badges/overlays as extra feed children — they must not be "slides"
     * or indices disagree with thumbnails/dots.
     * @param {HTMLElement | null} feed - `.electric-slider__feed`
     * @returns {NodeListOf<HTMLElement>}
     * @private
     */
    #queryFeedSlideElements(feed) {
      if (!feed) {
        return document.createDocumentFragment().querySelectorAll("*");
      }
      const excl = ":not(.electric-slider__thumbnails):not([data-electric-slider-ignore])";
      if (feed.querySelector(":scope > *.contents")) {
        return feed.querySelectorAll(`.contents > *${excl}`);
      }
      return feed.querySelectorAll(`:scope > *${excl}`);
    }

    /**
     * Scroll the thumbnail strip so the active control sits in view (centered when possible).
     * @param {number} targetIndex
     * @private
     */
    #scrollThumbnailIntoView(targetIndex) {
      if (!this.hasAttribute("data-align-thumbnails")) return;
      const { thumbnails: rail } = this.els;
      if (!rail) return;
      const thumb = rail.children[targetIndex];
      if (!thumb) return;
      const targetLeft = this.#computeCenteredScrollLeft(rail, thumb);
      if (targetLeft === null) return;
      rail.scrollTo({
        left: targetLeft,
        behavior: this.#resolveScrollBehavior("smooth"),
      });
    }

    /**
     * Cancels pending scrollend/timeout from a previous programmatic scroll.
     * @private
     */
    #clearPendingSnapRestore() {
      const p = this.#pendingSnapRestore;
      if (!p) return;
      clearTimeout(p.timeoutId);
      p.slider.removeEventListener("scrollend", p.scrollEndHandler);
      this.#pendingSnapRestore = null;
    }

    /**
     * Re-applies CSS snap / scroll-behavior from classes after programmatic scroll.
     * @param {HTMLElement} slider
     * @private
     */
    #restoreSliderSnapStyles(slider) {
      slider.style.removeProperty("scroll-snap-type");
      slider.style.removeProperty("scroll-behavior");
      const runThumbAlignAfterMain =
        this.hasAttribute("data-align-thumbnails") && this.#deferThumbnailRailScroll;
      this.#deferThumbnailRailScroll = false;
      requestAnimationFrame(() => {
        this.updateSliderBar();
        this.updateEdgeStates();
        if (runThumbAlignAfterMain) {
          const idx = this.currentIndex;
          requestAnimationFrame(() => this.#scrollThumbnailIntoView(idx));
        }
      });
    }

    /**
     * Restores scroll-snap after programmatic scroll. Uses scrollend or timeout; syncToken
     * ensures stale handlers from a newer navigation do not run.
     * @param {HTMLElement} slider
     * @param {'smooth'|'auto'} resolvedBehavior
     * @param {number} syncToken
     * @private
     */
    #scheduleSnapRestoreAfterProgrammaticScroll(slider, resolvedBehavior, syncToken) {
      this.#clearPendingSnapRestore();

      const finish = () => {
        this.#clearPendingSnapRestore();
        if (syncToken !== this.#navScrollSyncToken) return;
        this.#suppressScrollIndexSync = false;
        this.#restoreSliderSnapStyles(slider);
      };

      if (resolvedBehavior !== "smooth") {
        requestAnimationFrame(() => requestAnimationFrame(finish));
        return;
      }

      let completed = false;
      const onDone = () => {
        if (completed) return;
        completed = true;
        finish();
      };

      const scrollEndHandler = () => onDone();
      const timeoutId = setTimeout(onDone, 1000);
      this.#pendingSnapRestore = {
        timeoutId,
        scrollEndHandler,
        slider,
      };
      slider.addEventListener("scrollend", scrollEndHandler, { once: true });
    }

    /**
     * User grabbed the feed during a programmatic scroll — release index lock and snap styles.
     * @private
     */
    #onSliderPointerDownDuringProgrammaticScroll() {
      if (!this.#suppressScrollIndexSync && !this.#pendingSnapRestore && !this.#deferThumbnailRailScroll) return;
      this.#navScrollSyncToken++;
      this.#suppressScrollIndexSync = false;
      this.#deferThumbnailRailScroll = false;
      this.#clearPendingSnapRestore();
      const feed = this.querySelector(".electric-slider__feed");
      if (feed) {
        feed.style.removeProperty("scroll-snap-type");
        feed.style.removeProperty("scroll-behavior");
      }
    }

    /**
     * Get the last child element with actual width, handling display: contents
     * @param {Element} element - Element to check
     * @returns {Element|null} Last child with width > 0, or null
     * @private
     */
    #getLastChildWithWidth(element) {
      if (!element) return null;
      
      // Check if element itself has width
      if (element.clientWidth > 0) return element;
      
      // Check if element has display: contents
      const computedStyle = window.getComputedStyle(element);
      if (computedStyle.display === 'contents') {
        // Recursively check last child
        const lastChild = element.lastElementChild;
        if (lastChild) {
          return this.#getLastChildWithWidth(lastChild);
        }
      }
      
      return null;
    }

    initSlider() {
      const { slider, sliderBar, dots, thumbnails } = this.els;

      // attempts to check if slider is loaded late
      if (!slider.clientWidth) {
        let attempts = 0;
        const maxAttempts = 10;
        
        const tryInit = () => {
          if (attempts >= maxAttempts) return;
          attempts++;
          
          setTimeout(() => {
            if (!slider.clientWidth) {
              tryInit();
            } else {
              this.initSlider();
            }
          }, 100);
        };
        
        tryInit();
        return;
      }

      
      // Cache slider items
      this.items = this.#queryFeedSlideElements(slider);

      if (!this.items.length) return;

      // Find first and last visible elements
      const itemsArray = [...this.items];
      this.firstEl = itemsArray.map(item => this.#getFirstChildWithWidth(item)).find(el => el);
      this.lastEl = [...itemsArray].reverse().map(item => this.#getLastChildWithWidth(item)).find(el => el);
      
      if (!this.firstEl || !this.lastEl) return;

      if (sliderBar) {
        // Initialize progress bar at 10% minimum
        sliderBar.style.setProperty('--slider-bar-progress', '0.1');
      }
            
      this.currentIndex = 0;

      if (dots) {
        [...dots.children].forEach((dot, index) => {
          dot.dataset.active = index === 0 ? 'true' : 'false';
          this.addEvent(dot, 'click', () => this.navigateSlider(index, 'instant'));
        });
      }

      if (thumbnails) {
        [...thumbnails.children].forEach((thumbnail, index) => {
          thumbnail.dataset.active = index === 0 ? 'true' : 'false';
          this.addEvent(thumbnail, 'click', () =>
            this.navigateSlider(index, 'smooth', { deferThumbnailRailUntilMainScrollEnd: true })
          );
        });
      }

      // Set up carousel modes
      this.setupSliderMode();

      // Setup edge detection
      this.setupEdgeDetection();
      
      // Initialize UI state
      this.updateUI();
      
      // Ensure edge states are correct after initialization
      setTimeout(() => this.updateEdgeStates(), 100);
    }
    
    setupSliderMode() {
      if (this.dataset.carouselMode === "fade") {
        this.setupFadeMode();
      } else if (this.dataset.carouselMode === "slide") {
        this.setupSlideMode();
      }
    }
    
    setupEdgeDetection() {
      const { slider } = this.els;
      
      // Use scroll-based edge detection for more reliable results
      this.addEvent(slider, 'scroll', () => this.updateEdgeStates(), { passive: true });
      
      // Initial edge state update
      this.updateEdgeStates();
      
      // Fallback intersection observer for additional reliability
      const options = { 
        threshold: [0, 0.1, 0.9, 1], 
        root: slider,
        rootMargin: '0px -1px 0px -1px' // Small margin to account for sub-pixel rendering
      };
      
      this.startObserver = this.createObserver(entries => {
        const entry = entries[0];
        const isAtStart = entry.intersectionRatio >= 0.99;
        this.els.prevBtn?.toggleAttribute("disabled", isAtStart);
        this.updateNavVisibility();
      }, options);
      
      this.endObserver = this.createObserver(entries => {
        const entry = entries[0];
        const isAtEnd = entry.intersectionRatio >= 0.99;
        this.els.nextBtn?.toggleAttribute("disabled", isAtEnd);
        this.updateNavVisibility();
      }, options);
      
      // Observer to manage activation
      this.elementObserver = this.createObserver(entries => {
        if (entries[0].isIntersecting) {
          if (this.firstEl) this.startObserver.observe(this.firstEl);
          if (this.lastEl) this.endObserver.observe(this.lastEl);
        } else {
          if (this.firstEl) this.startObserver.unobserve(this.firstEl);
          if (this.lastEl) this.endObserver.unobserve(this.lastEl);
        }
      }, { threshold: 0.1 });
      
      this.elementObserver.observe(slider);
    }
    
    setupFadeMode() {
      const { slider } = this.els;
      
      slider.style.position = 'relative';
      slider.style.overflow = 'hidden';
      
      this.items.forEach(item => {
        item.style.position = 'absolute';
        item.style.inset = '0';
      });
    }
    
    setupSlideMode() {
      const { slider, sliderBar } = this.els;
      
      // Clone the first element and add it as the last item to create an infinite loop effect
      const clonedFirstElement = this.firstEl.cloneNode(true);
      clonedFirstElement.classList.add('cloned-slide');
      slider.appendChild(clonedFirstElement);
      
      // Set up intersection observer to detect when the clone is in view
      const firstCloneObserver = this.createObserver(entries => {
        if (entries[0].isIntersecting && !this.state.isTransitioning) {
          this.state.isTransitioning = true;
          slider.scrollTo({ left: slider.clientWidth, behavior: 'instant' });
          setTimeout(() => { this.state.isTransitioning = false; }, 50);
        }
      }, { threshold: 1.0, root: slider });
      
      firstCloneObserver.observe(clonedFirstElement);
      this.observers.push({ observer: firstCloneObserver, target: clonedFirstElement, options: { threshold: 1.0, root: slider } });

      // Clone the last element and add it as the first item
      const clonedLastElement = this.lastEl.cloneNode(true);
      clonedLastElement.classList.add('cloned-slide');
      slider.insertBefore(clonedLastElement, slider.firstChild);
      slider.scrollTo({ left: slider.clientWidth, behavior: 'instant' });

      const lastCloneObserver = this.createObserver(entries => {
        if (entries[0].isIntersecting && !this.state.isTransitioning) {
          this.state.isTransitioning = true;
          slider.scrollTo({ left: slider.scrollWidth - (2 * slider.clientWidth), behavior: 'instant' });
          setTimeout(() => { this.state.isTransitioning = false; }, 50);
        }
      }, { threshold: 1.0, root: slider });
      
      lastCloneObserver.observe(clonedLastElement);
      this.observers.push({ observer: lastCloneObserver, target: clonedLastElement, options: { threshold: 1.0, root: slider } });

      if (sliderBar) {
        this.cloneSliderBar = sliderBar.cloneNode(true);
        this.cloneSliderBar.style.transform = `translateX(calc(var(--slider-bar-clone-position) - var(--slider-bar-width)))`;
        this.cloneSliderBar.style.willChange = 'transform';
        sliderBar.parentElement.appendChild(this.cloneSliderBar);
      }
    }

    updateUI() {
      this.updateGradient();
      this.updateScrollability();
      this.updateSliderBar();
      this.updateVerticalBar();
      this.updateEdgeStates();
    }

    updateGradient() {
      const { gradient, slider } = this.els;
      
      if (gradient && this.items?.[0]) {
        const height = this.items[0].clientHeight || 0;
        slider.style.setProperty("--gradient-height", `${height}px`);
      }
    }

    updateScrollability() {
      const { slider, nav, sliderBar, dots, thumbnails, prevBtn, nextBtn, verticalBarWrapper } = this.els;
      const isHidden = this.dataset.carouselMode === "fade" ? this.items.length <= 1 : slider.scrollWidth <= slider.clientWidth;
      nav?.classList.toggle("hidden", isHidden);
      sliderBar?.parentElement?.classList.toggle("hidden", isHidden);
      dots?.classList.toggle("hidden", isHidden);
      thumbnails?.classList.toggle("hidden", isHidden);
      prevBtn?.classList.toggle("hidden", isHidden);
      nextBtn?.classList.toggle("hidden", isHidden);
      
      // Vertical bar visibility is handled in updateVerticalBar() method
      // since it depends on vertical scrollability, not horizontal
    }

    updateNavVisibility() {
      const { prevBtn, nextBtn, sliderBar } = this.els;

      if (!prevBtn || !nextBtn) return;

      let navChromeRow = this._navChromeRow;
      if (!navChromeRow && prevBtn.parentElement !== this) {
        const btnParent = prevBtn.parentElement;
        const row = btnParent?.parentElement;
        if (
          row &&
          row !== this &&
          btnParent &&
          row.contains(btnParent) &&
          row.contains(prevBtn)
        ) {
          navChromeRow = row;
          this._navChromeRow = row;
        }
      }

      const bothDisabled =
        prevBtn.hasAttribute("disabled") && nextBtn.hasAttribute("disabled");

      if (navChromeRow) {
        navChromeRow.classList.toggle("hidden", bothDisabled);
      } else {
        prevBtn.classList.toggle("hidden", bothDisabled);
        nextBtn.classList.toggle("hidden", bothDisabled);
        sliderBar?.parentElement?.classList.toggle("hidden", bothDisabled);
      }

      const prevWrapper =
        prevBtn.parentElement === this ? prevBtn : prevBtn.parentElement;
      prevWrapper?.classList.remove("opacity-0");
    }

    /**
     * Update edge states based on scroll position - more reliable than intersection observer alone
     * @private
     */
    updateEdgeStates() {
      const { slider, prevBtn, nextBtn } = this.els;
      if (!slider || (!prevBtn && !nextBtn)) return;
      
      // Handle different carousel modes
      if (this.dataset.carouselMode === "fade") {
        this.updateFadeEdgeStates();
        return;
      }
      
      // Calculate scroll boundaries with tolerance for sub-pixel precision
      const tolerance = 2; // 2px tolerance for edge detection
      const scrollLeft = Math.round(slider.scrollLeft);
      const maxScrollLeft = Math.round(slider.scrollWidth - slider.clientWidth);
      
      // Account for slide mode offset (cloned elements)
      const startOffset = this.dataset.carouselMode === "slide" ? slider.clientWidth : 0;
      const adjustedScrollLeft = scrollLeft - startOffset;
      const adjustedMaxScroll = maxScrollLeft - (this.dataset.carouselMode === "slide" ? slider.clientWidth : 0);
      
      // Update button states
      const isAtStart = adjustedScrollLeft <= tolerance;
      const isAtEnd = adjustedScrollLeft >= (adjustedMaxScroll - tolerance);
      
      prevBtn?.toggleAttribute("disabled", isAtStart);
      nextBtn?.toggleAttribute("disabled", isAtEnd);
      
      this.updateNavVisibility();
    }

    /**
     * Update edge states for fade mode carousel
     * @private
     */
    updateFadeEdgeStates() {
      const { prevBtn, nextBtn } = this.els;
      const currentIdx = this.currentIndex;
      const totalSlides = this.items.length;
      
      // In fade mode, disable based on current index
      prevBtn?.toggleAttribute("disabled", currentIdx === 0);
      nextBtn?.toggleAttribute("disabled", currentIdx === totalSlides - 1);
      
      this.updateNavVisibility();
    }

    handleWheel(e) {
      if (this.dataset.carouselMode !== "fade" || 
          Math.abs(e.deltaX) <= Math.abs(e.deltaY) || 
          Math.abs(e.deltaX) < 1 ||
          this.state.isTransitioning || 
          (Date.now() - this.state.lastWheelTime) < 200) return;
      
      e.preventDefault();
      
      this.state.lastWheelTime = Date.now();
      this.state.isTransitioning = true;
      
      const direction = e.deltaX > 0 ? 1 : -1;
      this.navigateSlider(this.currentIndex + direction);
      
      setTimeout(() => { this.state.isTransitioning = false; }, 800);
    }

    get currentIndex() {
      const i = [...this.items].findIndex((item) => item.dataset.active === "true");
      return i < 0 ? 0 : i;
    }

    set currentIndex(index) {
      if (!this.items[index]) return;
      const previousIndex = this.currentIndex;
      this.items.forEach((item) => (item.dataset.active = "false"));
      this.items[index].dataset.active = "true";
      this.updateNavs(index);
      if (previousIndex !== index) {
        this.#emitIndexChange(index, previousIndex);
      }
    }

    /**
     * Notify listeners (e.g. PDP gallery media budget) when the active slide changes.
     * @param {number} index
     * @param {number} previousIndex
     * @private
     */
    #emitIndexChange(index, previousIndex) {
      this.dispatchEvent(
        new CustomEvent("electric-slider:index-change", {
          bubbles: true,
          detail: { index, previousIndex },
        }),
      );
    }

    updateNavs(targetIndex) {
      // Update dots
      if (this.els.dots) {
        const dots = [...this.els.dots.children];
        dots.forEach(dot => dot.dataset.active = 'false');
        if (dots[targetIndex]) dots[targetIndex].dataset.active = 'true';
      }

      // Update thumbnails
      if (this.els.thumbnails) {
        const thumbnails = [...this.els.thumbnails.children];
        thumbnails.forEach(thumbnail => thumbnail.dataset.active = 'false');
        if (thumbnails[targetIndex]) {
          thumbnails[targetIndex].dataset.active = 'true';
          if (!this.#deferThumbnailRailScroll) {
            requestAnimationFrame(() => this.#scrollThumbnailIntoView(targetIndex));
          }
        }
      }

      this.updateSliderBar()
    }

    handleDirectionClick(event) {
      event.preventDefault();
      const { slider } = this.els;
      const { clientWidth } = this.values;
      const direction = event.currentTarget.name === "next" ? 1 : -1;
      const step = this.dataset.sliderStepBy === "page" 
      ? clientWidth || 0
      : this.firstEl?.clientWidth || 0;

      if(step) {
        slider.scrollTo({
          left: slider.scrollLeft + (direction * step),
          behavior: 'smooth'
        });
      }
    }

    /**
     * Handle vertical navigation button clicks
     * Scrolls the slider vertically when up/down buttons are clicked
     * @param {Event} event - Click event from vertical navigation button
     * @private
     */
    handleVerticalDirectionClick(event) {
      event.preventDefault();
      const { slider } = this.els;
      const direction = event.currentTarget.name === "down" ? 1 : -1;
      const clientHeight = slider.clientHeight;
      const step = clientHeight || 0;

      if (step) {
        slider.scrollTo({
          top: slider.scrollTop + (direction * step),
          behavior: 'smooth'
        });
      }
    }

    /**
     * @param {number} index
     * @param {'smooth'|'instant'} [behavior]
     * @param {{ deferThumbnailRailUntilMainScrollEnd?: boolean }} [options] - If true (thumbnail click),
     * thumbnail strip {@link #scrollThumbnailIntoView} runs only after the main feed finishes scrolling.
     */
    navigateSlider(index, behavior = 'smooth', options = {}) {
      const { slider } = this.els;
      const { clientWidth } = this.values;
      const deferThumbRail = options.deferThumbnailRailUntilMainScrollEnd === true;
      
      if (this.dataset.carouselMode === "fade") {
        const totalSlides = this.items.length;
        if (index < 0) index = totalSlides - 1;
        if (index >= totalSlides) index = 0;
      } else {
        const step = this.dataset.sliderStepBy === "page" 
          ? clientWidth || 0
          : this.firstEl?.clientWidth || 0;

        const slideEl = this.items[index];
        const useCenterAlign =
          this.dataset.navigateAlign === "center" &&
          this.dataset.carouselMode !== "slide" &&
          !this.#isThumbnailRail(slideEl);

        let targetScrollLeft = null;
        if (useCenterAlign && slideEl) {
          targetScrollLeft = this.#computeCenteredScrollLeft(slider, slideEl);
        }
        if (targetScrollLeft === null && step) {
          targetScrollLeft =
            (index + (this.dataset.carouselMode === "slide" ? 1 : 0)) * step;
        }

        const resolvedBehavior = this.#resolveScrollBehavior(behavior);

        if (targetScrollLeft !== null && targetScrollLeft !== undefined) {
          if (behavior === 'instant') {
            this.#clearPendingSnapRestore();
            this.#suppressScrollIndexSync = false;
            this.#deferThumbnailRailScroll = false;
            this.#navScrollSyncToken++;
            // Disable smooth scrolling CSS for instant navigation
            // This overrides the scroll-smooth Tailwind class
            slider.style.scrollBehavior = 'auto';
            slider.style.scrollSnapType = 'none';
            
            // Force a reflow to ensure style changes take effect before scrolling
            void slider.offsetHeight;
            
            // Direct assignment for truly instant navigation
            slider.scrollLeft = targetScrollLeft;
            
            // Update UI immediately after instant scroll
            requestAnimationFrame(() => {
              this.updateSliderBar();
              this.updateEdgeStates();
              slider.style.removeProperty("scroll-snap-type");
              slider.style.removeProperty("scroll-behavior");
            });
          } else {
            if (deferThumbRail) {
              this.#deferThumbnailRailScroll = true;
            }
            this.#navScrollSyncToken++;
            const syncToken = this.#navScrollSyncToken;
            if (this.dataset.navigateAlign === "center") {
              this.#suppressScrollIndexSync = true;
            }

            // snap-mandatory + long smooth scroll stops at intermediate snaps — disable snap until scroll finishes
            slider.style.setProperty("scroll-snap-type", "none", "important");
            slider.style.scrollBehavior = "auto";
            void slider.offsetHeight;

            slider.scrollTo({
              left: targetScrollLeft,
              behavior: resolvedBehavior,
            });

            this.#scheduleSnapRestoreAfterProgrammaticScroll(slider, resolvedBehavior, syncToken);
          }
        }
      }

      this.currentIndex = index;
      
      // Update edge states after navigation (only for smooth scrolling)
      if (behavior !== 'instant') {
        setTimeout(() => this.updateEdgeStates(), 50);
      }
    }

    shouldStartDrag(e) {
      const isTouchEvent = e.type.includes('touch');
      const clientX = isTouchEvent ? e.touches[0].clientX : e.clientX;
      const { slider } = this.els;
      
      // Store initial position
      this.state.initialPos = {
        left: slider.scrollLeft,
        x: clientX
      };
      
      return true;
    }

    startDrag(e, preventDefault = true) {
      if (this.state.isDragging) return;
      
      const isTouchEvent = e.type.includes('touch');
      // Return early if it's a touch event but not in carousel mode
      if (isTouchEvent && !this.dataset.carouselMode) return;
      
      const clientX = isTouchEvent ? e.touches[0].clientX : e.clientX;
      const { slider } = this.els;
      
      this.state = {
        ...this.state,
        isDragging: true,
        wasDragged: false,
        pos: {
          left: slider.scrollLeft,
          x: clientX
        }
      };
      
      // Disable smooth scrolling during drag
      slider.style.scrollBehavior = "auto";
      slider.style.scrollSnapType = "none";
      
      this.addGlobalEvents(isTouchEvent);
    }

    dragMove(e) {
      if (!this.state.isDragging) return;
      
      const isTouchEvent = e.type.includes('touch');
      if (isTouchEvent) {
        // Check if we've moved enough to consider this a drag
        const touch = e.touches[0];
        const dx = Math.abs(touch.clientX - this.state.initialTouch.x);
        const dy = Math.abs(touch.clientY - this.state.initialTouch.y);
        const timeDiff = Date.now() - this.state.initialTouch.time;
        
        // If we've moved enough horizontally and not too much vertically, prevent default
        if (dx > 10 && dy < 20 && timeDiff < 300) {
          e.preventDefault();
        }
      }
      
      const clientX = isTouchEvent ? e.touches[0].clientX : e.clientX;
      const { pos, dragThreshold } = this.state;
      const dx = clientX - pos.x;
      
      if (Math.abs(dx) <= dragThreshold) {
        return;
      }
      this.state.wasDragged = true;
      
      if (this.dataset.carouselMode === "fade") {
        this.dragDistance = dx;
        return;
      }
      
      const { slider } = this.els;
      slider.scrollLeft = pos.left - dx;
      this.updateSliderBar();
    }

    dragEnd() {
      if (!this.state.isDragging) return;
      
      this.state.isDragging = false;
      
      // Remove all event listeners
      this.cleanUpGlobalEvents();
      
      const { slider } = this.els;
      
      if (this.dataset.carouselMode === "fade") {
        if (Math.abs(this.dragDistance) > this.state.dragThreshold) {
          const direction = this.dragDistance < 0 ? 1 : -1;
          this.navigateSlider(this.currentIndex + direction);
        }
        this.dragDistance = 0;
        return;
      }

      if (!this.state.wasDragged) return;
      
      const direction = slider.scrollLeft > this.state.pos.left ? "right" : "left";
      const target = this.findSnapPoint({
        currentScrollLeft: slider.scrollLeft,
        dragDirection: direction,
        by: 'page'
      });
      
      slider.scrollTo({ left: target, behavior: 'smooth' });
      slider.addEventListener('wheel', this.resetScrollSnap);
      
      // Update edge states after drag end
      setTimeout(() => this.updateEdgeStates(), 100);
    }

    resetScrollSnap() {
      setTimeout(() => {
        const { slider } = this.els;
        slider.style.scrollSnapType = "x mandatory";
        slider.style.scrollBehavior = "smooth";
        slider.removeEventListener('wheel', this.resetScrollSnap);
      }, 100);
    }

    findSnapPoint({currentScrollLeft, dragDirection, by = 'page' }) {
      const { slider } = this.els;
      let targetScrollLeft = currentScrollLeft;
      let closestDiff = Infinity;
      
      const padding = parseInt(getComputedStyle(slider).scrollPaddingLeft) || 0;
      const gap = parseInt(getComputedStyle(slider).gap) || 0;
      
      if (by === 'item') {
        for (const item of slider.children) {
          const itemLeft = item.offsetLeft - padding;
          const diff = itemLeft - currentScrollLeft;
          const absDiff = Math.abs(diff);
          
          if ((dragDirection === "right" && diff >= 0) || 
              (dragDirection === "left" && diff <= 0) || 
              diff === 0) {
            if (absDiff < closestDiff) {
              closestDiff = absDiff;
              targetScrollLeft = itemLeft;
            }
          }
        }
      } else if (by === 'page') {
        const times = currentScrollLeft / slider.clientWidth;
        targetScrollLeft = (slider.clientWidth - padding - padding + gap) * (dragDirection === "right" ? Math.ceil(times) : Math.floor(times))
      }

      return Math.max(0, Math.min(targetScrollLeft, slider.scrollWidth - slider.clientWidth));
    }

    get values() {
      const { slider } = this.els;
      const scrollPaddingLeft = parseInt(getComputedStyle(slider).scrollPaddingLeft) || 0;
      const scrollPaddingRight = parseInt(getComputedStyle(slider).scrollPaddingRight) || 0;
      const paddingRight = parseInt(getComputedStyle(slider).paddingRight) || 0;
      const paddingLeft = parseInt(getComputedStyle(slider).paddingLeft) || 0;
      const gap = parseInt(getComputedStyle(slider).gap) || 0;
      const totalPadding = scrollPaddingLeft + scrollPaddingRight + paddingRight + paddingLeft;
      const clientWidth = slider.clientWidth - totalPadding + gap;
      const scrollWidth = this.dataset.carouselMode === "slide" 
        ? slider.scrollWidth - (2 * clientWidth) - totalPadding 
        : slider.scrollWidth - totalPadding;
      const scrollable = scrollWidth - clientWidth;

      return {
        scrollPaddingLeft,
        scrollPaddingRight,
        clientWidth,
        scrollWidth,
        scrollable
      };
    }

    throttleUpdate() {
      requestAnimationFrame(() => {
        this.updateSliderBar();
        this.updateVerticalBar();
        this.updateCurrentIndexFromScroll();
      });
    }

    /**
     * Update current index based on scroll position
     * This ensures dots and navigation stay in sync with manual scrolling
     * @private
     */
    updateCurrentIndexFromScroll() {
      const { slider } = this.els;
      if (!slider || !this.items.length) return;

      if (this.#suppressScrollIndexSync) return;

      const { clientWidth } = this.values;
      const scrollLeft = slider.scrollLeft;
      
      // Account for slide mode offset (cloned elements)
      const startOffset = this.dataset.carouselMode === "slide" ? clientWidth : 0;
      const adjustedScrollLeft = scrollLeft - startOffset;

      let newIndex = 0;

      if (this.dataset.navigateAlign === "center") {
        newIndex = this.#getClosestSlideIndexToViewportCenter(slider);
      } else if (this.dataset.sliderStepBy === "page") {
        // Calculate index based on page width
        const step = clientWidth || 0;
        if (step > 0) {
          newIndex = Math.round(adjustedScrollLeft / step);
        }
      } else {
        // Calculate index based on item positions
        const padding = parseInt(getComputedStyle(slider).scrollPaddingLeft) || 0;
        let closestIndex = 0;
        let closestDistance = Infinity;

        this.items.forEach((item, index) => {
          const itemLeft = item.offsetLeft - padding;
          const distance = Math.abs(itemLeft - adjustedScrollLeft);
          
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        newIndex = closestIndex;
      }

      // Clamp index to valid range
      const maxIndex = this.items.length - 1;
      newIndex = Math.max(0, Math.min(newIndex, maxIndex));

      // Only update if index actually changed to avoid unnecessary updates
      if (newIndex !== this.currentIndex) {
        const previousIndex = this.currentIndex;
        // Update items' data-active without triggering setter (to avoid circular updates)
        this.items.forEach((item, index) => {
          item.dataset.active = index === newIndex ? 'true' : 'false';
        });
        
        // Update dots and thumbnails directly
        this.updateNavs(newIndex);
        this.#emitIndexChange(newIndex, previousIndex);
      }
    }

    updateSliderBar() {
      const { slider, sliderBar } = this.els;
      if (!sliderBar || slider.scrollWidth === 0) return;

      const { scrollable, clientWidth } = this.values;
      
      // Calculate progress as 0.1-1 value (minimum 10% width)
      const scrollLeft = slider.scrollLeft - (this.dataset.carouselMode === "slide" ? clientWidth : 0);
      const rawProgress = scrollable > 0 ? Math.max(0, Math.min(1, scrollLeft / scrollable)) : 0;
      const progress = 0.1 + (rawProgress * 0.9); // Scale from 10% to 100%

      // Set progress for scaleX transform (progress bar grows from left)
      sliderBar.style.setProperty('--slider-bar-progress', progress);
    }

    /**
     * Update vertical scrollbar indicator
     * Shows a vertical progress bar when the slider is vertically scrollable
     * Updates button states based on scroll position
     * @private
     */
    updateVerticalBar() {
      const { slider, verticalBarWrapper, upBtn, downBtn } = this.els;
      if (!verticalBarWrapper) return;

      // Get the actual track element for accurate height calculations
      const track = verticalBarWrapper.querySelector('.electric-slider__vertical-bar-track');
      if (!track) return;

      // Wait for track to have dimensions if not yet initialized
      const trackHeight = track.clientHeight;
      if (trackHeight === 0) {
        requestAnimationFrame(() => this.updateVerticalBar());
        return;
      }

      // Check if slider is vertically scrollable
      const scrollHeight = slider.scrollHeight;
      const clientHeight = slider.clientHeight;
      const computedStyle = window.getComputedStyle(slider);
      const overflowY = computedStyle.overflowY;
      
      // Only show vertical bar if content overflows and vertical scrolling is enabled
      const isVerticallyScrollable = scrollHeight > clientHeight && 
        (overflowY === 'scroll' || overflowY === 'auto' || overflowY === 'overlay');
      
      if (!isVerticallyScrollable) {
        verticalBarWrapper.style.opacity = '0';
        verticalBarWrapper.style.pointerEvents = 'none';
        upBtn?.toggleAttribute("disabled", true);
        downBtn?.toggleAttribute("disabled", true);
        return;
      }

      // Show the vertical bar and enable interactions
      verticalBarWrapper.style.opacity = '1';
      verticalBarWrapper.style.pointerEvents = 'auto';
      
      // Ensure buttons are interactive
      if (upBtn) upBtn.style.pointerEvents = 'auto';
      if (downBtn) downBtn.style.pointerEvents = 'auto';

      // Calculate vertical scroll metrics
      const scrollTop = slider.scrollTop;
      const scrollableHeight = scrollHeight - clientHeight;

      // Calculate the height of the vertical bar (proportional to visible area)
      const barHeightPercent = (clientHeight / scrollHeight) * 100;
      
      // Set the height first so we can read the actual rendered height
      verticalBarWrapper.style.setProperty('--vertical-bar-height', `${barHeightPercent}%`);
      
      // Get the fill element to read its actual rendered height
      const fill = track.querySelector('.electric-slider__vertical-bar-fill');
      let thumbHeight = 0;
      
      if (fill) {
        // Force a reflow to ensure the percentage height is applied
        void fill.offsetHeight;
        // Read the actual computed height, accounting for any rounding
        thumbHeight = fill.getBoundingClientRect().height || (barHeightPercent / 100) * trackHeight;
      } else {
        // Fallback to calculated height if fill element not found
        thumbHeight = (barHeightPercent / 100) * trackHeight;
      }
      
      // Calculate the maximum position the thumb can reach without overflowing
      // Subtract thumb height to ensure the bottom edge of thumb stays within track bounds
      const maxPosition = Math.max(0, trackHeight - thumbHeight);
      
      // Calculate scroll progress (0 to 1)
      const scrollProgress = scrollableHeight > 0 ? Math.min(1, Math.max(0, scrollTop / scrollableHeight)) : 0;
      
      // Calculate the thumb position, clamped to prevent overflow
      const barPosition = Math.min(Math.max(0, scrollProgress * maxPosition), maxPosition);

      // Update position CSS variable
      verticalBarWrapper.style.setProperty('--vertical-bar-position', `${barPosition}px`);

      // Update button states based on scroll position
      const tolerance = 2; // 2px tolerance for edge detection
      const isAtTop = scrollTop <= tolerance;
      const isAtBottom = scrollTop >= (scrollableHeight - tolerance);

      upBtn?.toggleAttribute("disabled", isAtTop);
      downBtn?.toggleAttribute("disabled", isAtBottom);
    }
  }

  customElements.define("electric-slider", ElectricSlider);
}
})();
(function () {
// Core functionality interfaces
class SearchFeature {
  constructor(host) {
    this.host = host;
  }

  init() {} // Optional initialization
  destroy() {} // Cleanup if needed
}
class ProductTypeFilter extends SearchFeature {
  constructor(host) {
    super(host);
    this.productTypeSelector = host.querySelector('.js-product-type');
    this.input = host.querySelector('input[type="search"]');
    this.boundFilterChange = this.handleFilterChange.bind(this);
  }

  init() {
    if (this.productTypeSelector) {
      this.productTypeSelector.addEventListener('change', this.boundFilterChange);
    }
  }

  destroy() {
    if (this.productTypeSelector) {
      this.productTypeSelector.removeEventListener('change', this.boundFilterChange);
    }
  }

  handleFilterChange() {
    this.applyFilter();
    // Trigger search with new filter if there's an active search
    if (this.input.value.trim()) {
      // If predictive search is enabled, trigger it
      if (!this.host.dataset?.disabled) {
        this.host.features.search.onChange();
      }
    }
  }

  getFilter() {
    return this.productTypeSelector?.value.replace(' ', '+') || '';
  }

  applyFilter() {
    const cloneNode = this.input.cloneNode(true);
    cloneNode.setAttribute("type", "hidden");
    cloneNode.value = '';

    const productTypeFilter = this.getFilter();

    if (productTypeFilter !== '') {
      cloneNode.value += "product_type:" + productTypeFilter;
      if (this.input.value !== '') {
        cloneNode.value += " AND ";
      }
    }
    cloneNode.value += this.input.value;

    this.input.removeAttribute("name");
    this.input.insertAdjacentElement("afterend", cloneNode);
  }
}

class KeyboardManager extends SearchFeature {
  constructor(host) {
    super(host);
    this.input = host.querySelector('input[type="search"]');
    this.boundShortcutHandler = this.handleShortcut.bind(this);
  }

  init() {
    document.addEventListener("keydown", this.boundShortcutHandler);
  }

  destroy() {
    document.removeEventListener("keydown", this.boundShortcutHandler);
  }

  handleShortcut(event) {
    // Ignore if another handler already handled it
    if (event.defaultPrevented) return;

    // Normalize key
    const key = (event.key || '').toLowerCase();

    // Do not trigger on plain '/' while typing in editable fields
    const activeElement = document.activeElement;
    const isEditable =
      activeElement && (
        activeElement.isContentEditable ||
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.getAttribute?.('role') === 'textbox'
      );

    // Slash to focus only when not inside an editable target
    const isSlashFocus = key === '/' && !isEditable;

    // Cmd/Ctrl+K anywhere (avoid with extra modifiers)
    const isShortcutFocus =
      key === 'k' && (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey;

    if (isSlashFocus || isShortcutFocus) {
      event.preventDefault();
      
      // Find and click the search drawer trigger
      const searchTrigger = document.querySelector('drawer-trigger[data-target="search-drawer"]');
      if (searchTrigger) {
        searchTrigger.click();
      }
    }
  }
}

class PredictiveSearchCore extends SearchFeature {
  constructor(host) {
    super(host);
    this.cachedResults = {};
    this.input = host.querySelector('input[type="search"]');
    this.outputWrapper = host.querySelector(".js-search-output-wrapper");
    this.resultsList = host.querySelector("#predictive-search-results-list");
    this.statusElement = host.querySelector(".predictive-search-status");
    this.displayElement = host.querySelector(".search-input-display");
    this.isOpen = false;
    this.noResults = false;
    this.show_collections = host.dataset.collections === 'true';
    this.isOpenedByClick = false; // Track if search was opened by click vs focus
    this.isSubmitting = false; // Track if form is being submitted
    this.isShowingSuggestions = false; // Track if suggestions are currently being shown to prevent duplicate calls

    this.boundHandlers = {
      onChange: debounce(this.onChange.bind(this), 300),
      onInput: this.onInput.bind(this),
      onFocus: this.onFocus.bind(this),
      onKeyup: this.onKeyup.bind(this),
      onKeydown: this.onKeydown.bind(this),
      onRecentSearchClick: this.onRecentSearchClick.bind(this)
    };
  }

  init() {
    console.log('🔍 [Electric Search] Initializing search component...');
    
    if (this.host.dataset?.disabled) {
      console.log('🔍 [Electric Search] Search component is disabled, skipping initialization');
      return;
    }

    this.setupEventListeners();
    this.saveSuggestions();
    this.setupDrawerListeners();

    // Check if input already has a value (e.g., on search page)
    const currentQuery = this.getQuery();
    if (currentQuery.length > 0) {
      // If we have search terms, mark that we have results
      this.host.setAttribute("results", true);
      // Update search form visibility
      this.updateSearchForm();
    }

    // Inject recent searches into saved suggestions
    const tempWrapper = document.createElement('div');
    tempWrapper.innerHTML = this.suggestions;
    const popularSearchesList = tempWrapper.querySelector('ul.space-y-3.font-medium.mb-6');
    if (popularSearchesList) {
      const recentSearchesHtml = this.generateRecentSearchesHtml();
      if (recentSearchesHtml) {
        // Create wrapper for recent searches
        const recentWrapper = document.createElement('div');
        recentWrapper.setAttribute('data-recent-searches', 'true');
        recentWrapper.className = 'mb-6 px-b-lg';
        recentWrapper.innerHTML = recentSearchesHtml;

        // Insert before popular searches
        popularSearchesList.parentNode.insertBefore(recentWrapper, popularSearchesList);
        this.suggestions = tempWrapper.innerHTML;
      }
    }
  }

  destroy() {
    this.removeEventListeners();
    this.removeDrawerListeners();
  }

  /**
   * Setup drawer event listeners
   * @private
   */
  setupDrawerListeners() {
    this.boundDrawerOpenHandler = this.handleDrawerOpen.bind(this);
    this.boundDrawerCloseHandler = this.handleDrawerClose.bind(this);
    
    // Find the search drawer element
    this.searchDrawer = document.getElementById('search-drawer');
    if (this.searchDrawer) {
      this.searchDrawer.addEventListener('drawer:opened', this.boundDrawerOpenHandler);
      this.searchDrawer.addEventListener('drawer:closed', this.boundDrawerCloseHandler);
    }
  }

  /**
   * Remove drawer event listeners
   * @private
   */
  removeDrawerListeners() {
    if (this.searchDrawer) {
      this.searchDrawer.removeEventListener('drawer:opened', this.boundDrawerOpenHandler);
      this.searchDrawer.removeEventListener('drawer:closed', this.boundDrawerCloseHandler);
    }
  }

  /**
   * Handle drawer open event
   * @private
   */
  handleDrawerOpen() {
    this.isOpen = true;
    
    // Focus the input when drawer opens
    setTimeout(() => {
      this.input?.focus({ preventScroll: true });
      this.positionCursorAtEnd();
    }, 100);

    const searchTerm = this.getQuery();
    if (!searchTerm.length) {
      this.showSuggestions();
    } else if (this.host.getAttribute("results") === "true") {
      // Results already loaded
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  /**
   * Handle drawer close event
   * @private
   */
  handleDrawerClose() {
    this.isOpen = false;
    
    const selected = this.host.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute("aria-selected", false);
    
    this.input.setAttribute("aria-activedescendant", "");
  }

  setupEventListeners() {
    this.input.addEventListener("input", this.boundHandlers.onChange);
    this.input.addEventListener("input", this.boundHandlers.onInput);
    this.input.addEventListener("focus", this.boundHandlers.onFocus);
    this.host.addEventListener("keyup", this.boundHandlers.onKeyup);
    this.host.addEventListener("keydown", this.boundHandlers.onKeydown);
    if (this.outputWrapper) {
      this.outputWrapper.addEventListener("click", this.boundHandlers.onRecentSearchClick);
    }
  }

  removeEventListeners() {
    this.input.removeEventListener("input", this.boundHandlers.onChange);
    this.input.removeEventListener("input", this.boundHandlers.onInput);
    this.input.removeEventListener("focus", this.boundHandlers.onFocus);
    this.host.removeEventListener("keyup", this.boundHandlers.onKeyup);
    this.host.removeEventListener("keydown", this.boundHandlers.onKeydown);
    if (this.outputWrapper) {
      this.outputWrapper.removeEventListener("click", this.boundHandlers.onRecentSearchClick);
    }
  }

  saveSuggestions() {
    this.suggestions = this.outputWrapper.innerHTML;
  }

  stabilizeMobileSearchViewport() {
    // Prevent iOS/mobile from scrolling the locked page when tall results inject.
    if (this.resultsList) {
      this.resultsList.scrollTop = 0;
    }

    if (this.input) {
      this.input.focus({ preventScroll: true });
    }

    if (typeof window.scrollTo === 'function') {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }

  /**
   * Generate recent searches HTML
   * @private
   * @returns {string} HTML string for recent searches section
   */
  generateRecentSearchesHtml() {
    let recentSearches = [];
    try {
      recentSearches = JSON.parse(localStorage.getItem('recent_searches') || '[]');
    } catch (e) {
      // Clear corrupted data
      localStorage.removeItem('recent_searches');
      return '';
    }

    if (!Array.isArray(recentSearches) || recentSearches.length === 0) return '';

    // Ensure all searches are strings and clean up localStorage if needed
    const validSearches = recentSearches.filter(q => typeof q === 'string' && q.trim());

    // If we filtered out invalid entries, update localStorage
    if (validSearches.length !== recentSearches.length) {
      localStorage.setItem('recent_searches', JSON.stringify(validSearches));
    }

    if (validSearches.length === 0) return '';

    return `
      <ul class="space-y-b-sm mb-6">
        ${validSearches.map((query, index) => `
          <li class="slide-right-sm" style="--nth-child:${index + 1}" data-recent-search-item="true" data-query="${this.escapeHtml(query)}">
            <div class="flex justify-between items-center gap-x-3">
              <a
                href="${routes.search_url}?q=${encodeURIComponent(query)}"
                class="hover:underline w-full block"
                data-recent-search-link="true"
              >
                ${this.escapeHtml(query)}
              </a>
              <button
                type="button"
                class="shrink-0 cursor-pointer hover:opacity-70"
                data-recent-search-delete="true"
                aria-label="Remove ${this.escapeHtml(query)} from recent searches"
              >
                ${window.icons.trash}
              </button>
            </div>
          </li>
        `).join('')}
      </ul>
    `;
  }

  /**
   * Handle click events for recent search actions (e.g., delete)
   * Uses event delegation to avoid per-item listeners.
   * @param {MouseEvent} event
   * @private
   */
  onRecentSearchClick(event) {
    const deleteButton = event.target.closest('[data-recent-search-delete="true"]');
    if (!deleteButton) return;

    // Prevent navigation when trash icon is clicked
    event.preventDefault();
    event.stopPropagation();

    const item = deleteButton.closest('[data-recent-search-item="true"]');
    if (!item) return;

    const query = item.getAttribute('data-query') || '';
    if (!query) return;

    // Update localStorage
    try {
      const stored = JSON.parse(localStorage.getItem('recent_searches') || '[]');
      if (Array.isArray(stored)) {
        const updated = stored.filter(search => search !== query);
        localStorage.setItem('recent_searches', JSON.stringify(updated));
      }
    } catch (error) {
      console.warn('Failed to remove recent search:', error);
    }

    // Remove item from DOM
    const list = item.parentElement;
    item.remove();

    // If list is empty, remove the wrapper container
    if (list && list.children.length === 0) {
      const wrapper = list.closest('[data-recent-searches="true"]');
      if (wrapper) {
        wrapper.remove();
      }
    }
  }

  /**
   * Escape HTML to prevent XSS
   * @private
   * @param {string} text
   * @returns {string}
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Inject recent searches above popular searches
   * @private
   */
  injectRecentSearches() {
    // Check if recent searches already injected
    if (this.outputWrapper.querySelector('[data-recent-searches]')) return;

    const popularSearchesList = this.outputWrapper.querySelector('.trending-searches-title');
    if (!popularSearchesList) return;

    const recentSearchesHtml = this.generateRecentSearchesHtml();
    if (recentSearchesHtml) {
      // Create a wrapper div with the recent searches
      const wrapper = document.createElement('div');
      wrapper.setAttribute('data-recent-searches', 'true');
      wrapper.className = 'mb-6 px-b-lg';
      wrapper.innerHTML = recentSearchesHtml;

      // Insert before popular searches
      popularSearchesList.parentNode.insertBefore(wrapper, popularSearchesList);
    }
  }

  getQuery() {
    return this.input.value.trim();
  }


  /**
   * Update search form visibility and content based on input length
   * @private
   */
  updateSearchForm() {
    const searchFormContainer = this.host.querySelector('#search-form-container');
    const searchFormInput = this.host.querySelector('#search-form-input');
    const searchFormLabel = this.host.querySelector('#search-form-label');
    
    if (!searchFormContainer || !searchFormInput || !searchFormLabel) return;

    const value = this.input.value.trim();
    
    // Show form if input has 3+ characters, hide otherwise
    if (value.length >= 3) {
      searchFormContainer.classList.remove('hidden');
      
      // Update the hidden input value
      searchFormInput.value = value;
      
      // Update the button label with the search term
      const searchForText = window.theme?.strings?.templates?.search?.search_for || 'View all products for {{ terms }}';
      searchFormLabel.textContent = searchForText.replace('{{ terms }}', `"${value}"`);
    } else {
      searchFormContainer.classList.add('hidden');
    }
  }

  /**
   * Position cursor at the end of the input text
   * @private
   */
  positionCursorAtEnd() {
    if (!this.input) return;
    
    const value = this.input.value;
    if (value.length > 0) {
      // Use setTimeout to ensure the focus has been applied before setting cursor position
      setTimeout(() => {
        this.input.setSelectionRange(value.length, value.length);
      }, 0);
    }
  }

  /**
   * Handle input events for real-time form sync
   * @private
   */
  onInput() {
    this.updateSearchForm();
  }

  /**
   * Check if current device is mobile based on breakpoint
   * @returns {boolean} True if mobile device
   * @private
   */
  isMobile() {
    const mobileBreakpoints = ["xs", "sm", "md"];
    const currentBreakpoint = getCurrentBreakpoint();
    return mobileBreakpoints.includes(currentBreakpoint);
  }


  onChange() {
    // Don't handle change events during form submission
    if (this.isSubmitting) return;

    const searchTerm = this.getQuery();
    console.log('🔍 [Electric Search] onChange called with searchTerm:', searchTerm);
    this.host.querySelector(".js-error-message").style.display = "";

    if (!searchTerm.length) {
      console.log('🔍 [Electric Search] No search term, showing suggestions');
      this.showSuggestions();
      return;
    }

    console.log('🔍 [Electric Search] Getting search results for:', searchTerm);
    this.getSearchResults(searchTerm);
  }

  onFocus() {
    // Don't handle focus events during form submission
    if (this.isSubmitting) return;

    // Position cursor at end of text when focusing
    this.positionCursorAtEnd();

    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.showSuggestions();
      return;
    }

    if (this.host.getAttribute("results") === "true") {
      // Results already loaded, just make sure they're visible
    } else {
      this.getSearchResults(searchTerm);
    }
  }


  onKeyup(event) {
    // Don't handle keyboard events during form submission
    if (this.isSubmitting) return;

    switch (event.code) {
      case "Escape": {
        event.preventDefault();
        // Close the drawer by clicking the drawer trigger
        if (this.searchDrawer) {
          const drawerTrigger = this.searchDrawer.querySelector('drawer-trigger[data-target="search-drawer"]');
          if (drawerTrigger) drawerTrigger.click();
        }
        break;
      }
      case "ArrowUp": {
        event.preventDefault();
        this.switchOption("up");
        break;
      }
      case "ArrowDown": {
        event.preventDefault();
        this.switchOption("down");
        break;
      }
      case "Enter": {
        // Only prevent default when we actively trigger a selection click.
        // Otherwise allow native form submit via Enter.
        const selectedOption = this.host.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
        if (selectedOption) {
          event.preventDefault();
          selectedOption.click();
        }
        break;
      }
    }
  }

  onKeydown(event) {
    // Don't handle keyboard events during form submission
    if (this.isSubmitting) return;

    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  showSuggestions() {
    if (this.host.dataset?.disabled) return;
    // Don't show suggestions during form submission
    if (this.isSubmitting) return;
    // Prevent duplicate calls in quick succession
    if (this.isShowingSuggestions) {
      return;
    }

    this.isShowingSuggestions = true;

    // Clear results attribute since we're showing suggestions now
    this.host.removeAttribute("results");

    // Check for actual elements that exist in the template: .trending-searches-title and #promoted-list
    const hasPopularCars = this.outputWrapper.querySelector('.trending-searches-title');
    const hasPromotedList = this.outputWrapper.querySelector('#promoted-list');
    const hasRecentSearches = this.outputWrapper.querySelector('[data-recent-searches]');
    
    // Only reset if we don't have the core suggestion elements
    if (!hasPopularCars || !hasPromotedList) {
      this.outputWrapper.innerHTML = this.suggestions;
    }

    // Inject recent searches
    this.injectRecentSearches();

    this.input.setAttribute("aria-expanded", true);
    if (this.resultsList) this.resultsList.classList.remove('[&_>_*]:opacity-50');
    
    // Reset flag after a short delay to allow for async operations
    setTimeout(() => {
      this.isShowingSuggestions = false;
    }, 200);
  }

  switchOption(direction) {
    if (!this.isOpen) return;

    const moveUp = direction === "up";
    const selectedElement = this.host.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.host.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);

    let activeElementIndex = 0;
    if (moveUp && !selectedElement) return;

    let selectedElementIndex = allVisibleElements.findIndex(
      element => element === selectedElement
    );

    if (this.statusElement) this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }


  async getSearchResults(searchTerm) {
    // Don't fetch search results during form submission
    if (this.isSubmitting) return;

    let queryKey = searchTerm.replace(" ", "+").toLowerCase();
    const productTypeFilter = this.host.features.productTypeFilter?.getFilter() || '';

    if (productTypeFilter !== '') {
      queryKey = `product_type:${productTypeFilter} AND ${queryKey}`;
    }

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.noResults = false;
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    try {
      const response = await fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(
          queryKey
        )}&${encodeURIComponent(
          "resources[type]"
        )}=product${this.show_collections ? ',collection' : ''}&section_id=api--search--predictive`
      );

      if (!response.ok) {
        throw new Error(response.status);
      }

      const text = await response.text();
      const resultsMarkup = new DOMParser()
        .parseFromString(text, "text/html")
        .querySelector("#shopify-section-api--search--predictive");

      const noResultsItem = resultsMarkup.querySelector("#no-results");
      const noResultsText = this.host.querySelector(".js-error-message");

      this.noResults = false;
      if (noResultsItem) {
        this.noResults = true;
        noResultsText.innerHTML = noResultsItem.innerHTML;
      } else {
        this.cachedResults[queryKey] = resultsMarkup.innerHTML;
        noResultsText.innerHTML = "";
      }

      this.renderSearchResults(resultsMarkup.innerHTML);
    } catch (error) {
      this.close();
      throw error;
    }
  }

  setLiveRegionLoadingState() {
    // Don't set loading state during form submission
    if (this.isSubmitting) return;

    this.statusElement = this.statusElement || this.host.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.host.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.host.setAttribute("loading", true);
    if (this.resultsList) this.resultsList.classList.add('[&_>_*]:opacity-50');
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    // Don't render search results during form submission
    if (this.isSubmitting) return;
    
    // Reset the showing suggestions flag since we're showing results now
    this.isShowingSuggestions = false;

    console.log('🔍 [Electric Search] Rendering search results...', {
      noResults: this.noResults,
      hasResultsMarkup: !!resultsMarkup,
      resultsLength: resultsMarkup?.length
    });

    if (this.noResults) {
      this.host.querySelector(".js-error-message").style.display = "block";
      // Only clear if we have content to clear
      if (this.outputWrapper.children.length > 0) {
        this.outputWrapper.innerHTML = "";
      }
    } else {
      this.host.querySelector(".js-error-message").style.display = "";
      // Only update if content is different
      if (this.outputWrapper.innerHTML !== resultsMarkup) {
        console.log('🔍 [Electric Search] Updating outputWrapper with search results');
        this.outputWrapper.innerHTML = resultsMarkup;
        this.stabilizeMobileSearchViewport();
      } else {
        console.log('🔍 [Electric Search] Results already match, skipping update');
      }
      this.host.setAttribute("results", true);
      this.setLiveRegionResults();
      console.log('🔍 [Electric Search] Results rendered successfully');
    }
  }

  setLiveRegionResults() {
    this.host.removeAttribute("loading");
    this.setLiveRegionText(
      this.host.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent
    );
    if (this.resultsList) this.resultsList.classList.remove('[&_>_*]:opacity-50');
  }

}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    // Initialize features
    this.features = {
      productTypeFilter: new ProductTypeFilter(this),
      keyboard: new KeyboardManager(this),
      search: new PredictiveSearchCore(this)
    };
  }

connectedCallback() {
    // Initialize all features that should work regardless of disabled state
    this.features.keyboard.init();
    this.features.productTypeFilter.init(); // Initialize product type filter

    // Initialize predictive search only if not disabled
    if (!this.dataset?.disabled) {
      this.features.search.init();
    }

    // Set up form submission handling
    const form = this.querySelector('form[role="search"]');
    form.addEventListener("submit", this.handleFormSubmit.bind(this));
  }

  disconnectedCallback() {
    // Cleanup all features
    Object.values(this.features).forEach(feature => feature.destroy?.());
  }

  handleFormSubmit(event) {
    // Some search apps (e.g. snize/Searchanise) may rewrite form actions to custom pages.
    // Ensure drawer search submits to Shopify's native search endpoint.
    try {
      const form = event?.currentTarget;
      if (form?.dataset?.forceNativeSearch === 'true') {
        const nativeSearchUrl = window?.routes?.search_url || '/search';
        form.setAttribute('action', nativeSearchUrl);
      }
    } catch (error) {
      // Non-blocking: if something goes wrong, allow normal submit flow.
      console.warn('[Electric Search] Failed to enforce native search action', error);
    }

    if (this.dataset?.disabled) {
      event.preventDefault();
      this.removeAttribute("data-disabled");
      setTimeout(() => {
        this.closest('form').submit();
      }, 1000);
      return;
    }

    // If there's a selected option, let it handle the click instead
    const selectedOption = this.querySelector('[aria-selected="true"] a');
    if (selectedOption) {
      event.preventDefault();
      selectedOption.click();
      return;
    }

    // If there's no search query, prevent submission
    if (!this.features.search.getQuery().length) {
      event.preventDefault();
      return;
    }

    // For Enter key navigation to search page, save the search term and navigate smoothly
    const searchQuery = this.features.search.getQuery();
    if (searchQuery.length) {
      // Set submitting flag to prevent focus events from firing
      this.features.search.isSubmitting = true;

      // Apply filter after setting the flag to prevent focus issues
      this.features.productTypeFilter.applyFilter();

      // Save to recent searches before navigating
      this.#saveRecentSearch(searchQuery);
      // Allow form to submit naturally - don't close search UI
      // The navigation will happen and the search UI will remain open until page unloads
    }
  }

  /**
   * Save search query to recent searches
   * @param {string} query - Search query to save
   * @private
   */
  #saveRecentSearch(query) {
    if (!query || typeof query !== 'string') return;
    
    const cleanQuery = query.trim();
    if (!cleanQuery) return;

    try {
      let recentSearches = JSON.parse(localStorage.getItem('recent_searches') || '[]');
      
      // Remove if already exists to avoid duplicates
      recentSearches = recentSearches.filter(search => search !== cleanQuery);
      
      // Add to beginning
      recentSearches.unshift(cleanQuery);
      
      // Keep only last 5 searches
      recentSearches = recentSearches.slice(0, 5);
      
      localStorage.setItem('recent_searches', JSON.stringify(recentSearches));
    } catch (error) {
      console.warn('Failed to save recent search:', error);
    }
  }
}

customElements.define("electric-search", PredictiveSearch);

// Global native search enforcement (covers search forms outside <electric-search>)
// Some third-party search apps may replace the search form node entirely and/or rewrite form actions.
// We enforce Shopify's native search endpoint at submit time for any form explicitly marked with
// `data-force-native-search="true"`.
(() => {
  try {
    // Theme setting: allow merchants to fall back to a search app redirect page.
    // When disabled, we must not force `/search`.
    if (window?.theme_settings?.force_native_search === false) return;

    if (window.__electricForceNativeSearchInstalled) return;
    window.__electricForceNativeSearchInstalled = true;

    const nativeSearchUrl = window?.routes?.search_url || '/search';
    const nativeSearchAbsUrl = (() => {
      try {
        return new URL(nativeSearchUrl, window.location.origin).toString();
      } catch (e) {
        return (window.location.origin || '') + '/search';
      }
    })();

    document.addEventListener('submit', (event) => {
      try {
        const form = event?.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (form?.dataset?.forceNativeSearch !== 'true') return;

        // Always enforce action to native search.
        form.setAttribute('action', nativeSearchAbsUrl);

        // Hard-guard: if something intercepts submit and navigates elsewhere, force navigation ourselves.
        const currentAction = String(form.getAttribute('action') || form.action || '');
        if (currentAction && !currentAction.startsWith(nativeSearchAbsUrl)) {
          event.preventDefault();
          event.stopImmediatePropagation?.();

          const params = new URLSearchParams();
          const data = new FormData(form);
          for (const [key, value] of data.entries()) {
            if (typeof value === 'string') params.append(key, value);
          }
          window.location.assign(`${nativeSearchAbsUrl}?${params.toString()}`);
        }
      } catch (e) {
        // ignore
      }
    }, true);

    // Some apps redirect on Enter keydown without submitting the form.
    // Capture Enter and route to native /search when the form is explicitly marked.
    document.addEventListener('keydown', (event) => {
      try {
        if (!event || event.defaultPrevented) return;
        if (event.key !== 'Enter') return;

        const target = event.target;
        if (!target || !target.closest) return;

        const form = target.form || target.closest('form');
        if (!(form instanceof HTMLFormElement)) return;
        if (form?.dataset?.forceNativeSearch !== 'true') return;

        // If inside predictive <electric-search> and a suggestion is selected, let it handle Enter.
        const electricHost = target.closest('electric-search');
        if (electricHost) {
          const selected = electricHost.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
          if (selected) return;
        }

        event.preventDefault();
        event.stopImmediatePropagation?.();

        form.setAttribute('action', nativeSearchAbsUrl);

        const params = new URLSearchParams();
        const data = new FormData(form);
        for (const [key, value] of data.entries()) {
          if (typeof value === 'string') params.append(key, value);
        }
        window.location.assign(`${nativeSearchAbsUrl}?${params.toString()}`);
      } catch (e) {
        // ignore
      }
    }, true);
  } catch (e) {
    // ignore
  }
})();
})();
// Main script file — lean critical bundle (see deferred.js, productpage.js, collectionpage.js)







// Reviews UGC playlist + More reviews ship as their own entrypoints and load
// only from the reviews sections (s--reviews, reviews-feed), not on every page.

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function triggerEvent(element, name, data = {}) {
  window.events = window.events || {};
  if (!window.events[name]) {
    window.events[name] = debounce((element, name, data) => {
      element.dispatchEvent(
        new CustomEvent(name, {
          bubbles: true,
          detail: data,
        }),
      );
    }, 300);
  }
  window.events[name](element, name, data);
}
function delegate(parent, eventName, childSelector, callback) {
  if (typeof childSelector === "function") {
    callback = childSelector;
    childSelector = null;
  }
  const selector = childSelector
    ? childSelector
    : "." + [...parent.classList].join(".").replaceAll(":", "\\:");
  parent.addEventListener(eventName, (event) => {
    const target = event.target.closest(selector);
    if (target) {
      callback(event, target);
    }
  });
}


function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

let trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function (e) {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*",
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document
    .querySelectorAll("model-viewer")
    .forEach((model) => model.modelViewerUI?.pause());
}

function pauseThisMedia(media) {
  if (media.tagName === "VIDEO") {
    media.pause();
  } else if (media.tagName === "IFRAME") {
    if (media.classList.contains("js-youtube")) {
      media.contentWindow.postMessage(
        JSON.stringify({ event: "command", func: "stopVideo", args: [] }),
        "*",
      );
    } else if (media.classList.contains("js-vimeo")) {
      media.contentWindow.postMessage('{"method":"pause"}', "*");
    }
  } else if (media.tagName === "MODEL-VIEWER") {
    media.modelViewerUI?.pause();
  }
}




// Convert the bg color to rgba format with specific opacity
function setOpacity(color, opacity) {
  // If it's a named color like 'white'
  if (!color.startsWith("oklch") && !color.startsWith("rgb")) {
    const tempDiv = document.createElement("div");
    tempDiv.style.color = color;
    document.body.appendChild(tempDiv);
    color = getComputedStyle(tempDiv).color;
    tempDiv.remove();
  }

  // Convert RGB to OKLCH if needed
  if (color.startsWith("rgb")) {
    const [r, g, b] = color.match(/\d+/g).map(Number);
    const rgbColor = `rgb(${r}, ${g}, ${b})`;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = rgbColor;
    color = ctx.fillStyle;
    canvas.remove();
  }

  // Extract OKLCH values
  const [l, c, h] = color.match(/[\d.]+/g).map(Number);
  return `oklch(${l} ${c} ${h} / ${opacity})`;
}

function getPathWithoutQuery(url) {
  return new URL(url, window.location.origin).pathname;
}


function replaceNodeWithNodeList(nodeToReplace, nodeList) {
  if (!(nodeList instanceof NodeList) && !Array.isArray(nodeList)) {
    throw new Error("Second argument must be a NodeList or an array of nodes");
  }

  const parent = nodeToReplace.parentNode;
  if (!parent) {
    throw new Error("Cannot replace a node that is not in the DOM");
  }

  const fragment = document.createDocumentFragment();

  // If it's an array, we need to iterate differently
  if (Array.isArray(nodeList)) {
    nodeList.forEach((node) => fragment.appendChild(node));
  } else {
    // NodeList is array-like but not an array, so we use this method
    fragment.append(...nodeList);
  }

  parent.replaceChild(fragment, nodeToReplace);
}




class SectionFetcher extends HTMLElement {
  static observedAttributes = [
    "data-url",
    "data-section-id",
    "data-selector",
    "data-animate",
    "data-fire-event",
    "data-onload",
    "data-load-on-event",
  ];

  constructor() {
    super();
    this.handlePopState = this.handlePopState.bind(this);
    this.debouncedCheckAndFetch = this.debounce(
      this.checkAndFetch.bind(this),
      250,
    );
    this.intersectionObserver = null;
  }

  connectedCallback() {
    this.setupInitialState();
    this.addEventListeners();
    this.reWriteData();
    if (this.dataset.onload === "true") {
      this.setupIntersectionObserver();
    }
  }

  disconnectedCallback() {
    this.removeEventListeners();
    this.removeIntersectionObserver();
  }

  setupIntersectionObserver() {
    const options = {
      rootMargin: "0px 0px 800px 0px",
      threshold: 0,
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && this.dataset.onload === "true") {
          queueMicrotask(() => this.checkAndFetch(true));
          this.removeIntersectionObserver();
        }
      });
    }, options);

    this.intersectionObserver.observe(this);
  }

  removeIntersectionObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || name !== "data-url") return;
    this.debouncedCheckAndFetch(false, true);
  }

  setupInitialState() {
    const defaultUrl = new URL(window.location.href);
    defaultUrl.searchParams.delete("section_id");
    this.dataset.url = this.dataset.url || defaultUrl.toString();
    this.lastFetchedUrl = this.dataset.url || window.location.href;
  }

  addEventListeners() {
    if (!this.disablePopEvents) {
      window.addEventListener("popstate", this.handlePopState, {
        passive: true,
      });
    }
    if (this.dataset.loadOnEvent) {
      window.addEventListener(this.dataset.loadOnEvent, this.handlePopState, {
        passive: true,
      });
    }
  }

  removeEventListeners() {
    window.removeEventListener("popstate", this.handlePopState);
    if (this.dataset.loadOnEvent) {
      window.removeEventListener(this.dataset.loadOnEvent, this.handlePopState);
    }
  }

  handlePopState() {
    this.dataset.url = this.buildFetchUrl(window.location.href);
    this.debouncedCheckAndFetch(false);
  }

  async checkAndFetch(isInitialLoad = true, isAttributeChange) {
    const currentUrl = new URL(this.dataset.url, window.location.origin);
    const dataUrl = new URL(this.lastFetchedUrl, window.location.origin);

    const relevantParams = new Set([
      "sort_by",
      "page",
      "q",
      ...Array.from(dataUrl.searchParams.keys()).filter((key) =>
        key.startsWith("filter."),
      ),
      ...Array.from(currentUrl.searchParams.keys()).filter((key) =>
        key.startsWith("filter."),
      ),
    ]);

    let shouldFetch = isInitialLoad;

    for (const param of relevantParams) {
      const currentValues = currentUrl.searchParams.getAll(param);
      const dataValues = dataUrl.searchParams.getAll(param);

      if (param === "page" && currentValues[0] == 1 && dataValues.length === 0)
        return;

      if (JSON.stringify(currentValues) !== JSON.stringify(dataValues)) {
        shouldFetch = true;
        dataUrl.searchParams.delete(param); // Remove all existing values for this param
        currentValues.forEach((value) => {
          dataUrl.searchParams.append(param, value); // Add each value individually
        });
      }
    }

    if (shouldFetch) {
      const newUrl = dataUrl.toString();
      const fetchUrl = this.buildFetchUrl(newUrl);

      if (fetchUrl !== this.lastFetchedUrl) {
        this.lastFetchedUrl = fetchUrl;
        await this.fetchAndReplaceContent(fetchUrl, isAttributeChange);
        return true;
      }
    }
  }

  buildFetchUrl(url) {
    const fetchUrl = new URL(url, window.location.origin);
    fetchUrl.searchParams.set("section_id", this.dataset.sectionId);
    return fetchUrl.toString();
  }

  keepData() {
    if (!this.dataset.toKeep) return;
    window.temporaryDatas = window.temporaryDatas || {};
    window.temporaryDatas[this.id] = {};
    this.dataset.toKeep.split(",").forEach((data) => {
      window.temporaryDatas[this.id][data] = this.dataset[data];
    });
    setTimeout(() => (window.temporaryDatas = {}), 3000);
  }

  reWriteData() {
    if (!window.temporaryDatas || !window.temporaryDatas[this.id]) return;
    Object.entries(window.temporaryDatas[this.id]).forEach(([key, value]) => {
      this.dataset[key] = value;
    });
  }

  async fetchAndReplaceContent(url, isAttributeChange) {
    try {
      this.keepData();
      let newContent;
      if (
        window.temporaryFeeds &&
        window.temporaryFeeds[this.id] &&
        window.temporaryFeeds[this.id][this.dataset.url]
      ) {
        newContent = this.parseHTML(
          window.temporaryFeeds[this.id][this.dataset.url],
        );
        this.dataset.animate = "false";
      }

      if (!newContent) {
        const response = await this.fetchWithRetry(url);
        const html = await response.text();
        newContent = this.parseHTML(html);
        if (newContent === null) {
          this.remove();
          return;
        }
      }

      if (this.dataset.animate !== "false") {
        await this.animateTransition(newContent);
      } else {
        this.replaceWith(newContent);
      }

      this.fireCustomEvent(newContent);
      return true;
    } catch (error) {
      console.error("Error fetching content:", error);
    }
  }

  async fetchWithRetry(url, retries = 3) {
    for (let i = 0; i < retries; i++) {
      try {
        const response = await fetch(url);
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        return response;
      } catch (error) {
        if (i === retries - 1) throw error;
        await new Promise((resolve) =>
          setTimeout(resolve, 1000 * Math.pow(2, i)),
        );
      }
    }
  }

  parseHTML(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    return (
      doc.querySelector(`[data-section-id="${this.dataset.sectionId}"]`) ??
      doc.querySelector(`[data-section="${this.dataset.sectionId}"]`)
    );
  }

  async animateTransition(newContent) {
    await this.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: 100,
    }).finished;

    this.replaceWith(newContent);

    this.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: 100,
    });
  }

  replaceContent(newContent) {
    replaceNodeWithNodeList(this, newContent);
  }

  fireCustomEvent(newContent) {
    if (this.dataset.fireEvent) {
      const event = new CustomEvent(this.dataset.fireEvent, {
        bubbles: true,
        detail: {
          source: this.dataset.id,
          found: newContent.length > 0,
        },
      });
      this.dispatchEvent(event);
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

customElements.define("section-fetcher", SectionFetcher);



if (!customElements.get("cart-dynamic")) {
  customElements.define(
    "cart-dynamic",
    class CartDynamic extends SectionFetcher {
      constructor() {
        super();
        this.disablePopEvents = true;
      }

      connectedCallback() {
        super.connectedCallback();
        document.addEventListener("cart:update", (e) => {
          const source = e.detail?.source;
          const skipRefetch =
            source === "quantity-input" || source === "cart-delete";
          if (skipRefetch) return;
          const fetchUrl = this.buildFetchUrl(window.location.href);
          this.fetchAndReplaceContent(fetchUrl);
        });
      }

      modified(miliseconds = 1000) {
        this.dataset.modified = true;

        setTimeout(() => {
          this.removeAttribute("data-modified");
        }, miliseconds);
      }
    },
  );
}

// Ensure the cart page can switch to the empty-state layout after JS-driven removal.

// cart-delete and quantity-input dispatch a full cart payload in `e.detail.cart`.
if (!window.__voldtCartEmptyReloadBound) {
  window.__voldtCartEmptyReloadBound = true;
  document.addEventListener("cart:update", (e) => {
    try {
      if (window.location.pathname !== "/cart") return;
      const cart = e?.detail?.cart;
      if (!cart) return;
      const itemCount = Number(cart.item_count);
      if (Number.isFinite(itemCount) && itemCount === 0) {
        window.location.reload();
      }
    } catch (err) {
      // no-op: never break cart interactions due to telemetry errors
    }
  });
}

if (!window.__voldtCartCountSyncBound) {
  window.__voldtCartCountSyncBound = true;
  document.addEventListener("cart:update", (e) => {
    try {
      const itemCount = Number(e?.detail?.cart?.item_count);
      if (!Number.isFinite(itemCount)) return;

      document.querySelectorAll("[data-cart-item-count]").forEach((node) => {
        node.textContent = String(itemCount);
      });

      const bubble = document.getElementById("header--cart-bubble");
      if (!bubble) return;

      bubble.classList.toggle("cart-bubble--filled", itemCount > 0);

      let countEl = bubble.querySelector("span");
      if (itemCount > 0) {
        if (!countEl) {
          countEl = document.createElement("span");
          countEl.className = "text-ui-base px-1 empty:hidden";
          bubble.appendChild(countEl);
        }
        countEl.textContent = String(itemCount);
      } else {
        countEl?.remove();
      }
    } catch (err) {
      // no-op: cart count sync should never block interactions
    }
  });
}

if (!customElements.get('cart-delete')) {
  customElements.define(
    'cart-delete',
    class CartDelete extends HTMLElement {
      /** @type {number | null} Line item ID to delete */
      #lineItemId = null;
      
      /** @type {boolean} Component connection state */
      #isConnected = false;
      
      /** @type {AbortController | null} Abort controller for fetch operations */
      #abortController = null;

      constructor() {
        super();
        this.deleteHandler = this.#onClick.bind(this);
      }

      connectedCallback() {
        if (this.#isConnected) return;
        
        try {
          this.#isConnected = true;
          this.#lineItemId = this.dataset.lineItemId;
          
          if (!this.#lineItemId) {
            this.#logError(new Error('Missing line-item-id data attribute'));
            return;
          }

          this.#initializeEventListeners();
        } catch (error) {
          this.#logError(error);
        }
      }

      disconnectedCallback() {
        this.#cleanup();
      }

      /**
       * Initialize event listeners for delete functionality
       * @private
       */
      #initializeEventListeners() {
        this.addEventListener('click', this.deleteHandler, { passive: false });
        this.addEventListener('keydown', this.#onKeydown.bind(this), { passive: false });
        
        // Set proper ARIA attributes for accessibility
        this.setAttribute('role', 'button');
        this.setAttribute('tabindex', '0');
        this.setAttribute('aria-label', 'Remove item from cart');
      }

      /**
       * Handle keyboard navigation for accessibility
       * @param {KeyboardEvent} event - Keyboard event
       * @private
       */
      #onKeydown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.#onClick(event);
        }
      }

      /**
       * Handle click events for cart item deletion
       * @param {Event} event - Click event
       * @private
       */
      async #onClick(event) {
        event.preventDefault();
        event.stopPropagation();
        
        if (this.dataset.state === 'loading') return;

        try {
          this.#setState('loading');
          
          // Get dynamic sections for updates
          const dynamicSectionsNodes = document.querySelectorAll('cart-dynamic');
          const sectionIds = dynamicSectionsNodes.length
            ? [...dynamicSectionsNodes].map((n) => n.dataset.sectionId)
            : [];

          // Prepare cart change request
          const config = this.#getFetchConfig('json');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          
          const body = {
            id: this.#lineItemId,
            quantity: 0,
            sections: sectionIds.join(',')
          };

          config.body = JSON.stringify(body);

          // Abort any existing requests
          if (this.#abortController) {
            this.#abortController.abort();
          }
          
          this.#abortController = new AbortController();
          config.signal = this.#abortController.signal;

          const response = await fetch(`${routes.cart_change_url}`, config);
          
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
          }

          const data = await response.json();

          if (data.status) {
            this.#setState('error');
            this.#dispatchErrorEvent('Failed to remove item from cart', data.description);
            navigator.vibrate?.(100);
            return;
          }

          // When the last item is removed on the cart page, the surrounding page
          // markup (empty state) won't be re-rendered by section updates alone.
          // Match quantity-input behavior and reload to show the empty state.
          if (data.item_count === 0 && window.location.pathname === "/cart") {
            window.location.reload();
            return;
          }

          // Update dynamic sections if provided
          if (data.sections && dynamicSectionsNodes.length) {
            this.#updateDynamicSections(data.sections, dynamicSectionsNodes);
          }

          this.#setState('success');
          this.#dispatchSuccessEvent();
          navigator.vibrate?.(100);

          // Dispatch cart update event for other components
          // Note: Shopify's cart API returns the cart object directly, not nested
          this.dispatchEvent(new CustomEvent('cart:item-removed', {
            detail: { 
              lineItemId: this.#lineItemId,
              cart: data 
            },
            bubbles: true
          }));

          // Dispatch standard cart:update event for consistency
          document.dispatchEvent(new CustomEvent('cart:update', {
            detail: { 
              source: 'cart-delete',
              lineItemId: this.#lineItemId,
              cart: data 
            },
            bubbles: true
          }));

        } catch (error) {
          if (error.name === 'AbortError') return; // Request was cancelled
          
          this.#logError(error);
          this.#setState('error');
          this.#dispatchErrorEvent('Failed to remove item from cart', error.message);
          navigator.vibrate?.(100);
        }
      }

      /**
       * Update dynamic sections with new cart data
       * @param {Object} sections - Section HTML data from response
       * @param {NodeList} dynamicSectionsNodes - Dynamic section nodes to update
       * @private
       */
      #updateDynamicSections(sections, dynamicSectionsNodes) {
        [...dynamicSectionsNodes].forEach((node) => {
          const htmlString = sections[node.dataset.sectionId];
          if (!htmlString) return;
          
          try {
            const html = new DOMParser().parseFromString(htmlString, 'text/html');
            const updated = html.querySelector(
              `cart-dynamic[data-section-id='${node.dataset.sectionId}']`
            );
            
            if (!updated) return;
            
            // Update the node content and classes
            node.innerHTML = updated.innerHTML;
            node.className = updated.className;
            const shouldFlash =
              node.dataset.sectionId !== "api--header--cart-bubble" &&
              typeof node.animate === "function";
            if (shouldFlash) {
              node.animate([{ opacity: 0.35 }, { opacity: 1 }], {
                duration: 180,
                easing: "ease-in-out",
              });
            }
            
            // Call lifecycle hook if present
            if (typeof node.modified === 'function') {
              node.modified();
            }
          } catch (parseError) {
            this.#logError(new Error(`Failed to parse section HTML: ${parseError.message}`));
          }
        });
      }

      /**
       * Set component state for visual feedback
       * @param {string} state - State to set ('loading', 'success', 'error')
       * @private
       */
      #setState(state) {
        this.dataset.state = state;
        const isLoading = state === 'loading';
        this.classList.toggle('pointer-events-none', isLoading);
        const cartSpinner = this.querySelector('[data-cart-spinner]');
        if (cartSpinner) {
          cartSpinner.hidden = !isLoading;
        }
        this.querySelector('.hide-when-spinner')?.classList.toggle(
          'invisible',
          isLoading,
        );
        
        switch (state) {
          case 'loading':
            this.setAttribute('aria-disabled', 'true');
            this.setAttribute('aria-busy', 'true');
            this.setAttribute('aria-label', 'Removing item...');
            break;
          case 'error':
            this.removeAttribute('aria-disabled');
            this.setAttribute('aria-busy', 'false');
            this.setAttribute('aria-label', 'Error removing item');
            // Reset state after delay
            setTimeout(() => this.removeAttribute('data-state'), 3000);
            break;
          case 'success':
            this.removeAttribute('aria-disabled');
            this.setAttribute('aria-busy', 'false');
            this.setAttribute('aria-label', 'Item removed successfully');
            // Reset state after delay
            setTimeout(() => this.removeAttribute('data-state'), 3000);
            break;
        }
      }

      /**
       * Dispatch success event for parent components
       * @private
       */
      #dispatchSuccessEvent() {
        this.dispatchEvent(new CustomEvent('cart-delete:success', {
          detail: { lineItemId: this.#lineItemId },
          bubbles: true
        }));
      }

      /**
       * Dispatch error event for parent components
       * @param {string} message - Error message
       * @param {string} details - Error details
       * @private
       */
      #dispatchErrorEvent(message, details) {
        this.dispatchEvent(new CustomEvent('cart-delete:error', {
          detail: { 
            message, 
            details, 
            lineItemId: this.#lineItemId 
          },
          bubbles: true
        }));
      }

      /**
       * Get fetch configuration for cart operations
       * @param {string} type - Response type ('json', 'javascript')
       * @returns {Object} Fetch configuration object
       * @private
       */
      #getFetchConfig(type = 'json') {
        return {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: `application/${type}`,
          },
        };
      }

      /**
       * Log errors with component context
       * @param {Error} error - Error to log
       * @private
       */
      #logError(error) {
        console.error(`[cart-delete] Line item ${this.#lineItemId}:`, error);
      }

      /**
       * Clean up resources and event listeners
       * @private
       */
      #cleanup() {
        this.#isConnected = false;
        
        if (this.#abortController) {
          this.#abortController.abort();
          this.#abortController = null;
        }
        
        this.removeEventListener('click', this.deleteHandler);
        this.removeEventListener('keydown', this.#onKeydown.bind(this));
      }
    }
  );
}

customElements.define(
  "eye-in-the-sky",
  class EyeInTheSky extends HTMLElement {
    constructor() {
      super();
      this.password = document.getElementById(this.dataset.target);
    }

    connectedCallback() {
      this.addEventListener("click", () => this.handleClick());
    }

    handleClick() {
      this.classList.toggle("active");
      const isPassword = this.password.type === "password";
      this.password.type = isPassword ? "text" : "password";
    }
  },
);

class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "change",
      debounce((event) => {
        fetch(`${routes.cart_update_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ note: event.target.value }),
        });
      }, 300),
    );
  }
}

customElements.define("cart-note", CartNote);
if (!customElements.get("slider-component")) {
  class SliderComponent extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector("ul");
      this.sliderItems = this.querySelectorAll(".slider-items");
      this.sliderThumbnails = this.querySelectorAll("slider-thumbnails li");
      this.pageCount = this.querySelector(".slider-counter--current");
      this.pageTotal = this.querySelector(".slider-counter--total");
      this.prevButton = this.querySelector('button[aria-label="previous"]');
      this.nextButton = this.querySelector('button[aria-label="next"]');

      this.initThumbnails();

      this.lightbox = this.querySelector(".lightbox");
      if (this.lightbox) this.initLightbox();

      if (!this.slider || !this.nextButton) return;

      const resizeObserver = new ResizeObserver((entries) => this.initPages());
      resizeObserver.observe(this.slider);

      this.slider.addEventListener("scroll", this.update.bind(this));
      this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
      this.nextButton.addEventListener("click", this.onButtonClick.bind(this));

      this.initVideoListeners();
    }

    initPages() {
      if (!this.sliderItems.length === 0) return;
      this.slidesPerPage = Math.floor(
        this.slider.clientWidth / this.sliderItems[0].clientWidth,
      );
      this.totalPages = this.sliderItems.length - this.slidesPerPage + 1;
      this.update();
    }

    initThumbnails() {
      this.sliderThumbnails.forEach((thumb, index) => {
        thumb.addEventListener("click", () => {
          this.slideTo(index);
        });
      });
    }

    initVideoListeners() {
      const videoElements = this.querySelectorAll(
        "video, .js-youtube, .js-vimeo",
      );
      videoElements.forEach((video) => {
        // is video intersecting
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              pauseThisMedia(video);
            } else {
              if (video.tagName === "VIDEO" && video.autoplay) video.play();
            }
          });
        });
        observer.observe(video);
      });
    }

    update() {
      this.currentPage =
        Math.round(this.slider.scrollLeft / this.sliderItems[0].clientWidth) +
        1;

      const img = this.sliderItems[this.currentPage]?.querySelector("img");
      if (img && !img.complete) {
        const image = new Image();
        image.src = img.src;
        if (img.srcset) image.srcset = img.srcset;
      }

      if (this.currentPage === 1) {
        this.prevButton.setAttribute("disabled", true);
      } else {
        this.prevButton.removeAttribute("disabled");
      }

      if (this.currentPage === this.totalPages) {
        this.nextButton.setAttribute("disabled", true);
      } else {
        this.nextButton.removeAttribute("disabled");
      }

      if (!this.pageCount || !this.pageTotal) return;
      this.pageCount.textContent = this.currentPage;
      this.pageTotal.textContent = this.totalPages;
    }

    slideTo(index) {
      this.slider.scrollTo({
        left: this.sliderItems[0].clientWidth * index,
      });
    }

    onButtonClick(event) {
      event.preventDefault();
      const slideScrollPosition =
        event.currentTarget.name === "next"
          ? this.slider.scrollLeft + this.sliderItems[0].clientWidth
          : this.slider.scrollLeft - this.sliderItems[0].clientWidth;
      this.slider.scrollTo({
        left: slideScrollPosition,
      });
    }

    initLightbox() {
      this.lightboxSlider = this.lightbox.querySelector("slider-component ul");
      this.sliderItems.forEach((item, index) => {
        const trigger = item.classList.contains("js-lightbox")
          ? item
          : item.querySelector(".js-lightbox");
        trigger?.addEventListener("click", () => {
          this.lightbox.querySelector("fader-component").activate(index);
          this.lightbox.classList.replace("hidden", "fixed");
          // record the scroll position
          this.scrollPosition = window.scrollY;
          document.body.classList.add("fixed");
        });
      });

      this.slider.addEventListener("scroll", (e) => {
        this.lightboxSlider.scrollLeft =
          (this.slider.scrollLeft / this.slider.scrollWidth) *
          this.lightboxSlider.scrollWidth;
      });

      this.lightboxCloser = this.lightbox.querySelector("#close-lightbox");

      this.lightboxCloser.addEventListener("click", () => {
        document.body.classList.remove("fixed");
        pauseAllMedia();
        this.lightbox.classList.replace("fixed", "hidden");
        // instant scroll
        window.scrollTo({
          top: this.scrollPosition,
          behavior: "instant",
        });
      });
    }
  }

  customElements.define("slider-component", SliderComponent);
}

if (!customElements.get("fader-component")) {
  class FaderComponent extends HTMLElement {
    constructor() {
      super();
      this.sliderItems = this.querySelectorAll(".fader-item");
      this.sliderThumbnails = this.querySelectorAll(`fader-thumbnails li`);
      this.initThumbnails();
    }

    initThumbnails() {
      this.sliderThumbnails.forEach((thumb, index) => {
        thumb.addEventListener("click", () => {
          this.activate(index);
        });
      });
    }

    activate(index) {
      this.querySelectorAll(".fader-item img").forEach((img) => {
        if (img.complete) return;
        const image = new Image();
        image.src = img.src;
        if (img.srcset) image.srcset = img.srcset;
      });
      this.sliderItems.forEach((item) => item.classList.add("hidden"));
      this.sliderItems[index].classList.remove("hidden");
    }
  }

  customElements.define("fader-component", FaderComponent);
}

if (!customElements.get("video-player")) {
  class VideoPlayer extends HTMLElement {
    connectedCallback() {
      this.template = this.querySelector("template");
      this.button = this.querySelector("._playButton");
      
      this.loaders();
      this.setIframeAspectRatio();
      this.hydrateVideoMeta();
    }

    disconnectedCallback() {
      this.#stopCountdown();
      this._facadeObserver?.disconnect();
      try {
        this._ytCountdownPlayer?.destroy?.();
      } catch {
        // ignore destroy errors
      }
      this._ytCountdownPlayer = null;
    }

    loaders() {
      if (!this.template) {
        return;
      }

      // Check if video has autoplay attribute - load immediately when no poster button
      const videoInTemplate = this.template.content.querySelector("video");
      const iframeInTemplate = this.template.content.querySelector("iframe");
      const hasAutoplay =
        videoInTemplate?.hasAttribute("autoplay") ||
        (iframeInTemplate &&
          (iframeInTemplate.src.includes("autoplay=1") ||
            iframeInTemplate.src.includes("autoplay=true")));

      if (hasAutoplay && !this.button) {
        this.load();
        return;
      }

      if (this.button) {
        this.button.addEventListener("click", this.load.bind(this));
        // Autoplay + play button (e.g. review UGC overlays): still hydrate when
        // near the viewport so feature / gallery loops are not click-only.
        // YouTube facades are click-only: their autoplay=1 must start on the click.
        if (!hasAutoplay || this.dataset.facade === "true") {
          return;
        }
      }

      const observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.load();
              observer.unobserve(this);
            }
          });
        },
        { threshold: 0, rootMargin: "400px" },
      );
      observer.observe(this);
    }

    load() {
      if (this.button) {
        this.style.height = this.getBoundingClientRect().height + "px";
        this.style.width = this.getBoundingClientRect().width + "px";
        this.stopAllVideos();
      }
      
      const clonedContent = this.template.content.cloneNode(true);
      
      this.appendChild(clonedContent);
      this.button?.classList.add("hidden");
      
      const videoElement = this.querySelector('video');

      // Setup video controls and play video
      if (videoElement) {
        // Remove native controls if present (we're using custom controls)
        videoElement.removeAttribute('controls');
        
        // Add cursor pointer to video so users know it's clickable
        videoElement.style.cursor = 'pointer';
        
        // Setup controls immediately (they'll be hidden until video plays)
        this.setupVideoControls(videoElement);
        
        // If this was triggered by play button click, play the video
        if (this.button) {
          // Play video immediately when play button is clicked
          const playPromise = videoElement.play();
          if (playPromise !== undefined) {
            playPromise.catch(() => {});
          }
        } else if (videoElement.hasAttribute('autoplay')) {
          // Autoplay video - ensure it plays
          
          // Try to play immediately if ready, otherwise wait for canplay event
          if (videoElement.readyState >= 3) { // HAVE_FUTURE_DATA or higher
            this.playVideo(videoElement);
          } else {
            videoElement.addEventListener('canplay', () => {
              this.playVideo(videoElement);
            }, { once: true });
          }
        }
      }

      if (this.dataset.facade === "true") {
        // Keyboard users keep their place: focus moves from the removed button into the player.
        this.querySelector("iframe")?.focus({ preventScroll: true });
      }

      this.hydrateVideoMeta();

      setTimeout(() => {
        this.style.height = "";
        this.style.width = "";
      }, 1000);
    }

    /**
     * Fill runtime/title overlays for YouTube embeds via IFrame API (and oEmbed title fallback).
     * File-video meta is rendered in Liquid; missing/zero values stay hidden.
     */
    hydrateVideoMeta() {
      if (this.dataset.videoMeta !== "true") {
        return;
      }

      // YouTube facades: runtime pill is always shown (prefetched before play) and
      // counts down while playing. Handled separately from the eager-iframe path.
      if (this.dataset.facade === "true") {
        this.#setupFacadeMeta();
        return;
      }

      if (this.dataset.videoMetaHydrated === "true") {
        return;
      }

      const runtimeEl = this.querySelector("[data-video-runtime]");
      const titleWrapEl = this.querySelector("[data-video-title-wrap]");
      const titleEl = this.querySelector("[data-video-title]");
      const needsRuntime = Boolean(runtimeEl?.hasAttribute("hidden") || runtimeEl?.classList.contains("hidden"));
      const needsTitle = Boolean(titleWrapEl?.hasAttribute("hidden") || titleWrapEl?.classList.contains("hidden"));

      if (!needsRuntime && !needsTitle) {
        this.dataset.videoMetaHydrated = "true";
        return;
      }

      const iframe = this.querySelector("iframe");
      if (!iframe?.src?.includes("youtube")) {
        return;
      }

      const targets = { needsRuntime, needsTitle, runtimeEl, titleWrapEl, titleEl };
      const start = () => {
        if (this.dataset.videoMetaHydrated === "true") return;
        this.dataset.videoMetaHydrated = "true";
        this.#hydrateYouTubeMeta(iframe, targets);
      };

      if (!("IntersectionObserver" in window)) {
        start();
        return;
      }

      const observer = new IntersectionObserver(
        (entries, obs) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            obs.disconnect();
            start();
          }
        },
        { rootMargin: "200px", threshold: 0 },
      );
      observer.observe(this);
    }

    /**
     * Facade meta. Before play (card within 300px of the viewport): title from oEmbed and
     * duration from a hidden, off-screen IFrame API probe (no visible YouTube chrome; cached
     * per session). After the click: countdown on the real player.
     */
    #setupFacadeMeta() {
      const videoId = this.dataset.mediaId;
      if (!videoId) return;

      const iframe = this.querySelector("iframe");
      if (iframe?.src?.includes("youtube")) {
        this._facadeObserver?.disconnect();
        this.#attachCountdown(iframe);
        return;
      }

      if (this.dataset.facadeMetaInit === "true") return;
      this.dataset.facadeMetaInit = "true";

      const runtimeEl = this.querySelector("[data-video-runtime]");
      const titleWrapEl = this.querySelector("[data-video-title-wrap]");
      const titleEl = this.querySelector("[data-video-title]");

      const start = () => {
        const needsTitle = Boolean(titleWrapEl?.hasAttribute("hidden") || titleWrapEl?.classList.contains("hidden"));
        if (needsTitle) {
          this.#fetchYouTubeOEmbedTitle(videoId, titleWrapEl, titleEl);
        }
        this.#requestDuration(videoId, runtimeEl, 0);
      };

      if (!("IntersectionObserver" in window)) {
        start();
        return;
      }

      this._facadeObserver = new IntersectionObserver(
        (entries, obs) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            obs.disconnect();
            start();
          }
        },
        { rootMargin: "300px", threshold: 0 },
      );
      this._facadeObserver.observe(this);
    }

    /**
     * @param {string} videoId
     * @param {Element|null} runtimeEl
     * @param {number} attempt
     */
    #requestDuration(videoId, runtimeEl, attempt) {
      VideoPlayer.#prefetchYouTubeDuration(videoId).then((duration) => {
        if (!this.isConnected || this._ytDuration) return;
        if (duration > 0) {
          this._ytDuration = duration;
          this.#revealRuntime(runtimeEl, VideoPlayer.#formatRuntime(duration));
        } else if (attempt < 2) {
          // Cold IFrame API / slow network: retry a couple of times before giving up.
          window.setTimeout(() => this.#requestDuration(videoId, runtimeEl, attempt + 1), 1500 * (attempt + 1));
        }
      });
    }

    /**
     * Remaining-time countdown on the facade's injected player (IFrame API, ~250 ms poll
     * only while PLAYING). Pause/buffer keeps the value; ENDED resets to the full duration.
     * @param {HTMLIFrameElement} iframe
     */
    async #attachCountdown(iframe) {
      if (this._ytCountdownPlayer || this._ytCountdownPending) return;
      this._ytCountdownPending = true;
      const runtimeEl = this.querySelector("[data-video-runtime]");

      try {
        const YT = await VideoPlayer.#loadYouTubeIframeApi();
        if (!YT?.Player || !this.isConnected) return;

        if (!iframe.id || document.querySelectorAll(`#${CSS.escape(iframe.id)}`).length > 1) {
          iframe.id = `yt-cd-${Math.random().toString(36).slice(2, 10)}`;
        }

        const showFull = () => {
          if (this._ytDuration > 0) {
            this.#revealRuntime(runtimeEl, VideoPlayer.#formatRuntime(this._ytDuration));
          }
        };

        this._ytCountdownPlayer = new YT.Player(iframe.id, {
          events: {
            onReady: (event) => {
              const duration = Number(event.target.getDuration?.() || 0);
              if (duration > 0) this._ytDuration = duration;
              if (event.target.getPlayerState?.() === YT.PlayerState.PLAYING) {
                this.#startCountdown(event.target, runtimeEl);
              } else {
                showFull();
              }
            },
            onStateChange: (event) => {
              if (event.data === YT.PlayerState.PLAYING) {
                this.#startCountdown(event.target, runtimeEl);
              } else if (event.data === YT.PlayerState.ENDED) {
                this.#stopCountdown();
                showFull();
              } else {
                // PAUSED / BUFFERING / CUED: keep the last remaining value.
                this.#stopCountdown();
              }
            },
          },
        });
      } catch (error) {
        console.warn("VideoPlayer: YouTube countdown failed", error);
      } finally {
        this._ytCountdownPending = false;
      }
    }

    /**
     * @param {YT.Player} player
     * @param {Element|null} runtimeEl
     */
    #startCountdown(player, runtimeEl) {
      this.#stopCountdown();
      const tick = () => {
        if (!this.isConnected) {
          this.#stopCountdown();
          return;
        }
        try {
          const duration = Number(player.getDuration?.() || 0) || this._ytDuration || 0;
          const current = Number(player.getCurrentTime?.() || 0);
          if (duration > 0) {
            this._ytDuration = duration;
            this.#revealRuntime(runtimeEl, VideoPlayer.#formatRuntime(Math.ceil(Math.max(0, duration - current))));
          }
        } catch {
          // player torn down between ticks
        }
      };
      tick();
      this._ytCountdownTimer = window.setInterval(tick, 250);
    }

    #stopCountdown() {
      if (this._ytCountdownTimer) {
        window.clearInterval(this._ytCountdownTimer);
        this._ytCountdownTimer = null;
      }
    }

    /** @type {Map<string, Promise<number>>} */
    static #durationRequests = new Map();
    /** Probes run one at a time so a rail of cards never spawns many hidden players at once. */
    static #durationQueue = Promise.resolve();

    /**
     * Video length in seconds without a Data API key: a hidden 1×1 youtube-nocookie IFrame API
     * player is cued off-screen, `getDuration()` is read, then it is destroyed. Cached in
     * sessionStorage. Resolves 0 when unavailable (pill stays hidden; nothing is invented).
     * @param {string} videoId
     * @returns {Promise<number>}
     */
    static #prefetchYouTubeDuration(videoId) {
      const key = `voldt:yt-duration:${videoId}`;
      try {
        const cached = Number(window.sessionStorage?.getItem(key) || 0);
        if (cached > 0) return Promise.resolve(cached);
      } catch {
        // storage unavailable
      }
      if (VideoPlayer.#durationRequests.has(videoId)) {
        return VideoPlayer.#durationRequests.get(videoId);
      }

      const request = VideoPlayer.#durationQueue
        .then(() => VideoPlayer.#probeYouTubeDuration(videoId))
        .then((duration) => {
          if (duration > 0) {
            try {
              window.sessionStorage?.setItem(key, String(duration));
            } catch {
              // storage unavailable
            }
          }
          if (!(duration > 0)) {
            // Do not cache failures; a later card or retry may succeed.
            VideoPlayer.#durationRequests.delete(videoId);
          }
          return duration;
        })
        .catch(() => {
          VideoPlayer.#durationRequests.delete(videoId);
          return 0;
        });

      VideoPlayer.#durationQueue = request;
      VideoPlayer.#durationRequests.set(videoId, request);
      return request;
    }

    /**
     * @param {string} videoId
     * @returns {Promise<number>}
     */
    static async #probeYouTubeDuration(videoId) {
      const YT = await VideoPlayer.#loadYouTubeIframeApi();
      if (!YT?.Player) return 0;

      return new Promise((resolve) => {
        const wrap = document.createElement("div");
        wrap.setAttribute("aria-hidden", "true");
        wrap.style.cssText =
          "position:absolute;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;";
        const target = document.createElement("div");
        wrap.appendChild(target);
        document.body.appendChild(wrap);

        let player = null;
        let poll = null;
        let settled = false;
        const finish = (duration) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          if (poll) window.clearInterval(poll);
          try {
            player?.destroy?.();
          } catch {
            // ignore destroy errors
          }
          wrap.remove();
          resolve(duration > 0 ? duration : 0);
        };
        const timeout = window.setTimeout(() => finish(0), 12000);

        try {
          player = new YT.Player(target, {
            host: "https://www.youtube-nocookie.com",
            width: 1,
            height: 1,
            videoId,
            playerVars: { controls: 0, playsinline: 1, enablejsapi: 1, origin: window.location.origin },
            events: {
              onReady: (event) => {
                const read = () => {
                  const duration = Number(event.target.getDuration?.() || 0);
                  if (duration > 0) finish(duration);
                };
                read();
                if (!settled) poll = window.setInterval(read, 250);
              },
              onError: () => finish(0),
            },
          });
        } catch {
          finish(0);
        }
      });
    }

    /**
     * @param {HTMLIFrameElement} iframe
     * @param {{ needsRuntime: boolean, needsTitle: boolean, runtimeEl: Element|null, titleWrapEl: Element|null, titleEl: Element|null }} targets
     */
    async #hydrateYouTubeMeta(iframe, targets) {
      try {
        const videoId = iframe.dataset.videoId || this.dataset.mediaId;
        if (targets.needsTitle && videoId) {
          this.#fetchYouTubeOEmbedTitle(videoId, targets.titleWrapEl, targets.titleEl);
        }

        const YT = await VideoPlayer.#loadYouTubeIframeApi();
        if (!YT?.Player) return;

        if (!iframe.id || document.querySelectorAll(`#${CSS.escape(iframe.id)}`).length > 1) {
          iframe.id = `yt-meta-${Math.random().toString(36).slice(2, 10)}`;
        }

        if (!iframe.src.includes("enablejsapi=1")) {
          const url = new URL(iframe.src);
          url.searchParams.set("enablejsapi", "1");
          iframe.src = url.toString();
        }

        const player = new YT.Player(iframe.id, {
          events: {
            onReady: (event) => {
              try {
                if (targets.needsRuntime) {
                  const duration = Number(event.target.getDuration?.() || 0);
                  if (duration > 0) {
                    this.#revealRuntime(targets.runtimeEl, VideoPlayer.#formatRuntime(duration));
                  }
                }
                if (targets.needsTitle) {
                  const data = event.target.getVideoData?.() || {};
                  const title = typeof data.title === "string" ? data.title.trim() : "";
                  if (title) {
                    this.#revealTitle(targets.titleWrapEl, targets.titleEl, title);
                  }
                }
              } catch (error) {
                console.warn("VideoPlayer: YouTube meta onReady failed", error);
              }
            },
          },
        });

        this._ytMetaPlayer = player;
      } catch (error) {
        console.warn("VideoPlayer: YouTube meta hydration failed", error);
      }
    }

    /**
     * @param {string} videoId
     * @param {Element|null} titleWrapEl
     * @param {Element|null} titleEl
     */
    async #fetchYouTubeOEmbedTitle(videoId, titleWrapEl, titleEl) {
      if (!videoId || !titleEl) return;
      try {
        const response = await fetch(
          `https://www.youtube.com/oembed?url=${encodeURIComponent(`https://www.youtube.com/watch?v=${videoId}`)}&format=json`,
        );
        if (!response.ok) return;
        const data = await response.json();
        const title = typeof data?.title === "string" ? data.title.trim() : "";
        if (title) {
          this.#revealTitle(titleWrapEl, titleEl, title);
        }
      } catch {
        // oEmbed is best-effort; IFrame API may still supply the title
      }
    }

    /**
     * @param {Element|null} runtimeEl
     * @param {string} label
     */
    #revealRuntime(runtimeEl, label) {
      if (!runtimeEl || !label) return;
      const textEl = runtimeEl.querySelector("[data-video-runtime-text]");
      if (textEl) textEl.textContent = label;
      runtimeEl.hidden = false;
      runtimeEl.classList.remove("hidden");
    }

    /**
     * @param {Element|null} titleWrapEl
     * @param {Element|null} titleEl
     * @param {string} title
     */
    #revealTitle(titleWrapEl, titleEl, title) {
      if (!titleEl || !title) return;
      titleEl.textContent = title;
      if (titleWrapEl) {
        titleWrapEl.hidden = false;
        titleWrapEl.classList.remove("hidden");
      }
    }

    /**
     * @param {number} totalSeconds
     * @returns {string}
     */
    static #formatRuntime(totalSeconds) {
      const seconds = Math.max(0, Math.floor(totalSeconds));
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    }

    /**
     * @returns {Promise<typeof window.YT>}
     */
    static #loadYouTubeIframeApi() {
      if (window.YT?.Player) {
        return Promise.resolve(window.YT);
      }

      if (window.__ytIframeApiPromise) {
        return window.__ytIframeApiPromise;
      }

      window.__ytIframeApiPromise = new Promise((resolve, reject) => {
        const previousReady = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          try {
            previousReady?.();
          } catch {
            // ignore prior handler errors
          }
          resolve(window.YT);
        };

        const existing = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
        if (existing) {
          if (window.YT?.Player) resolve(window.YT);
          return;
        }

        const script = document.createElement("script");
        script.src = "https://www.youtube.com/iframe_api";
        script.async = true;
        script.onerror = () => reject(new Error("Failed to load YouTube IFrame API"));
        document.head.appendChild(script);
      });

      return window.__ytIframeApiPromise;
    }

    setupVideoControls(videoElement) {
      const controls = this.querySelector('._videoControls');
      if (!controls) {
        return;
      }

      const pausePlayButton = controls.querySelector('._pausePlayButton');
      const muteButton = controls.querySelector('._muteButton');
      const pauseIcon = controls.querySelector('._pauseIcon');
      const playIcon = controls.querySelector('._playIcon');
      const volumeIcon = controls.querySelector('._volumeIcon');
      const muteIcon = controls.querySelector('._muteIcon');

      if (!pausePlayButton || !muteButton) {
        return;
      }

      // Keep controls hidden initially - they'll show after video starts
      controls.classList.add('opacity-0');
      controls.classList.remove('opacity-100');
      controls.style.pointerEvents = 'auto'; // Always allow clicks even when hidden

      // Track if video has started playing
      let videoHasStarted = false;
      const markVideoStarted = () => {
        videoHasStarted = true;
        // Show controls when video starts
        controls.classList.remove('opacity-0');
        controls.classList.add('opacity-100');
      };
      videoElement.addEventListener('play', markVideoStarted, { once: true });
      // Also check if video is already playing or has played
      if (!videoElement.paused || videoElement.currentTime > 0) {
        videoHasStarted = true;
        controls.classList.remove('opacity-0');
        controls.classList.add('opacity-100');
      }

      // Show controls on hover (only after video has started)
      const showControlsOnHover = () => {
        if (videoHasStarted) {
          controls.classList.remove('opacity-0');
          controls.classList.add('opacity-100');
        }
      };

      const hideControlsOnLeave = () => {
        // Keep controls visible after video starts, only hide when mouse leaves
        if (videoHasStarted) {
          controls.classList.remove('opacity-100');
          controls.classList.add('opacity-0');
        }
      };

      // Show on hover over video player
      this.addEventListener('mouseenter', showControlsOnHover);
      this.addEventListener('mouseleave', hideControlsOnLeave);
      
      // Also show controls when hovering directly on controls
      controls.addEventListener('mouseenter', () => {
        if (videoHasStarted) {
          controls.classList.remove('opacity-0');
          controls.classList.add('opacity-100');
        }
      });
      
      controls.addEventListener('mouseleave', () => {
        // Check if we're still hovering over the video player
        setTimeout(() => {
          if (!this.matches(':hover')) {
            hideControlsOnLeave();
          }
        }, 50);
      });

      // Click on video element to pause/play
      videoElement.addEventListener('click', (e) => {
        // Don't toggle if clicking on controls
        const clickedControls = e.target.closest('._videoControls');
        if (clickedControls) {
          return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        
        if (videoElement.paused) {
          videoElement.play().catch(() => {});
        } else {
          videoElement.pause();
        }
      });

      // Pause/Play toggle button
      pausePlayButton.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        
        if (videoElement.paused) {
          videoElement.play().catch(() => {});
        } else {
          videoElement.pause();
        }
      });

      // Mute/Unmute toggle button
      muteButton.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        
        videoElement.muted = !videoElement.muted;
        
        if (videoElement.muted) {
          volumeIcon.classList.add('hidden');
          muteIcon.classList.remove('hidden');
          muteButton.setAttribute('aria-label', 'Unmute video');
        } else {
          volumeIcon.classList.remove('hidden');
          muteIcon.classList.add('hidden');
          muteButton.setAttribute('aria-label', 'Mute video');
        }
      });

      // Update pause/play button icon when video state changes
      const updatePlayPauseIcon = () => {
        if (videoElement.paused) {
          pauseIcon.classList.add('hidden');
          playIcon.classList.remove('hidden');
          pausePlayButton.setAttribute('aria-label', 'Play video');
        } else {
          pauseIcon.classList.remove('hidden');
          playIcon.classList.add('hidden');
          pausePlayButton.setAttribute('aria-label', 'Pause video');
        }
      };

      videoElement.addEventListener('play', updatePlayPauseIcon);
      videoElement.addEventListener('pause', updatePlayPauseIcon);
      
      // Initialize icon state
      updatePlayPauseIcon();

      // Initialize mute icon state
      if (videoElement.muted) {
        volumeIcon.classList.add('hidden');
        muteIcon.classList.remove('hidden');
        muteButton.setAttribute('aria-label', 'Unmute video');
      } else {
        volumeIcon.classList.remove('hidden');
        muteIcon.classList.add('hidden');
        muteButton.setAttribute('aria-label', 'Mute video');
      }
    }

    playVideo(videoElement) {
      const playPromise = videoElement.play();
      
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Autoplay was prevented, usually due to browser policy
          // This is expected behavior on some browsers/contexts
        });
      }
    }

    stopAllVideos() {
      const videos = document.querySelectorAll("video");
      videos.forEach((video) => {
        if (video !== this.querySelector("video") && !video.autoplay) {
          video.pause();
        }
      });

      const iframes = document.querySelectorAll("iframe");
      iframes.forEach((iframe) => {
        if (iframe !== this.querySelector("iframe")) {
          iframe.contentWindow.postMessage(
            JSON.stringify({ event: "command", func: "pauseVideo" }),
            "*",
          );
        }
      });
    }
    async setIframeAspectRatio() {
      if (!this.template) {
        return;
      }
      
      const iframe = this.template.content.querySelector("iframe");
      if (!iframe) {
        return;
      }
      
      if (iframe.src.includes("youtube")) {
        return;
      }
      
      const { width, height } = await this.getIframeWidthHeight(iframe);
      iframe.style.aspectRatio = `${width}/${height}`;
      
      if (this.querySelector("iframe")) {
        this.querySelector("iframe").style.aspectRatio = `${width}/${height}`;
        this.style.setProperty("--aspect-ratio", `${width} / ${height}`);
      }
    }
    
    async getIframeWidthHeight(iframe) {
      let url = `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${iframe.dataset.videoId}`;
      const { width, height } = await fetch(url).then((resp) => resp.json());
      return { width, height };
    }
  }
  customElements.define("video-player", VideoPlayer);
}
if (!customElements.get("pagination-element")) {
  class PaginationElement extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute("data-section-fetcher-target")) return;
      this.sectionFetcherTarget = document.getElementById(
        this.dataset.sectionFetcherTarget,
      );
      this.querySelectorAll("a").forEach((link) => {
        // create new href from a href with sectionFetcherTarget's data-section-id value as a parameter to query key section_id
        const newHref = new URL(link.href);
        newHref.searchParams.set(
          "section_id",
          this.sectionFetcherTarget.dataset.sectionId,
        );
        link.href = newHref.toString();
      });
      delegate(this, "click", "a", this.onLinkClicked.bind(this));
      window.addEventListener("popstate", (event) => {
        this.handlePopState(event);
      });
    }

    handlePopState(event) {
      const page = event.state && event.state.page ? event.state.page : "1";

      const target = this.querySelector(`a[data-page="${page}"]`);

      if (target) {
        this.onLinkClicked(new Event("click"), target, true);
      }
    }

    onLinkClicked(event, target, isPopState = false) {
      event.preventDefault();

      const page = target.getAttribute("data-page");
      const searchParams = new URLSearchParams(window.location.search);
      target.style.position =
        getComputedStyle(target).position === "absolute" ? "" : "relative";
      target.innerHTML += window.icons.spinner;

      if (page === "1") {
        searchParams.delete("page");
      } else {
        searchParams.set("page", page);
      }

      const newUrl = `${window.location.pathname}${
        searchParams.toString() ? "?" + searchParams.toString() : ""
      }`;

      // if isPopState is false and the section fetcher url is the same with our current url, push a new state
      // this is to prevent pushing a new url state if we are on homepage etc.
      const targetPath = getPathWithoutQuery(
        this.sectionFetcherTarget.dataset.url,
      );
      const currentPath = getPathWithoutQuery(window.location.href);
      if (!isPopState && targetPath === currentPath) {
        window.history.pushState({ page: page }, "", newUrl);
      }

      triggerEvent(this, "pagination:page-changed", {
        searchParams: searchParams.toString(),
        sectionFetcherTarget: this.sectionFetcherTarget.id,
      });

      this.sectionFetcherTarget.dataset.skeletonItems =
        target.dataset.nextItems;
      this.sectionFetcherTarget.dataset.url = `${targetPath}${
        searchParams.toString() ? "?" + searchParams.toString() : ""
      }`;
    }
  }

  window.customElements.define("pagination-element", PaginationElement);
}

class GradientFinish extends HTMLElement {
  connectedCallback() {
    // reduce opacity when scroll of parent reaches end, parent could be vertical or horizontal scroll
    this.slider = document.querySelector(
      `[data-slider="${this.dataset.sliderTarget}"]`,
    );
    // find parent that has a class starting with bg-
    if (!this.slider) {
      this.remove();
      return;
    }

    // initial adjustment
    this.adjustColor();

    // Opacity adjustment is now handled by electric-dom-ready component
    // Set data attribute for the component to handle this slider
    this.slider.setAttribute('data-slider-opacity', 'true');

    // adjust opacity on scroll
    this.slider.addEventListener("scroll", this.adjustOpacity.bind(this));

    // adjust opacity on resize
    window.addEventListener(
      "resize",
      debounce(this.adjustOpacity.bind(this), 100),
    );
    // adjust opacity on resize
    window.addEventListener(
      "resize",
      debounce(this.adjustColor.bind(this), 400),
    );
  }

  adjustColor() {
    let bgParentWithStyle = this.parentElement.closest("[class*='bg-']");
    if (
      bgParentWithStyle &&
      getComputedStyle(bgParentWithStyle).backgroundColor === "rgba(0, 0, 0, 0)"
    ) {
      bgParentWithStyle =
        bgParentWithStyle.parentElement.closest("[class*='bg-']");
    }
    let bg = bgParentWithStyle
      ? getComputedStyle(bgParentWithStyle).backgroundColor
      : "white";
    this.style.setProperty("--from-bg", setOpacity(bg, 0.0));
    this.style.setProperty("--to-bg", setOpacity(bg, 1));
  }

  adjustOpacity() {
    const isVertical = this.slider.scrollHeight > this.slider.clientHeight;
    const isHorizontal = this.slider.scrollWidth > this.slider.clientWidth;

    let ratio = 1;

    if (isVertical) {
      const scrollEnd = this.slider.scrollHeight - this.slider.clientHeight;
      ratio = scrollEnd > 0 ? this.slider.scrollTop / scrollEnd : 1;
    } else if (isHorizontal) {
      const scrollEnd = this.slider.scrollWidth - this.slider.clientWidth;
      ratio = scrollEnd > 0 ? this.slider.scrollLeft / scrollEnd : 1;
    }

    this.style.opacity = ratio == 1 ? 0 : 1;
  }
}
customElements.define("gradient-finish", GradientFinish);

class ProductCard extends HTMLElement {
  connectedCallback() {
    let image = this.querySelector("img.js-lazyloadBlur");
    let placeholder = this.querySelector(".js-lazyloadBlurPlaceholder");
    if (!placeholder) return;
    if (image.complete) {
      placeholder.style.opacity = 0;
      setTimeout(() => {
        placeholder.remove();
      }, 100);
      return;
    }
    image.addEventListener("load", () => {
      placeholder.style.opacity = 0;
      setTimeout(() => {
        placeholder.remove();
      }, 100);
    });
  }
}
customElements.define("product-card", ProductCard);

if (!customElements.get("scroll-top")) {
  customElements.define(
    "scroll-top",
    class extends HTMLElement {
      connectedCallback() {
        window.addEventListener("scroll", this.toggle.bind(this));
      }
      disconnectedCallback() {
        window.removeEventListener("scroll", this.toggle.bind(this));
      }
      toggle() {
        this.dataset.fadeIn = window.scrollY > 200 ? true : false;
      }
    },
  );
}

if (!customElements.get("quantity-input")) {
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[name="quantity"]');
      this.changeEvent = new Event("change", { bubbles: true });
      this.form = this.querySelector("form");
      this.quantityDisplay = this.querySelector("[data-quantity-display]");

      this.querySelectorAll("button").forEach((button) =>
        button.addEventListener("click", this.onButtonClick.bind(this)),
      );
      this.input.addEventListener("change", this.onInputChange.bind(this));
    }

    onButtonClick(event) {
      event.preventDefault();
      if (this.dataset.state === "loading") return;

      this.previousValue = this.input?.value;

      event.currentTarget.name === "plus"
        ? this.input.stepUp()
        : this.input.stepDown();

      if (this.dataset.mode == "qty") return;

      if (this.previousValue !== this.input.value)
        this.input.dispatchEvent(this.changeEvent);

      if (this.previousValue !== this.input.value) {
        this.setLoading(true);
        document.querySelectorAll(".cart-spinner").forEach((spinner) => {
          spinner.classList.remove("hidden");
        });
      }
    }

    setLoading(isLoading) {
      if (isLoading) {
        this.dataset.state = "loading";
        this.setAttribute("aria-busy", "true");
      } else {
        delete this.dataset.state;
        this.setAttribute("aria-busy", "false");
      }

      this.classList.toggle("pointer-events-none", isLoading);

      for (const button of this.querySelectorAll("button")) {
        button.disabled = isLoading;
      }

      this.querySelector(".hide-when-spinner")?.classList.toggle(
        "opacity-0",
        isLoading,
      );
      this.querySelector(".hide-when-spinner")?.classList.toggle(
        "invisible",
        isLoading,
      );

      const cartSpinner = this.querySelector("[data-cart-spinner]");
      if (cartSpinner) {
        cartSpinner.hidden = !isLoading;
        return;
      }

      const spinner = this.querySelector(".spinner");
      const spinnerSvg = spinner?.querySelector("svg");
      if (!spinner) return;

      spinner.classList.toggle("hidden", !isLoading);
      spinnerSvg?.classList.toggle("animate-spin", isLoading);
    }

    onInputChange(event) {
      const formData = new FormData(this.form);

      this.dynamicSectionsNodes = document.querySelectorAll("cart-dynamic");

      let dynamicSections = [];
      if (this.dynamicSectionsNodes) {
        dynamicSections = [...this.dynamicSectionsNodes].map(
          (item) => item.dataset.sectionId,
        );
      }

      formData.append("sections", dynamicSections.join(","));

      let url =
        this.dataset.quantity == 0
          ? routes.cart_add_url
          : routes.cart_change_url;

      fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status === 422) {
            this.input.value = this.previousValue;
            this.handleErrorMessage(response.message);
            return;
          }
          if (
            response.item_count === 0 &&
            window.location.pathname === "/cart"
          ) {
            window.location.reload();
            return;
          }

          this.response = response;

          const lineId = String(this.dataset.id || "");
          const cartItem =
            this.dataset.quantity == 0
              ? response
              : response.items?.find(
                  (item) =>
                    String(item.variant_id) === lineId ||
                    String(item.key) === lineId ||
                    String(item.id) === lineId,
                );

          this.dataset.quantity = cartItem ? cartItem.quantity : 0;
          if (this.quantityDisplay) {
            this.quantityDisplay.innerText = this.dataset.quantity;
          }

          this.updateClones();

          this.dynamicSectionsNodes.forEach((node) => {
            const htmlString = this.response?.sections?.[node.dataset.sectionId];
            if (!htmlString) return;

            const html = new DOMParser().parseFromString(
              htmlString,
              "text/html",
            );
            const updated = html.querySelector(
              `cart-dynamic[data-section-id='${node.dataset.sectionId}']`,
            );
            if (!updated) return;

            node.innerHTML = updated.innerHTML;
            node.className = updated.className;
            const shouldFlash =
              node.dataset.sectionId !== "api--header--cart-bubble" &&
              typeof node.animate === "function";
            if (shouldFlash) {
              node.animate([{ opacity: 0.35 }, { opacity: 1 }], {
                duration: 180,
                easing: "ease-in-out",
              });
            }
            if (typeof node.modified === "function") {
              node.modified();
            }
          });

          const cartPayload =
            typeof response.item_count === "number" ? response : null;

          document.dispatchEvent(
            new CustomEvent("cart:update", {
              bubbles: true,
              detail: {
                source: this.tagName.toLowerCase(),
                found: cartPayload ? cartPayload.item_count > 0 : true,
                cart: cartPayload,
              },
            }),
          );
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          if (this.isConnected) {
            this.setLoading(false);
          }
          document.querySelectorAll(".cart-spinner").forEach((spinner) => {
            spinner.classList.add("hidden");
          });
        });
    }

    updateClones() {
      const clones = document.querySelectorAll(
        `quantity-input[data-id="${this.dataset.id}"]:not([data-mode="qty"])`,
      );

      for (const clone of clones) {
        if (clone == this) continue;
        if (clone.dataset.linkMode == "true") continue;

        clone.dataset.quantity = this.dataset.quantity;
        clone.quantityDisplay.innerText = this.dataset.quantity;
        clone.input.value = this.dataset.quantity;
      }
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrappers =
        this.errorMessageWrappers || this.querySelectorAll("error-message");

      this.errorMessageWrappers.forEach((errorMessageWrapper) => {
        if (errorMessage) {
          const errorMessageOutput = errorMessageWrapper.querySelector("span");
          errorMessageOutput.textContent = errorMessage;
          errorMessageWrapper.classList.remove("hidden");
          setTimeout(() => {
            errorMessageWrapper.classList.add("hidden");
          }, 3000);
        } else {
          errorMessageWrapper.classList.add("hidden");
        }
      });
      throw new Error(errorMessage);
    }
  }
  customElements.define("quantity-input", QuantityInput);
}

// Click-on-load functionality is now handled by electric-dom-ready component
// This ensures it works even when scripts are injected after DOM is ready

/**
 * Mobile Popover System
 * 
 * Handles lightweight mobile popovers that appear below trigger elements
 * and disappear when clicking anywhere or clicking the trigger again.
 * Uses CSS-only animations for smooth performance.
 */
class MobilePopoverManager {
  /** @type {HTMLElement | null} Currently open popover */
  #currentPopover = null;
  /** @type {HTMLElement | null} Currently active trigger */
  #currentTrigger = null;
  /** @type {AbortController | null} Event listener controller */
  #abortController = null;

  constructor() {
    this.#initialize();
  }

  /**
   * Initialize the popover system
   * @private
   */
  #initialize() {
    this.#setupEventListeners();
  }

  /**
   * Setup event listeners for popover triggers and document clicks
   * @private
   */
  #setupEventListeners() {
    this.#abortController = new AbortController();
    
    // Handle popover triggers
    document.addEventListener('click', this.#handleTriggerClick.bind(this), {
      signal: this.#abortController.signal
    });
    
    // Handle document clicks to close popovers
    document.addEventListener('click', this.#handleDocumentClick.bind(this), {
      signal: this.#abortController.signal
    });
    
    // Handle escape key to close popovers
    document.addEventListener('keydown', this.#handleKeydown.bind(this), {
      signal: this.#abortController.signal
    });
  }

  /**
   * Handle clicks on popover trigger elements
   * @param {Event} event - Click event
   * @private
   */
  #handleTriggerClick(event) {
    const trigger = event.target.closest('[data-popover-trigger]');
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();

    const popoverId = trigger.dataset.popoverTrigger;
    const popover = document.getElementById(popoverId);
    
    if (!popover) return;

    // If clicking the same trigger, toggle the popover
    if (this.#currentTrigger === trigger && this.#currentPopover === popover) {
      this.#closePopover();
      return;
    }

    // Close any existing popover
    this.#closePopover();

    // Open the new popover
    this.#openPopover(popover, trigger);
  }

  /**
   * Handle document clicks to close popovers
   * @param {Event} event - Click event
   * @private
   */
  #handleDocumentClick(event) {
    // Don't close if clicking inside the popover or on the trigger
    if (event.target.closest('[data-popover]') || 
        event.target.closest('[data-popover-trigger]')) {
      return;
    }

    this.#closePopover();
  }

  /**
   * Handle keyboard events (escape key)
   * @param {Event} event - Keydown event
   * @private
   */
  #handleKeydown(event) {
    if (event.key === 'Escape') {
      this.#closePopover();
    }
  }

  /**
   * Open a popover
   * @param {HTMLElement} popover - Popover element to open
   * @param {HTMLElement} trigger - Trigger element that opened the popover
   * @private
   */
  #openPopover(popover, trigger) {
    this.#currentPopover = popover;
    this.#currentTrigger = trigger;

    // Show the popover with animation
    popover.style.opacity = '1';
    popover.style.pointerEvents = 'auto';
    
    // Add active state to trigger
    trigger.setAttribute('aria-expanded', 'true');
    
    // Dispatch custom event
    popover.dispatchEvent(new CustomEvent('popover:opened', {
      detail: { trigger, popover },
      bubbles: true
    }));
  }

  /**
   * Close the currently open popover
   * @private
   */
  #closePopover() {
    if (!this.#currentPopover || !this.#currentTrigger) return;

    // Hide the popover with animation
    this.#currentPopover.style.opacity = '0';
    this.#currentPopover.style.pointerEvents = 'none';
    
    // Remove active state from trigger
    this.#currentTrigger.setAttribute('aria-expanded', 'false');
    
    // Dispatch custom event
    this.#currentPopover.dispatchEvent(new CustomEvent('popover:closed', {
      detail: { trigger: this.#currentTrigger, popover: this.#currentPopover },
      bubbles: true
    }));

    // Clear references
    this.#currentPopover = null;
    this.#currentTrigger = null;
  }

  /**
   * Clean up resources
   */
  destroy() {
    this.#closePopover();
    this.#abortController?.abort();
    this.#abortController = null;
  }
}

// Initialize mobile popover system
const mobilePopoverManager = new MobilePopoverManager();


class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
    
    // Listen for search events to close drawer
    this.boundSearchOpenedHandler = this.#handleSearchOpened.bind(this);
    document.addEventListener('search:opened', this.boundSearchOpenedHandler);
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this)),
    );
    this.querySelectorAll("button:not(.dropdown-trigger)").forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this)),
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement =
      event.target.parentElement.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector("summary"),
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement =
      detailsElement.parentElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    const input = summaryElement.querySelector("input");
    if (input) input.checked = !input.checked;

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button"),
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus,
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`,
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              "transitionend",
              addTrapFocus,
            );
      }, 1);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    // this.mainDetailsToggle.removeAttribute("open");
    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(`overflow-hidden`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut(e) {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.parentElement.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement =
      detailsElement.parentElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 1) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.parentElement.closest("details[open]")) {
          trapFocus(
            detailsElement.parentElement.closest("details[open]"),
            detailsElement.querySelector("summary"),
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }

  /**
   * Handle search opened event by closing the drawer
   * @param {CustomEvent} event - The search opened event
   * @private
   */
  #handleSearchOpened(event) {
    // Check if drawer is open and close it
    if (this.mainDetailsToggle && this.mainDetailsToggle.hasAttribute('open')) {
      const summaryElement = this.mainDetailsToggle.querySelector('summary');
      this.closeMenuDrawer(event, summaryElement);
    }
  }

  /**
   * Cleanup event listeners when element is removed
   */
  disconnectedCallback() {
    if (this.boundSearchOpenedHandler) {
      document.removeEventListener('search:opened', this.boundSearchOpenedHandler);
    }
  }
}

customElements.define("menu-drawer", MenuDrawer);
/**
 * HeaderDrawer - Mobile menu drawer component for header navigation
 * 
 * Extends MenuDrawer to provide mobile-specific navigation functionality
 * with proper focus management and responsive behavior.
 * 
 * @extends MenuDrawer
 * @fires menu:opened - When the menu drawer is opened
 * @fires menu:closed - When the menu drawer is closed
 * 
 * @example
 * <header-drawer>
 *   <details id="menu-drawer">
 *     <summary>Menu</summary>
 *     <nav>...</nav>
 *   </details>
 * </header-drawer>
 */
class HeaderDrawer extends MenuDrawer {
  // Private fields
  #header = null;
  #menuDrawer = null;
  #resizeHandler = null;
  /** @type {number|null} Cached root font size (px); invalidated on resize. */
  #cachedRootFontSize = null;

  constructor() {
    super();

    // Bind event handlers
    this.#resizeHandler = this.#handleResize.bind(this);
  }

  connectedCallback() {
    super.connectedCallback?.();

    try {
      this.#setupDOM();
      this.#adjustMenuPosition();
    } catch (error) {
      // Error in connectedCallback
    }
  }

  /**
   * Opens the menu drawer with proper animations and focus management
   * @param {HTMLElement} summaryElement - The summary element that triggered opening
   */
  openMenuDrawer(summaryElement) {
    try {
      document.body.classList.add("overflow-hidden");
      this.#header = this.#header || document.querySelector("header-element");

      if (this.#header) {
        this.#header.classList.add("menu-open");
      }

      setTimeout(() => {
        this.mainDetailsToggle?.classList.add("menu-opening");
      });

      summaryElement.setAttribute("aria-expanded", "true");
      window.addEventListener("resize", this.#resizeHandler);

      // Call parent's focus trap if available
      if (typeof trapFocus === 'function') {
        trapFocus(this.mainDetailsToggle, summaryElement);
      }

      this.dispatchEvent(new CustomEvent('menu:opened', {
        bubbles: true,
        detail: { drawer: this }
      }));
    } catch (error) {
      // Error opening menu
    }
  }

  /**
   * Closes the menu drawer and restores focus
   * @param {Event} event - The event that triggered closing
   * @param {HTMLElement} elementToFocus - Element to focus after closing
   */
  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;

    try {
      super.closeMenuDrawer?.(event, elementToFocus);

      if (this.#header) {
        this.#header.classList.remove("menu-open");
      }

      window.removeEventListener("resize", this.#resizeHandler);

      this.dispatchEvent(new CustomEvent('menu:closed', {
        bubbles: true,
        detail: { drawer: this }
      }));
    } catch (error) {
      // Error closing menu
    }
  }

  // Private methods
  #setupDOM() {
    this.#menuDrawer = this.querySelector("#menu-drawer");
  }

  #adjustMenuPosition() {
    if (!this.#menuDrawer) return;
    const headerElement = document.querySelector("header-element");
    if (!headerElement) return;
    // Batch style reads then single write (avoids interleaved getComputedStyle reflows)
    const headerStyle = window.getComputedStyle(headerElement);
    const paddingBottom = parseInt(headerStyle.paddingBottom, 10) || 0;
    const rootFontSize = this.#cachedRootFontSize ?? parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );
    if (this.#cachedRootFontSize == null) this.#cachedRootFontSize = rootFontSize;
    this.#menuDrawer.style.marginTop = `-${paddingBottom / rootFontSize}rem`;
  }

  #handleResize() {
    if (!this.#header) return;
    if (isViewportZoomed()) return;
    // Batch all reads (layout + style) then writes to avoid forced reflows
    const rect = this.#header.getBoundingClientRect();
    const bottomPosition = rect.bottom - (this.borderOffset || 0);
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    this.#cachedRootFontSize = rootFontSize;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${Math.round(bottomPosition) / rootFontSize}rem`
    );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight / rootFontSize}rem`
    );
  }
}

// Register custom element
if (!customElements.get("header-drawer")) {
  customElements.define("header-drawer", HeaderDrawer);
}

/**
 * HeaderElement - Advanced sticky header component with scroll-based visibility
 * 
 * Provides a high-performance sticky header that hides/shows based on scroll
 * direction and position. Includes comprehensive state management and 
 * accessibility features.
 * 
 * @fires header:state-change - When header state changes
 * @fires header:visibility-change - When header visibility changes
 * 
 * @example
 * <header-element data-sticky="true">
 *   <div class="js-announcement-bar">...</div>
 *   <nav class="header__desktop-navigation">...</nav>
 * </header-element>
 * 
 * @example CSS usage with state attributes
 * header-element[data-visibility="hidden"] { transform: translateY(-100%); }
 * header-element[data-threshold-passed="true"] { backdrop-filter: blur(10px); }
 * header-element[data-scroll-range="far"] { background: solid; }
 */
class HeaderElement extends HTMLElement {
  // Private fields
  #isConnected = false;
  #abortController = null;
  #rafId = null;
  #scrollTimeout = null;
  #resizeTimeout = null;
  #transitionTimeout = null;
  #lastScrollTime = 0;

  // DOM references
  #header = null;
  #announcementBar = null;
  #headerOffset = null;
  
  // Event handlers
  #scrollHandler = null;
  #resizeHandler = null;
  #viewportZoomHandler = null;
  
  // State management
  #state = {
    scrollPosition: 0,
    lastScrollPosition: 0,
    headerHeight: 0,
    announcementHeight: 0,
    visibility: 'visible',
    scrollDirection: 'none',
    thresholdPassed: false,
    isTransitioning: false,
    animationDuration: 300,
    isSticky: false
  };

  /** @type {number|null} Cached root font size (px); invalidated on resize to avoid getComputedStyle reflows. */
  #cachedRootFontSize = null;
  
  // Configuration
  #config = {
    threshold: 100,
    thresholdHysteresis: 20, // Prevents oscillation near threshold
    /** Below this scroll distance from top, `sticky` is removed from `.header-elements`. */
    stickyReleaseScrollPx: 120,
    scrollDebounceDelay: 10,
    resizeDebounceDelay: 150,
    hideOffset: 5,
    showOffset: 5,
    lastStateChangeTime: 0,
    minStateChangeInterval: 100 // Minimum time between state changes
  };
  
  // Constants
  static #TRANSITION_DELAY = 500;

  // Observed attributes
  static observedAttributes = ["data-sticky"];
  
  constructor() {
    super();
    
    // Bind event handlers
    this.#scrollHandler = this.#handleScroll.bind(this);
    this.#resizeHandler = this.#handleResize.bind(this);
    this.#viewportZoomHandler = this.#handleViewportZoomEvent.bind(this);
  }
  
  connectedCallback() {
    if (this.#isConnected) return;
    
    try {
      this.#setupDOM();
      
      if (!this.#state.isSticky || !this.#header) {
        this.#isConnected = true;
        return;
      }
      
      this.#setupEventListeners();
      requestAnimationFrame(() => {
        if (!this.#header) return;
        this.#setupAnimationDurations();
        const layoutSnapshot = this.#readLayoutSnapshot();
        this.#applyLayoutFromSnapshot(layoutSnapshot);
        this.#setupInitialStickyState();
        this.#state.scrollPosition = window.scrollY || document.documentElement.scrollTop;
        this.#state.lastScrollPosition = this.#state.scrollPosition;
        this.#syncHeaderStickyClass(this.#state.scrollPosition);
        this.#updateStateAttributes();
        this.#isConnected = true;
        this.dispatchEvent(new CustomEvent('header:ready', {
          detail: { header: this },
          bubbles: true
        }));
      });
    } catch (error) {
      // Error in connectedCallback
    }
  }
  
  disconnectedCallback() {
    if (!this.#isConnected) return;
    
    try {
      this.#cleanup();
      this.#isConnected = false;
    } catch (error) {
      // Error in disconnectedCallback
    }
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    
    if (name === "data-sticky") {
      this.#state.isSticky = newValue === "true";
      
      if (this.#isConnected) {
        if (this.#state.isSticky) {
          this.#setupEventListeners();
        } else {
          this.#cleanup();
        }
      }
    }
  }
  
  // Public API methods
  /**
   * Get current header state
   * @returns {Object} Current state object
   */
  getState() {
    return { ...this.#state };
  }
  
  /**
   * Force show the header
   */
  show() {
    this.#showHeader();
  }
  
  /**
   * Force hide the header
   */
  hide() {
    this.#hideHeader();
  }
  
  /**
   * Update header sticky offset CSS variable
   * Public method to allow external components to trigger offset recalculation
   */
  updateHeaderStickyOffset() {
    const layoutSnapshot = this.#readLayoutSnapshot();
    this.#applyLayoutFromSnapshot(layoutSnapshot);
  }
  
  // Private methods
  #setupDOM() {
    this.#header = this.closest(".header-elements");
    this.#announcementBar = this.querySelector(".js-announcement-bar");
    this.#state.isSticky = this.dataset.sticky === "true";
  }
  
  #setupAnimationDurations() {
    if (!this.#header) return;
    // Single read; transitionDuration is style-only (no layout). Batched at init.
    const transitionDuration = window.getComputedStyle(this.#header).transitionDuration || '0.3s';
    const durationInMs = transitionDuration.includes('ms')
      ? parseFloat(transitionDuration)
      : parseFloat(transitionDuration) * 1000;
    this.#state.animationDuration = durationInMs || 300;
  }

  /**
   * Root font size in px. Cached to avoid repeated getComputedStyle (forced reflow).
   * Invalidated in #handleResize so one read per resize batch.
   * @returns {number}
   * @private
   */
  #getRootFontSize() {
    if (this.#cachedRootFontSize == null) {
      this.#cachedRootFontSize = parseFloat(
        getComputedStyle(document.documentElement).fontSize
      );
    }
    return this.#cachedRootFontSize;
  }

  /**
   * Single batch of all layout reads (offsetHeight etc.) to avoid forced reflows.
   * Call once, then pass result to #applyLayoutFromSnapshot for all writes.
   * @returns {{ headerOffsetPx: number, stickyOffsetPx: number, headerHeight: number, announcementHeight: number, rootFontSize: number } | null}
   * @private
   */
  #readLayoutSnapshot() {
    if (!this.#header) return null;
    const headerElements = document.querySelectorAll("header-element");
    const headerOffsetPx = headerElements.length
      ? Array.from(headerElements).reduce((acc, el) => acc + el.offsetHeight, 0)
      : 0;
    const mainHeader = this.querySelector(".header__main");
    const promoBanner = this.querySelector("promo-banner");
    let stickyOffsetPx = mainHeader?.offsetHeight || 0;
    if (
      promoBanner &&
      this.getAttribute("data-threshold-passed") !== "true"
    ) {
      stickyOffsetPx += promoBanner.offsetHeight || 0;
    }
    const headerHeight = this.#header.offsetHeight;
    const announcementHeight = this.#announcementBar?.offsetHeight || 0;
    const rootFontSize = this.#getRootFontSize();
    return {
      headerOffsetPx,
      stickyOffsetPx,
      headerHeight,
      announcementHeight,
      rootFontSize
    };
  }

  /**
   * Applies a layout snapshot (writes only). No layout reads here — avoids reflow.
   * @param {{ headerOffsetPx: number, stickyOffsetPx: number, headerHeight: number, announcementHeight: number, rootFontSize: number } | null} snapshot
   * @private
   */
  #applyLayoutFromSnapshot(snapshot) {
    if (!snapshot) return;
    const { headerOffsetPx, stickyOffsetPx, headerHeight, announcementHeight, rootFontSize } =
      snapshot;
    document.body.style.setProperty(
      "--header-offset",
      `${headerOffsetPx / rootFontSize}rem`
    );
    this.#state.headerHeight = headerHeight;
    this.#state.announcementHeight = announcementHeight;
    if (this.#headerOffset) {
      this.#headerOffset.style.height = `${headerHeight / rootFontSize}rem`;
    }
    setTimeout(() => {
      if (!this.isConnected) return;
      document.body.style.setProperty(
        "--header-sticky-offset",
        `${stickyOffsetPx / rootFontSize}rem`
      );
    }, HeaderElement.#TRANSITION_DELAY);
  }

  /**
   * Voldt: overlay heroes inject `.shopify-section-group-header-group { height: 0 }` so the header
   * stacks above the first section (`m--hero-wrapper`, `overlay_header_active`). II-V3-style
   * sticky toggling near the top would drop `position: sticky` and break that overlay.
   * @returns {boolean}
   */
  #isHeaderOverlayLayout() {
    const group = document.querySelector(".shopify-section-group-header-group");
    if (!group) return false;
    const h = parseFloat(window.getComputedStyle(group).height);
    return h === 0;
  }

  /**
   * Toggle Tailwind `sticky` on the header shell: off near the top of the page; on
   * once scrolled past {@link #config.stickyReleaseScrollPx} while scrolling up (or on
   * initial programmatic sync). Never adds `sticky` during downward scroll.
   * @param {number} scrollPosition
   * @param {'up' | 'down' | 'none' | undefined} [scrollDirection] From last frame's delta; `undefined` = init / non-scroll (position-only add when past threshold).
   * @private
   */
  #syncHeaderStickyClass(scrollPosition, scrollDirection) {
    if (!this.#header) return;
    if (this.#isHeaderOverlayLayout()) {
      this.#header.classList.add("sticky");
      return;
    }
    const releasePx = this.#config.stickyReleaseScrollPx;
    if (scrollPosition < releasePx) {
      this.#header.classList.remove("sticky");
      return;
    }
    if (scrollDirection === 'down') {
      this.#header.classList.remove("sticky");
      return;
    }
    if (scrollDirection === 'up') {
      this.#header.classList.add("sticky");
      return;
    }
    if (scrollDirection === 'none') {
      return;
    }
    this.#header.classList.add("sticky");
  }

  #setupInitialStickyState() {
    if (!this.#header) return;
    this.#headerOffset = document.createElement("div");
    const rootFontSize = this.#getRootFontSize();
    this.#headerOffset.style.height = `${this.#state.headerHeight / rootFontSize}rem`;
    this.#headerOffset.className = 
      "absolute inset-0 bg-background1 shopify-section-group-header-group";
    this.#headerOffset.setAttribute("aria-hidden", "true");
    
    this.#header.classList.add(
      "transition-transform",
      "duration-300",
      "top-0",
      "z-20",
      "bg-transparent"
    );
    if (this.#isHeaderOverlayLayout()) {
      this.#header.classList.add("sticky");
    }
    // Non-overlay: `sticky` is applied from #syncHeaderStickyClass once scroll passes
    // {@link #config.stickyReleaseScrollPx}, not on initial paint.
  }

  /**
   * Stabilize sticky-header transforms while pinch-zoomed.
   * Global `data-viewport-zoomed` is owned by helpers.js; this only adjusts
   * header visibility so sticky+transform does not compound GPU pressure.
   * @param {CustomEvent} [event]
   * @private
   */
  #handleViewportZoomEvent(event) {
    const isZoomed = event?.detail?.zoomed ?? isViewportZoomed();
    if (!isZoomed) return;

    this.#state.visibility = "visible";
    this.#state.isTransitioning = false;
    if (this.#header) {
      this.#header.classList.remove("-translate-y-(--header-offset)");
    }
  }

  #setupEventListeners() {
    // Create abort controller for cleanup
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Zoom attribute + media pause live in helpers.js; header only stabilizes itself.
    this.#handleViewportZoomEvent();
    window.addEventListener("voldt:viewport-zoom", this.#viewportZoomHandler, {
      passive: true,
      signal,
    });

    // Throttled scroll handler
    const throttledScroll = this.#throttle(
      this.#scrollHandler,
      this.#config.scrollDebounceDelay
    );
    
    // Debounced resize handler
    const debouncedResize = this.#debounce(
      this.#resizeHandler,
      this.#config.resizeDebounceDelay
    );
    
    // Add listeners with signal
    window.addEventListener("scroll", throttledScroll, { 
      passive: true, 
      signal 
    });
    
    window.addEventListener("resize", debouncedResize, { 
      signal 
    });
    
    document.addEventListener("promo-banner:closed", this.#handlePromoBannerClosed.bind(this), { 
      signal 
    });
    
    document.addEventListener("promo-banner:visible", this.#handlePromoBannerVisible.bind(this), { 
      signal 
    });
  }
  
  #cleanup() {
    // Abort all event listeners
    this.#abortController?.abort();
    this.#abortController = null;
    
    // Cancel animation frames
    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
    
    // Clear timeouts
    clearTimeout(this.#scrollTimeout);
    clearTimeout(this.#resizeTimeout);
    clearTimeout(this.#transitionTimeout);

    // Reset state
    this.#state.visibility = 'visible';
    this.#state.isTransitioning = false;
  }

  // Event handlers
  #handleScroll() {
    if (isViewportZoomed()) return;
    const now = Date.now();
    if (now - this.#lastScrollTime < this.#config.scrollDebounceDelay) {
      return;
    }
    this.#lastScrollTime = now;
    
    // Cancel previous frame
    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
    }
    
    // Schedule update in next frame
    this.#rafId = requestAnimationFrame(() => {
      this.#updateScrollState();
    });
  }
  
  #handleResize() {
    if (isViewportZoomed()) return;
    try {
      this.#cachedRootFontSize = null;
      const layoutSnapshot = this.#readLayoutSnapshot();
      this.#applyLayoutFromSnapshot(layoutSnapshot);
    } catch (error) {
      // Error handling resize
    }
  }
  
  #handlePromoBannerClosed() {
    try {
      const layoutSnapshot = this.#readLayoutSnapshot();
      this.#applyLayoutFromSnapshot(layoutSnapshot);
    } catch (error) {
      // Error handling promo banner close
    }
  }

  #handlePromoBannerVisible() {
    try {
      const layoutSnapshot = this.#readLayoutSnapshot();
      this.#applyLayoutFromSnapshot(layoutSnapshot);
    } catch (error) {
      // Error handling promo banner visible
    }
  }
  
  #updateScrollState() {
    if (isViewportZoomed()) return;
    const scrollPosition = window.scrollY || document.documentElement.scrollTop;
    const scrollDelta = scrollPosition - this.#state.lastScrollPosition;
    const scrollDirection =
      Math.abs(scrollDelta) < 1
        ? 'none'
        : scrollDelta > 0
          ? 'down'
          : 'up';

    this.#state.scrollPosition = scrollPosition;
    this.#syncHeaderStickyClass(scrollPosition, scrollDirection);
    this.#state.scrollDirection = scrollDirection;
    
    // Check threshold with hysteresis to prevent oscillation
    const wasThresholdPassed = this.#state.thresholdPassed;
    if (!wasThresholdPassed && scrollPosition > this.#config.threshold + this.#config.thresholdHysteresis) {
      this.#state.thresholdPassed = true;
    } else if (wasThresholdPassed && scrollPosition < this.#config.threshold - this.#config.thresholdHysteresis) {
      this.#state.thresholdPassed = false;
    }
    
    // Always update state attributes to ensure scroll range is current
    this.#updateStateAttributes();
    
    // Handle header visibility only for significant movements
    if (Math.abs(scrollDelta) >= 1) {
      this.#handleHeaderVisibility(scrollPosition, scrollDelta);
    }
    
    this.#state.lastScrollPosition = scrollPosition;
  }
  
  #handleHeaderVisibility(scrollPosition, scrollDelta) {
    // Skip visibility changes when body scroll is locked (modal/drawer open)
    if (this.#isBodyLocked()) {
      return;
    }
    
    // Prevent rapid state changes
    const now = Date.now();
    if (now - this.#config.lastStateChangeTime < this.#config.minStateChangeInterval) {
      return;
    }
    
    // If body is locked (drawer/modal open), maintain current header state
    // This prevents header flicker when body has overflow-hidden
    if (document.body.classList.contains('overflow-hidden')) {
      return;
    }
    
    // Always show header when at the top of the page
    if (scrollPosition <= this.#state.announcementHeight) {
      if (this.#state.visibility !== 'visible' && this.#state.visibility !== 'showing') {
        this.#showHeader();
        this.#config.lastStateChangeTime = now;
      }
      return;
    }
    
    const isHidden = this.#state.visibility === 'hidden' || this.#state.visibility === 'hiding';
    const isVisible = this.#state.visibility === 'visible' || this.#state.visibility === 'showing';
    
    const shouldHide = 
      this.#state.thresholdPassed && 
      scrollDelta > this.#config.hideOffset && 
      !isHidden;
    
    const shouldShow = 
      scrollDelta < -this.#config.showOffset &&
      !isVisible;
    
    if (shouldHide) {
      this.#hideHeader();
      this.#config.lastStateChangeTime = now;
    } else if (shouldShow) {
      this.#showHeader();
      this.#config.lastStateChangeTime = now;
    }
  }
  
  /**
   * Check if body scroll is locked (modal/drawer is open)
   * @returns {boolean} True if body scroll is locked
   */
  #isBodyLocked() {
    return (
      document.body.classList.contains('overflow-hidden') ||
      document.body.style.overflow === 'hidden' ||
      document.body.style.position === 'fixed'
    );
  }
  
  #hideHeader() {
    if (this.#state.visibility === 'hidden') return;
    
    // Clear any pending show transition
    clearTimeout(this.#transitionTimeout);
    
    this.#state.isTransitioning = true;
    this.#state.visibility = 'hiding';
    this.#updateStateAttributes();
    
    if (this.#header) {
      this.#header.classList.add("-translate-y-(--header-offset)");
    }
    
    this.#transitionTimeout = setTimeout(() => {
      // Only finalize hide if we're still in hiding state
      if (this.#state.visibility === 'hiding') {
        this.#state.visibility = 'hidden';
        this.#state.isTransitioning = false;
        this.#updateStateAttributes();
        // Delay sticky offset update to prevent layout shift during scroll
        setTimeout(() => {
          if (this.#state.visibility === 'hidden') {
            const layoutSnapshot = this.#readLayoutSnapshot();
            this.#applyLayoutFromSnapshot(layoutSnapshot);
          }
        }, 100);
        
        this.dispatchEvent(new CustomEvent('header:visibility-change', {
          detail: { visibility: 'hidden' },
          bubbles: true
        }));
      }
    }, this.#state.animationDuration);
  }
  
  #showHeader() {
    if (this.#state.visibility === 'visible') return;
    
    // Clear any pending hide transition
    clearTimeout(this.#transitionTimeout);
    
    this.#state.isTransitioning = true;
    this.#state.visibility = 'showing';
    this.#updateStateAttributes();
    
    if (this.#header) {
      this.#header.classList.remove("-translate-y-(--header-offset)");
    }
    
    this.#transitionTimeout = setTimeout(() => {
      // Only finalize show if we're still in showing state
      if (this.#state.visibility === 'showing') {
        this.#state.visibility = 'visible';
        this.#state.isTransitioning = false;
        this.#updateStateAttributes();
        // Delay sticky offset update to prevent layout shift during scroll
        setTimeout(() => {
          if (this.#state.visibility === 'visible') {
            const layoutSnapshot = this.#readLayoutSnapshot();
            this.#applyLayoutFromSnapshot(layoutSnapshot);
          }
        }, 100);
        
        this.dispatchEvent(new CustomEvent('header:visibility-change', {
          detail: { visibility: 'visible' },
          bubbles: true
        }));
      }
    }, this.#state.animationDuration);
  }
  
  #updateStateAttributes() {
    // Set visibility states
    this.setAttribute('data-visibility', this.#state.visibility);
    this.setAttribute('data-scroll-direction', this.#state.scrollDirection);
    this.setAttribute('data-threshold-passed', String(this.#state.thresholdPassed));
    this.setAttribute('data-scroll-position', String(Math.round(this.#state.scrollPosition)));
    this.setAttribute('data-scrolled', String(this.#state.scrollPosition > 0));
    
    // Update HTML tag with header visibility state
    const isVisible = this.#state.visibility === 'visible' || this.#state.visibility === 'showing';
    const htmlElement = document.documentElement;
    
    if (isVisible) {
      htmlElement.setAttribute('data-header-visible', 'true');
    } else {
      htmlElement.removeAttribute('data-header-visible');
    }
    
    // Set scroll range
    const scrollPosition = this.#state.scrollPosition;
    let scrollRangeName = 'top';
    
    if (scrollPosition < 50) {
      scrollRangeName = 'top';
    } else if (scrollPosition < 150) {
      scrollRangeName = 'near-top';
    } else if (scrollPosition < 1000) {
      scrollRangeName = 'mid';
    } else {
      scrollRangeName = 'far';
    }
    
    this.setAttribute('data-scroll-range', scrollRangeName);
    htmlElement.setAttribute('data-scroll-range', scrollRangeName);
    
    // Dispatch state change event
    this.dispatchEvent(new CustomEvent('header:state-change', {
      detail: {
        visibility: this.#state.visibility,
        scrollDirection: this.#state.scrollDirection,
        thresholdPassed: this.#state.thresholdPassed,
        scrollPosition: this.#state.scrollPosition,
        isTransitioning: this.#state.isTransitioning
      },
      bubbles: true
    }));
  }
  
  // Utility methods
  #throttle(func, delay) {
    let lastCall = 0;
    let timeout = null;
    
    return (...args) => {
      const now = Date.now();
      const timeSinceLastCall = now - lastCall;
      
      if (timeSinceLastCall >= delay) {
        lastCall = now;
        func.apply(this, args);
      } else {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          lastCall = Date.now();
          func.apply(this, args);
        }, delay - timeSinceLastCall);
      }
    };
  }
  
  #debounce(func, delay) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), delay);
    };
  }
  
  // Static utility methods
  /**
   * Find header element by selector
   * @param {string} selector - CSS selector
   * @returns {HeaderElement|null}
   */
  static find(selector) {
    return document.querySelector(selector);
  }
  
  /**
   * Get all header elements
   * @returns {NodeList}
   */
  static findAll() {
    return document.querySelectorAll('header-element');
  }
}

// Register custom element
if (!customElements.get("header-element")) {
  customElements.define("header-element", HeaderElement);
}

/**
 * LocalizationForm - Handles language and country selection
 * 
 * Manages form submission for changing locale settings with
 * accessible dropdown interactions.
 * 
 * @fires localization:change - When locale is changed
 * 
 * @example
 * <localization-form>
 *   <form>
 *     <input name="language_code" type="hidden">
 *     <a data-value="en">English</a>
 *     <a data-value="fr">Français</a>
 *   </form>
 * </localization-form>
 */
class LocalizationForm extends HTMLElement {
  // Private fields
  #isConnected = false;
  #form = null;
  #input = null;
  #links = [];
  #abortController = null;
  
  constructor() {
    super();
  }
  
  connectedCallback() {
    if (this.#isConnected) return;
    
    try {
      this.#setupDOM();
      this.#setupEventListeners();
      this.#isConnected = true;
    } catch (error) {
      console.error('LocalizationForm: Error in connectedCallback', error);
    }
  }
  
  disconnectedCallback() {
    if (!this.#isConnected) return;
    
    try {
      this.#cleanup();
      this.#isConnected = false;
    } catch (error) {
      console.error('LocalizationForm: Error in disconnectedCallback', error);
    }
  }
  
  // Private methods
  #setupDOM() {
    this.#form = this.querySelector("form");
    this.#input = this.querySelector(
      'input[name="language_code"], input[name="locale_code"], input[name="country_code"]'
    );
    this.#links = Array.from(this.querySelectorAll("a[data-value]"));
  }
  
  #setupEventListeners() {
    if (!this.#links.length) return;
    
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    
    this.#links.forEach(link => {
      link.addEventListener("click", this.#handleItemClick.bind(this), { signal });
    });
  }
  
  #cleanup() {
    this.#abortController?.abort();
    this.#abortController = null;
  }
  
  #handleItemClick(event) {
    event.preventDefault();
    
    if (!this.#input || !this.#form) return;
    
    try {
      const value = event.currentTarget.dataset.value;
      this.#input.value = value;
      
      this.dispatchEvent(new CustomEvent('localization:change', {
        detail: { value, form: this.#form },
        bubbles: true,
        cancelable: true
      }));
      
      this.#form.submit();
    } catch (error) {
      console.error('LocalizationForm: Error handling click', error);
    }
  }
}

// Register custom element
if (!customElements.get("localization-form")) {
  customElements.define("localization-form", LocalizationForm);
}

/**
 * CountryStateProvinceSelector - Dynamic location-based dealer selector
 * 
 * Provides a multi-level selector for country, state/province with
 * dynamic dealer results display based on selection.
 * 
 * @fires dealer:selected - When a dealer location is selected
 * @fires dealer:cleared - When dealer results are cleared
 * 
 * @example
 * <country-state-province-selector data-dealers='[...]' data-inline="false">
 *   <select id="country">...</select>
 *   <select id="states">...</select>
 *   <select id="province">...</select>
 *   <div id="dealer-results"></div>
 * </country-state-province-selector>
 */
class CountryStateProvinceSelector extends HTMLElement {
  // Private fields
  #isConnected = false;
  #abortController = null;
  
  // DOM elements
  #countrySelect = null;
  #stateSelect = null;
  #provinceSelect = null;
  #dealerBox = null;
  
  // Data
  #cleanedDealers = [];
  #rawDealers = [];
  
  // Event handlers
  #countryChangeHandler = null;
  #stateChangeHandler = null;
  
  constructor() {
    super();
    
    // Bind event handlers
    this.#countryChangeHandler = this.#handleCountryChange.bind(this);
    this.#stateChangeHandler = this.#handleStateProvinceChange.bind(this);
  }
  
  connectedCallback() {
    if (this.#isConnected) return;
    
    try {
      this.#setupDOM();
      this.#processDealerData();
      this.#setupEventListeners();
      this.#initializeSelectors();
      
      this.#isConnected = true;
      
      this.dispatchEvent(new CustomEvent('selector:ready', {
        detail: { selector: this },
        bubbles: true
      }));
    } catch (error) {
      console.error('CountryStateProvinceSelector: Error in connectedCallback', error);
    }
  }
  
  disconnectedCallback() {
    if (!this.#isConnected) return;
    
    try {
      this.#cleanup();
      this.#isConnected = false;
    } catch (error) {
      console.error('CountryStateProvinceSelector: Error in disconnectedCallback', error);
    }
  }
  
  // Private methods
  #setupDOM() {
    this.#countrySelect = this.querySelector('#country');
    this.#stateSelect = this.querySelector('#states');
    this.#provinceSelect = this.querySelector('#province');
    this.#dealerBox = this.querySelector('#dealer-results');
  }
  
  #processDealerData() {
    try {
      const dealersData = this.dataset.dealers;
      if (!dealersData) return;
      
      this.#rawDealers = JSON.parse(dealersData);
      
      this.#cleanedDealers = this.#rawDealers.map(dealer => ({
        address: dealer.address?.replaceAll('\\n', '<br>') || '',
        contact_info: dealer.contact_info?.replaceAll('\\n', '<br>') || '',
        country: this.#normalize(dealer.country),
        google_maps: dealer.google_maps?.replaceAll('\\/', '/') || '',
        name: dealer.name || '',
        state: this.#normalize(dealer.state),
        logo: dealer.logo || ''
      }));
    } catch (error) {
      console.error('CountryStateProvinceSelector: Error processing dealer data', error);
      this.#cleanedDealers = [];
    }
  }
  
  #setupEventListeners() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    
    this.#countrySelect?.addEventListener('change', this.#countryChangeHandler, { signal });
    this.#stateSelect?.addEventListener('change', this.#stateChangeHandler, { signal });
    this.#provinceSelect?.addEventListener('change', this.#stateChangeHandler, { signal });
  }
  
  #cleanup() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#cleanedDealers = [];
    this.#rawDealers = [];
  }
  
  #initializeSelectors() {
    this.#hideStateProvinceSelectors();
    this.#clearDealerResults();
  }
  
  #handleCountryChange() {
    try {
      this.#hideStateProvinceSelectors();
      
      const countryValue = this.#countrySelect?.value;
      
      if (countryValue === 'us') {
        this.#showStateSelector();
      } else if (countryValue === 'ca') {
        this.#showProvinceSelector();
      } else {
        this.#showCountryDealers();
      }
    } catch (error) {
      console.error('CountryStateProvinceSelector: Error handling country change', error);
    }
  }
  
  #handleStateProvinceChange() {
    try {
      const selectedCountry = this.#getSelectedCountryText();
      const regionSelect = this.#countrySelect?.value === 'us' 
        ? this.#stateSelect 
        : this.#provinceSelect;
      const selectedRegion = this.#normalize(regionSelect?.value || '');
      
      const matching = this.#cleanedDealers.filter(d =>
        d.country === selectedCountry &&
        d.state.includes(selectedRegion)
      );
      
      this.#updateDealerResults(matching);
    } catch (error) {
      console.error('CountryStateProvinceSelector: Error handling state/province change', error);
    }
  }
  
  #showStateSelector() {
    if (!this.#stateSelect) return;
    
    this.#stateSelect.style.display = 'block';
    this.#stateSelect.disabled = false;
    
    const icon = this.#stateSelect.parentElement?.querySelector('.pointer-events-none.absolute.right-4');
    if (icon) icon.style.display = 'block';
    
    if (this.#stateSelect.value) {
      this.#handleStateProvinceChange();
    } else {
      this.#clearDealerResults();
    }
  }
  
  #showProvinceSelector() {
    if (!this.#provinceSelect) return;
    
    this.#provinceSelect.style.display = 'block';
    this.#provinceSelect.disabled = false;
    
    const icon = this.#provinceSelect.parentElement?.querySelector('.pointer-events-none.absolute.right-4');
    if (icon) icon.style.display = 'block';
    
    if (this.#provinceSelect.value) {
      this.#handleStateProvinceChange();
    } else {
      this.#clearDealerResults();
    }
  }
  
  #showCountryDealers() {
    const selectedCountry = this.#getSelectedCountryText();
    const matching = this.#cleanedDealers.filter(d => d.country === selectedCountry);
    this.#updateDealerResults(matching);
  }
  
  #getSelectedCountryText() {
    if (!this.#countrySelect) return '';
    
    const selectedOption = this.#countrySelect.options[this.#countrySelect.selectedIndex];
    return this.#normalize(selectedOption?.text || '');
  }
  
  #updateDealerResults(matchingDealers) {
    if (!this.#dealerBox) return;
    
    this.#dealerBox.innerHTML = '';
    this.#dealerBox.style.display = '';
    
    if (matchingDealers && matchingDealers.length > 0) {
      const html = matchingDealers.map(dealer => this.#createDealerCard(dealer)).join('');
      this.#dealerBox.innerHTML = html;
      
      this.dispatchEvent(new CustomEvent('dealer:selected', {
        detail: { dealers: matchingDealers },
        bubbles: true
      }));
    } else {
      this.#showHQFallback();
    }
  }
  
  #createDealerCard(dealer) {
    const cardClass = this.dataset.inline === 'false' 
      ? 'p-4 lg:p-12 lg:w-1/3 w-full' 
      : '';
    
    return `
      <div class="uppercase bg-beige ${cardClass}">
        ${dealer.logo ? `
          <img src="${dealer.logo}" 
               alt="${dealer.name} logo" 
               class="w-48 object-contain mb-6" 
               loading="lazy" 
               decoding="async" />
        ` : ''}
        <h4 class="font-bold mb-2">${dealer.name}</h4>
        <p>${dealer.address}</p>
        <h4 class="font-bold mt-4 mb-2">Contact</h4>
        <p>${dealer.contact_info}</p>
        ${dealer.google_maps ? `
          <a href="${dealer.google_maps}" 
             class="underline mt-2 block"
             target="_blank"
             rel="noopener noreferrer">
            Open Google Maps
          </a>
        ` : ''}
      </div>
    `;
  }
  
  #clearDealerResults() {
    if (!this.#dealerBox) return;
    
    this.#dealerBox.innerHTML = '';
    this.#dealerBox.style.display = 'none';
    
    this.dispatchEvent(new CustomEvent('dealer:cleared', {
      bubbles: true
    }));
  }
  
  #hideStateProvinceSelectors() {
    [this.#stateSelect, this.#provinceSelect].forEach(selector => {
      if (!selector) return;
      
      selector.style.display = 'none';
      selector.disabled = true;
      
      const icon = selector.parentElement?.querySelector('.pointer-events-none.absolute.right-4');
      if (icon) {
        icon.style.display = 'none';
      }
    });
  }
  
  #showHQFallback() {
    const hq = this.#cleanedDealers.find(d => 
      d.name.toLowerCase().includes('hq')
    );
    
    if (hq) {
      this.#dealerBox.innerHTML = this.#createDealerCard(hq);
    } else {
      this.#dealerBox.innerHTML = '<p>No dealers found.</p>';
    }
  }
  
  #normalize(str) {
    // This function should be implemented based on your normalization needs
    // For now, returning lowercase trimmed string
    return (str || '').toLowerCase().trim();
  }
  
  // Static utility methods
  /**
   * Find selector by ID
   * @param {string} id - Element ID
   * @returns {CountryStateProvinceSelector|null}
   */
  static find(id) {
    return document.getElementById(id);
  }
}

// Register custom element
if (!customElements.get('country-state-province-selector')) {
  customElements.define('country-state-province-selector', CountryStateProvinceSelector);
}

// Module exports
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    HeaderDrawer,
    HeaderElement,
    LocalizationForm,
    CountryStateProvinceSelector
  };
}
